import type { ResilienceLevelDisplayLabelKey } from "@/domain/center/resilience";
import { DASHBOARD_RECOMMENDATION_EMPTY_PLACEHOLDER_KEY } from "@/domain/dashboard";
import {
  HEALING_PROGRESS_BLOCKED_COOLDOWN_DISPLAY_KEY,
  HEALING_PROGRESS_BLOCKED_PHASE_DISPLAY_KEY,
} from "@/domain/healing";
import {
  ARENA_RUN_DETAIL_EMPTY_DISPLAY_KEY,
  ARENA_RUN_DETAIL_LOADING_DISPLAY_KEY,
} from "@/domain/rules/arenaRunDetailDisplay";
import type { WeeklyCompetitionStageTierBandDisplayLabelKey } from "@/domain/rules/weeklyCompetitionDisplay";

export type Locale = "ko" | "en";

export type Messages = {
  /** D3-0 Slice 1: BTY Daily OS "Today" static surface. First fold = Direction; status fields are static placeholders (no data wiring this slice). */
  today: {
    dailyOs: {
      pulseHeading: string;
      doorHeading: string;
      mirrorHasEvidence: string;
      mirrorNoEvidence: string;
      openLoopTitle: string;
      openLoopBody: string;
      openLoopCta: string;
      centerFirstTitle: string;
      centerFirstBody: string;
      centerFirstCta: string;
      reexposureTitle: string;
      reexposureBody: string;
      reexposureCta: string;
      firstDayTitle: string;
      firstDayBody: string;
      doorCenterTitle: string;
      doorCenterSub: string;
      doorArenaTitle: string;
      doorArenaSub: string;
      doorFoundryTitle: string;
      doorFoundrySub: string;
      exitLine: string;
      loadingLabel: string;
      pulse: {
        self: { quiet: string; living: string; connected: string; deepening: string };
        others: { quiet: string; living: string; connected: string; deepening: string };
        ground: { quiet: string; living: string; connected: string; deepening: string };
      };
      /** Today Intelligence v1 (STEP 7B) — deterministic relationship-focus brief. */
      intel: {
        focusLabel: string;
        focus: {
          Self: { title: string; line: string };
          Others: { title: string; line: string };
          World: { title: string; line: string };
          CleanStart: { title: string; line: string };
          ContinuePending: { title: string; line: string };
        };
        ctaSelf: string;
        ctaOthers: string;
        ctaWorld: string;
      };
    };
    eyebrow: string;
    title: string;
    greeting: string;
    nowReserved: string;
    todayLabel: string;
    promise: string;
    journeyPlaceholder: string;
    pendingPlaceholder: string;
    reflectionPlaceholder: string;
    beginButton: string;
    beginConfirmation: string;
    /** D3-1 home surface — live tiles + labeled stubs. */
    profileTitle: string;
    coreXpLabel: string;
    levelStub: string;
    companionName: string;
    companionLine: string;
    situationTitle: string;
    situationEmpty: string;
    beginCta: string;
    pointsTitle: string;
    pointsLabel: string;
    airTitle: string;
    airLabel: string;
    airBandLow: string;
    airBandMid: string;
    airBandHigh: string;
    airBandUnknown: string;
    streakLabel: string;
    stageLabel: string;
    /** D4 ritual-first — greeting (real name or omitted), promise language, card hierarchy. */
    greetingNamed: string;
    greetingPlain: string;
    promiseLine: string;
    companionCardTitle: string;
    choiceWaiting: string;
    behaviorTitle: string;
    behaviorWaiting: string;
    growthTitle: string;
    /** D5 Orb entry — one-time Day-0 hold-to-begin hint (today-side, localStorage-gated). */
    orbHint: string;
  };
  /** Lane 2: team-membership submission form + S2 arena-entry gate. KO values are TODO_KO placeholders (translation fast-follow). */
  membership: {
    form: {
      title: string;
      intro: string;
      fullName: { label: string };
      jobFunction: { label: string; staff: string; leader: string };
      joinedAt: { label: string };
      leaderStartedAt: { label: string; hint: string };
      submit: string;
      submitting: string;
      errorGeneric: string;
      success: string;
    };
    status: { pending: string; approved: string; approvedOn: string };
    gate: { redirectCopy: string };
  };
  /** §2: 전환 중 로딩 문구 (locale별) */
  loading: { message: string; hint: string; localeRouteSuspenseMainRegionAria: string };
  nav: { center: string; bty: string; arena: string; en: string; ko: string; skipToMainContent: string };
  login: {
    title: string;
    afterLoginGoTo: string;
    email: string;
    password: string;
    submit: string;
    submitting: string;
    cookieNotice: string;
    errorDefault: string;
    forgotPassword: string;
    forgotPasswordSent: string;
    forgotPasswordError: string;
    /** `/[locale]/bty/forgot-password` 페이지 `<main>` 랜드마크 */
    forgotPasswordMainRegionAria: string;
    /** `/[locale]/bty/(public)/loading` Suspense `<main>` 라벨 (로그인·비밀번호 찾기 공통) */
    btyPublicRouteSuspenseMainRegionAria: string;
  };
  auth: { backToLogin: string; loading: string; callbackError: string };
  logout: string;
  /**
   * 도메인 stable 키(weeklyTierDisplayLabelKey·arenaRunStateDisplayLabelKey)와 1:1.
   * @see domain/rules/leaderboard.ts · arenaRunState.ts
   */
  arena: {
    weeklyTierBronze: string;
    weeklyTierSilver: string;
    weeklyTierGold: string;
    weeklyTierPlatinum: string;
    runStateInProgress: string;
    runStateCompleted: string;
    runStateAborted: string;
    /** leaderboardTieRankSuffixDisplayKey(true) — 위 행과 동일 주간 XP 동순위 접미 */
    tieRankSuffix: string;
    /** After scenario choice toast — no raw AIR delta */
    sessionChoiceToastExecutionIntegrity: string;
    sessionSummaryExecutionIntegrityHeading: string;
    sessionSummaryAirBandBefore: string;
    sessionSummaryAirBandAfter: string;
    sessionSummaryContextNoteHeading: string;
    sessionSummaryPatternShiftHeading: string;
    sessionSummaryPatternShiftPlaceholder: string;
  };
  /** 도메인 reflectTextLengthHintKey → 문구 (Arena 성찰 입력). */
  reflectHints: {
    reflect_hint_empty: string;
    reflect_hint_short: string;
    reflect_hint_developing: string;
    reflect_hint_substantial: string;
    reflect_hint_near_limit: string;
    reflect_hint_at_limit: string;
  };
  integrity: {
    title: string;
    subtitle: string;
    /** 연습 플로우 2종 화면 보강: 단계 2 안내 라벨 */
    stepLabelGuide?: string;
    /** §7 2차: 단계 2 안내 문구 */
    guideMessage: string;
    startPractice: string;
    intro: string;
    placeholder: string;
    send: string;
    thinking: string;
    /** 빈 대화 영역 안내 (메시지 없을 때 EmptyState 메인 문구, render-only) */
    emptyHint: string;
    /** Optional hint under empty state message */
    emptyStateHint?: string;
    backToFoundry: string;
    reply: string;
    /** §7 2차: 단계 5 완료 */
    doneTitle: string;
    doneSub: string;
    doneCtaComplete: string;
    doneCtaMentor: string;
    doneCtaFoundry: string;
    /** Dojo 연습 플로우 2종 연동: 역지사지 완료 후 50문항 진단으로 */
    doneCtaAssessment: string;
    /** API 실패(네트워크 등) 시 표시 (render-only) */
    apiError: string;
    /** Error section title (e.g. for role=alert) */
    errorTitle?: string;
    /** API가 빈 응답일 때 표시 (render-only) */
    replyFallback: string;
    /** aria-label: 연습 시작 버튼 */
    ariaStartPractice?: string;
    /** aria-label: 연습 완료 버튼 */
    ariaCompletePractice?: string;
    /** aria-label: 갈등 입력 필드 */
    ariaInputLabel?: string;
    /** aria-label: Dr. Chi 응답 대기 (로딩 스켈레톤) */
    ariaWaitingChi?: string;
    /** 멘토 이름 라벨 (응답 버블 상단) */
    mentorName?: string;
    conversationSimulatorAria: string;
    messageComposerAria: string;
    /** 역지사지 페이지 메인 랜드마크 */
    integrityMainRegionAria: string;
  };
  center: {
    /** §2: 전환 중 로딩/대기 문구 (locale에 맞게) */
    loading: string;
    /** Dear Me 페이지 메인 랜드마크 접근성 라벨 */
    mainAriaLabel: string;
    /** Dear Me 페이지 상단 헤더(제목·안내) 영역 라벨 */
    headerLabel: string;
    /** Dear Me 페이지 본문 영역 라벨 */
    contentLabel: string;
    title: string;
    /** §1·§8: 아늑한 방 헤더 (locale별) */
    heroTitleMain?: string;
    heroTitleAccent?: string;
    tagline: string;
    /** §5: 단일 CTA 라벨 (문 열고 나가기 / 연습하러 가기 통합) */
    ctaToFoundry: string;
    linkToBty: string;
    assessmentCta: string;
    assessmentCtaSub: string;
    /** §3: 5문항 아래 안내 — "더 자세한 테스트를 원하시면 클릭하세요" + 50문항 링크 */
    assessmentDetailHint: string;
    entryIntro: string;
    startCta: string;
    /** 2단계: 오늘의 나 */
    todayStepTitle: string;
    todayMoodLabel: string;
    todayEnergyLabel: string;
    todayOneWordLabel: string;
    /** §8: locale별 placeholder (기분/한 단어 입력) */
    todayMoodPlaceholder: string;
    todayOneWordPlaceholder: string;
    todayNext: string;
    todaySkip: string;
    /** §1·§5: EmotionalBridge 문구 (문 열고 나가기) */
    bridgeHeading: string;
    bridgeSub: string;
    /** 3단계: 편지 쓰기 */
    letterStepTitle: string;
    /** 편지 입력·제출 그룹 접근성 라벨 */
    letterFormLabel: string;
    letterPrompt: string;
    letterPlaceholder: string;
    submitLetter: string;
    sendingLetter: string;
    /** 4단계: 답장 */
    replyStepTitle: string;
    /** 답장 영역(완료 안내+답장 내용) 그룹 접근성 라벨 */
    replySummaryLabel: string;
    /** 5단계: 완료 */
    completedTitle: string;
    completedSub: string;
    continueToChat: string;
    /** 편지 이력 */
    letterHistoryTitle: string;
    /** 이력 목록(ul) 전용 접근성 라벨 */
    letterHistoryListAria: string;
    letterHistoryEmpty: string;
    letterHistoryError: string;
    letterHistoryLoading: string;
    letterHistoryReplied: string;
    letterHistoryNoReply: string;
    /** Letter calendar (이력 화면) */
    calendarTitle: string;
    calendarPrevMonth: string;
    calendarNextMonth: string;
    calendarWeekdays: string[];
    calendarMonths: string[];
    calendarSelectedEmpty: string;
    /** DearMeComposer — bty center API, 100–1000자 */
    dearMeComposerRegionAria: string;
    dearMeComposerCharCount: string;
    dearMeComposerSubmit: string;
    dearMeComposerSubmitting: string;
    dearMeComposerSaved: string;
    dearMeComposerLoadError: string;
    dearMeComposerSubmitError: string;
    dearMeComposerPreviousTitle: string;
    /** 답장 화면 하단 액션 그룹 접근성 라벨 (다시 쓰기·Center로) */
    replyActionsLabel: string;
    /** Dear Me 페이지 푸터(Center 링크) 영역 라벨 */
    footerLabel: string;
    /** `/[locale]/dear-me` Suspense fallback `<main>` 라벨 */
    dearMeSuspenseMainRegionAria: string;
    /** `/[locale]/dear-me` `error.tsx` 경계 `<main>` 라벨 */
    dearMeErrorMainRegionAria: string;
    /** `/[locale]/center` Suspense fallback `<main>` 라벨 */
    centerSuspenseMainRegionAria: string;
    /** `/[locale]/center` `error.tsx` 경계 `<main>` 라벨 */
    centerErrorMainRegionAria: string;
    /** `/[locale]/center` 보조 "Current State" 통합 카드 타이틀 (Stage 투영 + 치유 단계) */
    currentStateTitle: string;
    /** 강제 리셋(Stage 4) · 48h 잠금 UX */
    forcedResetRegionAria: string;
    forcedResetBannerTitle: string;
    forcedResetCountdownLive: string;
    forcedResetArenaNavLabel: string;
    forcedResetArenaBlockedHint: string;
    forcedResetChecklistTitle: string;
    forcedResetItemStabilize: string;
    forcedResetItemBoundary: string;
    forcedResetItemAccountability: string;
    forcedResetProgress: string;
    forcedResetReturnCta: string;
    forcedResetSubmitting: string;
    forcedResetError: string;
    forcedResetWindowElapsed: string;
  };
  bty: {
    title: string;
    tagline: string;
    linkToCenter: string;
    entryIntro: string;
    startCta: string;
    arenaCta: string;
    dashboardLabel: string;
    leaderboardLabel: string;
    /** 대시보드 LE Stage·AIR 위젯 카드 라벨 */
    leStageLabel: string;
    airLabel: string;
    /** 대시보드 추천 위젯 카드 라벨 */
    recommendationLabel: string;
    /** DASHBOARD_RECOMMENDATION_EMPTY_PLACEHOLDER_KEY */
    recommendationEmptyPlaceholder: string;
    /** AIR 위젯: 로드 실패 시 문구 */
    airUnavailable: string;
    /** 대시보드 주간 성찰 위젯 영역 aria-label */
    weeklyReflectionRegion: string;
    /** 대시보드 Personal Record 위젯 영역 aria-label */
    personalRecordRegion: string;
    /** 대시보드 Streak 카드 라벨 */
    streakLabel: string;
    /** 대시보드 Points Today 카드 라벨 */
    pointsTodayLabel: string;
    /** 대시보드 Lifetime Progress 카드 라벨 */
    lifetimeProgressLabel: string;
    /** 대시보드 Dojo 연습 카드 라벨 */
    dojoPracticeLabel: string;
    /** Dojo 연습 링크 영역 aria-label */
    dojoPracticeLinksRegion: string;
    /** 리더보드 주간 리셋 일시 영역 aria-label */
    leaderboardWeekResetRegion: string;
    /** 대시보드 Arena 티어 카드 (GET /api/arena/core-xp 의 tier 표시만) */
    dashboardTierCardLabel: string;
    dashboardTierCardSubline: string;
    dashboardTierCardRegionAria: string;
    /** LE Stage·AIR 카드 묶음 랜드마크 (접근성) */
    leAirSummarySectionAria: string;
    /** 대시보드 상단 바로가기: Arena */
    /** 대시보드 바로가기 → /bty-arena/play (허브와 구분) */
    dashboardShortcutGoArena: string;
    /** 대시보드 상단 바로가기: 주간 랭킹 */
    dashboardShortcutWeeklyRanking: string;
    /** 리더보드 카드·목록 묶음 랜드마크 */
    leaderboardMainRegionAria: string;
    /** `/[locale]/bty/leaderboard/loading` Suspense `<main>` 라벨 */
    leaderboardRouteSuspenseMainRegionAria: string;
    /** Arena LeaderboardWidget — top 20 weekly + Core XP + reset countdown */
    leaderboardWidgetRegionAria: string;
    leaderboardWidgetTitle: string;
    leaderboardWidgetResetCountdown: string;
    leaderboardWidgetLoading: string;
    leaderboardWidgetError: string;
    leaderboardWidgetEmpty: string;
    leaderboardWidgetTop20Aria: string;
    /** AvatarRenderer — tier visual + Core XP bar (no weekly XP) */
    avatarRendererRegionAria: string;
    avatarRendererLoading: string;
    avatarRendererError: string;
    avatarRendererNextTierProgress: string;
    avatarRendererMaxTier: string;
    avatarRendererUnlockedBadges: string;
    avatarRendererCoreXpLabel: string;
    /** Core XP bar — `title` / SR: ledger ARENA·LAB·FOUNDRY·MENTOR % (AvatarRenderer). */
    avatarRendererCoreXpSources: string;
    /** Segmented strip — share of positive Core XP ledger by bucket (AvatarRenderer). */
    avatarRendererCoreXpSegmentsAria: string;
    avatarRendererXpArena: string;
    avatarRendererXpLab: string;
    avatarRendererXpFoundry: string;
    avatarRendererXpMentor: string;
    avatarRendererXpOther: string;
    /** `/[locale]/bty/(protected)/profile/loading` Suspense `<main>` 라벨 */
    profileRouteSuspenseMainRegionAria: string;
    /** 대시보드 히어로 부제 (한눈에 보기) */
    dashboardHeroSubtitle: string;
    /** 주간 XP·랭킹 위젯 (API 표시만, 순위는 리더보드 링크) */
    dashboardWeeklyRankWidgetTitle: string;
    dashboardWeeklyWidgetLoading: string;
    dashboardWeeklyWidgetAria: string;
    dashboardWeeklyXpCaption: string;
    /** 주간 티어 라벨(도메인 calculateTier→weeklyTierDisplayLabelKey→arena) 상단 캡션 */
    dashboardWeeklyTierCaption: string;
    /** weeklyCompetitionStageTierBandDisplayLabelKey 캡션 */
    dashboardWeeklyCompetitionStageCaption: string;
    weeklyCompetitionStageBandBronze: string;
    weeklyCompetitionStageBandSilver: string;
    weeklyCompetitionStageBandGold: string;
    weeklyCompetitionStageBandPlatinum: string;
    /** LE Stage 위젯 진행 바 접근성 */
    leStageLeProgressBarAria: string;
    dashboardWeeklyRankSeeLeaderboard: string;
    dashboardWeeklyRankHint: string;
    /** 대시보드: 라이브 리더보드 미리보기 모달 */
    dashboardOpenLiveLeaderboardCta: string;
    dashboardLiveLeaderboardModalTitle: string;
    dashboardLiveLeaderboardModalAria: string;
    dashboardLiveLeaderboardLoading: string;
    dashboardLiveLeaderboardEmpty: string;
    dashboardLiveLeaderboardClose: string;
    dashboardLiveLeaderboardFullPage: string;
    dashboardLiveLeaderboardFailed: string;
    /** 모달 보조 설명(aria-describedby) */
    dashboardLiveLeaderboardModalDesc: string;
    /** 순위 목록 ul 접근성 라벨 */
    dashboardLiveLeaderboardListAria: string;
    /** 내 순위 없을 때 상위 목록 안내 */
    dashboardLiveLeaderboardNotRankedBanner: string;
    /** nearMe 비어 전체 상위 표시 시 */
    dashboardLiveLeaderboardNearMeFallbackBanner: string;
    /** 닫기 버튼 스크린리더 */
    dashboardLiveLeaderboardCloseAria: string;
    /** LE stage-summary 위젯 스크린리더 부연 */
    leStageSummaryWidgetDesc: string;
    /** 프로필(Code·Sub Name) 카드 */
    profileCodeNameCardLabel: string;
    profileIdentitySubline: string;
    profileRenamePlaceholder: string;
    profileSaveSubName: string;
    profileSaving: string;
    eliteMeContentUnlockedLabel: string;
    eliteMeContentUnlockedYes: string;
    eliteMeContentUnlockedNo: string;
    /** Foundry EliteSpecWidget — Certified / mentor queue */
    eliteSpecWidgetRegionAria: string;
    eliteSpecWidgetCertifiedBadge: string;
    eliteSpecWidgetCertifiedYes: string;
    eliteSpecWidgetCertifiedNo: string;
    eliteSpecWidgetLriHint: string;
    eliteSpecWidgetQueueTitle: string;
    eliteSpecWidgetQueueForbidden: string;
    eliteSpecWidgetStatusPending: string;
    eliteSpecWidgetStatusApproved: string;
    eliteSpecWidgetStatusRejected: string;
    eliteSpecWidgetApprove: string;
    eliteSpecWidgetReject: string;
    eliteSpecWidgetLoading: string;
    eliteSpecWidgetError: string;
    /** LE 카드 내 TII·인증 요약 밴드 */
    leEngineTiiCertifiedBandAria: string;
    /** 시즌 카드: 주간 XP·이벤트·주간 구간 묶음 */
    weeklySeasonActivityAria: string;
    /** 대시보드 페이지 h1 */
    dashboardPageTitle: string;
    /** 대시보드 페이지 메인 랜드마크 (Foundry) */
    dashboardMainRegionAria: string;
    /** 대시보드 Suspense fallback `<main>` 라벨 (Foundry) */
    dashboardSuspenseMainRegionAria: string;
    /** Dojo Suspense fallback `<main>` 라벨 (Foundry) */
    dojoSuspenseMainRegionAria: string;
    /** Elite Suspense fallback `<main>` 라벨 (Foundry) */
    eliteSuspenseMainRegionAria: string;
    /** Healing Suspense fallback `<main>` 라벨 (Foundry) */
    healingSuspenseMainRegionAria: string;
    /** Mentor Suspense fallback `<main>` 라벨 (Foundry) */
    mentorSuspenseMainRegionAria: string;
    /** Integrity 연습 Suspense fallback `<main>` 라벨 (Foundry) */
    integrityPracticeSuspenseMainRegionAria: string;
    /** Foundry 허브 메인 랜드마크 */
    foundryHubMainLandmarkAria: string;
    /** Foundry 허브 Suspense fallback `<main>` 라벨 */
    foundryHubSuspenseMainRegionAria: string;
    /** bty(protected) 인덱스 메인 랜드마크 — Arena·Center·Foundry 허브 */
    btyIndexMainRegionAria: string;
    /** Foundry 기능 카드 그리드 */
    foundryFeatureCardsRegionAria: string;
    foundryBackToBtyHome: string;
    /** Foundry — 추천 프로그램 위젯 영역 */
    programRecommenderWidgetRegionAria: string;
    programRecommenderWidgetTitle: string;
    programRecommenderWidgetLoading: string;
    programRecommenderWidgetError: string;
    programRecommenderWidgetEmpty: string;
    programRecommenderWidgetLocked: string;
    /** Foundry 프로그램 상세 페이지 메인 */
    foundryProgramDetailMainAria: string;
    /** bty 인덱스: Arena·Center·Foundry 한 줄 설명 */
    indexThreeHubsExplainer: string;
    /** bty 인덱스: 세 허브 진입 카드 묶음 (landmark) */
    indexHubEntriesRegionAria: string;
    /** 오늘 획득 XP 카드 부가 설명 */
    pointsTodaySubline: string;
    /** 대시보드 AIR 위젯: integritySlip 플래그 시 (API 값만 표시) */
    airIntegritySlip: string;
    /** LE Stage 요약 API 실패·빈 응답 시 */
    leStageSummaryUnavailable: string;
    /** AIR 7·14·90일 그룹 라벨 (접근성) */
    leAirAria7d: string;
    leAirAria14d: string;
    leAirAria90d: string;
    leAirAriaGrid: string;
    /** LE·AIR 위젯: AIR API 대기 중 */
    leAirLoading: string;
    leStageSummarySectionAria: string;
    leStageStagePrefix: string;
    leStageResetDueLabel: string;
    leStageArenaSummaryHeading: string;
    leStageBehaviorHeading: string;
    /** Leadership Engine 위젯 — AIR(14d)·TII·LRI·인증 배지 */
    leadershipEngineWidgetRegionAria: string;
    leadershipEngineWidgetTitle: string;
    leadershipEngineAir14Aria: string;
    leadershipEngineTiiAria: string;
    leadershipEngineLriAria: string;
    leadershipEngineReadinessReady: string;
    leadershipEngineReadinessNotReady: string;
    leadershipEngineCertifiedBadge: string;
    leadershipEngineWidgetLoading: string;
    leadershipEngineWidgetError: string;
    leadershipEngineAirTrendSparklineAria: string;
    leadershipEnginePromotionBlockedBadge: string;
    leadershipEngineCertifiedExpiresIn: string;
    /** Certified Leader — auto-renewal attempts after grant expiry */
    leadershipEngineRenewalHistoryAria: string;
    leadershipEngineRenewalEmpty: string;
    leadershipEngineRenewalRowRenewed: string;
    leadershipEngineRenewalRowNotRenewed: string;
    leadershipEngineElitePendingBadge: string;
    leadershipEngineEliteApprovedBadge: string;
    leadershipEngineWeeklyStreak: string;
    leadershipEngineWeeklyCompletion: string;
    /** Overall integrity grade + composite (from integrity_score_cards) */
    leadershipEngineIntegrityRegionAria: string;
    leadershipEngineIntegrityLabel: string;
    leadershipEngineEliteCta: string;
    leadershipEngineEliteCtaBusy: string;
    /** Dashboard TIIWidget — team health bar (team_integrity_index + Realtime) */
    tiiWidgetRegionAria: string;
    tiiWidgetTitle: string;
    tiiWidgetHealthBarAria: string;
    tiiWidgetBandRed: string;
    tiiWidgetBandYellow: string;
    tiiWidgetBandGreen: string;
    tiiWidgetMembersLabel: string;
    tiiWidgetLastCalculated: string;
    tiiWidgetNoSnapshot: string;
    tiiWidgetLoading: string;
    tiiWidgetError: string;
    tiiWidgetScorePct: string;
    leStageLoading: string;
    /** Dashboard AIRTrendWidget — 30d sparkline, 7d avg, Certified countdown, Realtime warning */
    airTrendWidgetRegionAria: string;
    airTrendWidgetTitle: string;
    airTrendWidgetSevenDayLabel: string;
    airTrendWidgetSevenDayAria: string;
    airTrendWidgetCertifiedExpiryPrefix: string;
    airTrendWidgetWarningBanner: string;
    airTrendWidgetLoading: string;
    airTrendWidgetError: string;
    /** AIR band labels (execution integrity); cutoffs 0.50 / 0.80 */
    airBandLow: string;
    airBandMid: string;
    airBandHigh: string;
    airExecutionIntegrityFootnote: string;
    /** Center WeeklyReportCard — weekly AIR report + phase gates */
    weeklyReportCardRegionAria: string;
    /** Sparkline caption — execution integrity trend, not Pattern Shift */
    weeklyReportCardAirTrendCaption: string;
    weeklyReportCardTitle: string;
    weeklyReportCardWeekLabel: string;
    weeklyReportCardCompletionLabel: string;
    weeklyReportCardStreakLabel: string;
    weeklyReportCardStreakDays: string;
    weeklyReportCardFlagHero: string;
    weeklyReportCardFlagIntegrity: string;
    weeklyReportCardFlagClean: string;
    weeklyReportCardFlagBarAria: string;
    weeklyReportCardPhaseLabel: string;
    weeklyReportCardResilienceLabel: string;
    /** Weekly report — overall integrity grade (AIR/LRI/resilience weighted) */
    weeklyReportCardIntegrityRegionAria: string;
    weeklyReportCardIntegrityLabel: string;
    /** Placeholders: {grade}, {score} */
    weeklyReportCardIntegritySubline: string;
    weeklyReportCardLoading: string;
    weeklyReportCardError: string;
    weeklyReportCardNoReport: string;
    weeklyReportCardCtaStart: string;
    /** Foundry MentorChatShell — Dr. Chi */
    mentorChatShellRegionAria: string;
    mentorChatShellTitle: string;
    mentorChatShellPhaseBadge: string;
    mentorChatShellFlagLabel: string;
    mentorChatShellRecsCta: string;
    mentorChatShellPlaceholder: string;
    mentorChatShellSend: string;
    mentorChatShellLoading: string;
    mentorChatShellError: string;
    /** Foundry DojoAssessmentShell — micro Likert assessment */
    dojoAssessmentShellRegionAria: string;
    dojoAssessmentShellLoading: string;
    dojoAssessmentShellError: string;
    dojoAssessmentShellProgress: string;
    dojoAssessmentShellNext: string;
    dojoAssessmentShellSubmit: string;
    dojoAssessmentShellPass: string;
    dojoAssessmentShellFail: string;
    dojoAssessmentShellScoreLabel: string;
    dojoAssessmentShellHintLabel: string;
    dojoAssessmentShellReturnToFoundry: string;
    dojoAssessmentShellLikert1: string;
    dojoAssessmentShellLikert2: string;
    dojoAssessmentShellLikert3: string;
    dojoAssessmentShellLikert4: string;
    /** Foundry ProgramProgressShell — modules + progress bar */
    programProgressShellRegionAria: string;
    programProgressShellLoading: string;
    programProgressShellError: string;
    programProgressShellPhaseLabel: string;
    programProgressShellCompletionLabel: string;
    programProgressShellModulesHeading: string;
    programProgressShellModuleTap: string;
    programProgressShellCompleteOverlay: string;
    programProgressShellArenaCta: string;
    programProgressShellDojoCta: string;
    /** Center AwakeningMilestoneTracker — RENEWAL milestones */
    awakeningMilestoneTrackerRegionAria: string;
    awakeningMilestoneTrackerTitle: string;
    awakeningMilestoneTrackerNextLabel: string;
    awakeningMilestoneTrackerRenewalUnlocked: string;
    awakeningMilestoneTrackerRenewalSub: string;
    awakeningMilestoneTrackerDismiss: string;
    awakeningMilestoneTrackerChatCta: string;
    awakeningMilestoneTrackerCollapseChat: string;
    awakeningMilestoneTrackerLoading: string;
    awakeningMilestoneTrackerError: string;
  };
  /** 자존감 50문항 결과 화면 (Center assessment). */
  assessmentResult: {
    /** 본문(점수·레이더·권장 트랙) 영역 aria-label */
    mainContentRegionAria: string;
    /** 하단 다음 단계 CTA 그룹 aria-label */
    nextStepsCtaGroupAria: string;
    start28ProgramCta: string;
    retakeCta: string;
    /** CTA 그룹용 스크린리더 전용 제목 */
    ctaSrOnlyHeading: string;
  };
  landing: {
    heroTitle: string;
    heroSubtitle: string;
    recommended: string;
    arenaTitle: string;
    arenaDesc: string;
    arenaCta: string;
    foundryTitle: string;
    foundryDesc: string;
    foundryCta: string;
    centerTitle: string;
    centerDesc: string;
    centerCta: string;
    /** 대시보드 상단 카드 내 "Today's growth" 섹션으로 가는 링크 문구 */
    todayGrowthLink: string;
    footerHint: string;
    /** 자존감 50문항 진단 폼 페이지 메인 랜드마크 (Center 계열) */
    assessmentMainRegionAria: string;
    /** `/[locale]/assessment` 세그먼트 `error.tsx` `<main>` 라벨 */
    assessmentErrorMainRegionAria: string;
    /** `/[locale]/assessment` Suspense `loading.tsx` `<main>` 라벨 */
    assessmentSuspenseMainRegionAria: string;
    /** `/[locale]` 랜딩 허브 — Arena·Foundry·Center 카드 진입 */
    landingHubMainRegionAria: string;
  };
  safeMirror: {
    title: string;
    subtitle: string;
    placeholder: string;
    submit: string;
    submitting: string;
    /** §8: API 실패 시 표시할 답장 대체 문구 (locale별) */
    fallbackReply: string;
  };
  smallWins: {
    title: string;
    subtitle: string;
    add: string;
    customPlaceholder: string;
    count: string;
    suggested: string[];
  };
  selfEsteem: {
    title: string;
    subtitle: string;
    again: string;
    choices: { value: number; label: string }[];
    questions: string[];
    storyLabels: string[];
    results: {
      high: string;
      mid: string;
      low: string;
      strengthHigh: string;
      strengthMid: string;
      strengthLow: string;
      scoreSuffix: string;
    };
  };
  chat: {
    title: string;
    placeholder: string;
    send: string;
    thinking: string;
    /** 연결 끊김/에러 시 표시 문구 */
    connectionError: string;
    /** 재시도 버튼 라벨 */
    retry: string;
    /** CHATBOT_TRAINING_CHECKLIST §2.3: 빈 채팅 시 소개 문구 (Foundry) */
    introFoundry: string;
    /** CHATBOT_TRAINING_CHECKLIST §2.3: 빈 채팅 시 소개 문구 (Center) */
    introCenter: string;
    /** 공간 전환 시 한 줄 안내 (Foundry) */
    spaceHintFoundry: string;
    /** 공간 전환 시 한 줄 안내 (Center) */
    spaceHintCenter: string;
    /** CHATBOT_TRAINING: 빈 채팅 시 소개 문구 (Arena) */
    introArena: string;
    /** 공간 전환 시 한 줄 안내 (Arena) */
    spaceHintArena: string;
    /** 예시 문구 섹션 라벨 (빈 채팅 시) */
    exampleLabel: string;
    /** Foundry에서 클릭 가능한 예시 문구 (2~3개) */
    examplePhrasesFoundry: string[];
    /** Center에서 클릭 가능한 예시 문구 (2~3개) */
    examplePhrasesCenter: string[];
    /** Arena에서 클릭 가능한 예시 문구 (1~2개) */
    examplePhrasesArena: string[];
    rememberConversation: string;
    deleteHistory: string;
  };
  resilience: {
    title: string;
    subtitle: string;
    /** §4·§8: 일별 궤적 있을 때 그래프 부제 (locale별) */
    dailyTrajectorySubtitle: string;
    /** §4 CENTER_PAGE: 그래프 데이터 없을 때 안내 문구 */
    emptyMessage: string;
    past: string;
    now: string;
    /** resilienceLevelDisplayLabelKey(low|mid|high) */
    levelLow: string;
    levelMid: string;
    levelHigh: string;
    levelLegendAria: string;
  };
  arenaLevels: {
    membershipPending: string;
    /** PROJECT_BACKLOG §8: 빈 상태 — 해금된 레벨이 아직 없을 때 */
    noLevelsYet: string;
    /** BRIEF §2: 빈 상태 CTA 버튼 문구 */
    emptyCta: string;
    loginRequired: string;
    track: string;
    unlockedUpTo: string;
    /** ARENA_UI_REDESIGN_BRIEF 프롬프트 D: 레벨 카드 포근한 문구 */
    levelCardHint: string;
    l4AdminGranted: string;
    staff: string;
    leader: string;
    l4Partner: string;
  };
  avatarOutfit: {
    label: string;
    professional: string;
    fantasy: string;
    hint: string;
    /** §2: 아바타 설정 페이지 전용 */
    pageTitle: string;
    backToFoundry: string;
    backToFoundryAria: string;
    goToDashboard: string;
    goToDashboardAria: string;
    preview: string;
    previewAria: string;
    character: string;
    characterLocked: string;
    characterChangeHint: string;
    outfit: string;
    noOutfits: string;
    save: string;
    saving: string;
    saveAria: string;
    errorLoad: string;
    /** outfitKey → 표시 라벨 (en/ko) */
    outfitLabels: Record<string, string>;
    /** 대시보드 Avatar 카드 — 레벨 악세·현재 옷·캐릭터 선택 제목 */
    currentLevelAccessories: string;
    currentOutfitPrefix: string;
    pickCharacterTitle: string;
    /** 대시보드: 캐릭터 잠금 시에도 전체 그리드 표시용 안내 */
    dashboardCharacterLockedGridHint: string;
    /** 아바타 설정(/profile/avatar) 메인 랜드마크 */
    avatarSettingsMainRegionAria: string;
  };
  arenaMembership: {
    label: string;
    approved: string;
    pending: string;
    hint: string;
    jobFunction: string;
    joinedAt: string;
    leaderStartedAt: string;
    submit: string;
    submitting: string;
    submitError: string;
    submitSuccess: string;
    seniorDoctorHint: string;
    validationRequired: string;
  };
  /** 멘토 대화 신청·승인 UI (PHASE_4_ELITE_5_PERCENT_SPEC §10 3차). API 응답만 표시(render-only). */
  mentorRequest: {
    cardTitle: string;
    cardDesc: string;
    requestCta: string;
    submitting: string;
    statusPending: string;
    statusApproved: string;
    statusRejected: string;
    messageLabel: string;
    messagePlaceholder: string;
    errorEliteOnly: string;
    errorPendingExists: string;
    errorSubmit: string;
    approvedCta: string;
    /** 목록 화면: 제목·빈 상태·컬럼 라벨 */
    listTitle: string;
    listEmpty: string;
    colDate: string;
    colStatus: string;
    /** 도메인 키 elite_mentor_sla_response_imminent 표시 */
    slaImminentBadge: string;
    /** eliteMentorRequestTerminalLabelKey(approved) */
    eliteDomainTerminalApproved: string;
    /** eliteMentorRequestTerminalLabelKey(rejected) */
    eliteDomainTerminalRejected: string;
    /** eliteMentorPendingStaleLabelKey */
    eliteDomainPendingStale: string;
    /** eliteMentorRequestStatusDisplayLabelKey */
    eliteStatusBadgePending: string;
    eliteStatusBadgeApproved: string;
    eliteStatusBadgeRejected: string;
  };
  /** Elite 전용 페이지 (§7 서클 모임 카드 등). isElite 분기·render-only. §4차 해금 확장: 해금 조건·노출 문구. */
  elitePage: {
    circleCardTitle: string;
    circleCardDesc: string;
    circleCardPlaceholder: string;
    /** 배지 kind → 표시 문구 (API badges[].labelKey와 매핑). */
    badgeLabels?: Record<string, string>;
    /** 엘리트 4차 해금 확장: 해금 조건·노출 (render-only). */
    unlockConditionTitle: string;
    unlockConditionMet: string;
    unlockConditionLocked: string;
    unlockExposureTitle: string;
    unlockExposureMet: string;
    unlockExposureLocked: string;
    /** Elite 멘토·심화 대화 링크 목록 접근성 */
    mentorDeepLinksListAria: string;
    loadingElitePageAria: string;
    /** Elite 페이지 메인 랜드마크 (Foundry) */
    elitePageMainRegionAria: string;
    pageTitle: string;
    pageIntroElite: string;
    badgesSectionTitle: string;
    tableColActions: string;
  };
  /** Admin: 리더십 지표 대시보드(AIR/Stage/MWD/TII 탭). render-only. */
  leadershipMetricsAdmin: {
    networkError: string;
    title: string;
    computedPrefix: string;
    refresh: string;
    loading: string;
    emptyAir: string;
    emptyStage: string;
    emptyMwd: string;
    emptyTii: string;
    emptyTeamWeekly: string;
    colEmail: string;
    colStatus: string;
    colDoneSelected: string;
    colIncomplete: string;
    colLastActivity: string;
    colStageEntry: string;
    colDaysElapsed: string;
    colMwd7: string;
    colMwd14: string;
    colDoneTotal: string;
    colCompletionRate: string;
    colLeagueTeam: string;
    colMembers: string;
    colBaseWeek: string;
    resetBadge: string;
    daysSuffix: string;
    airBandHigh: string;
    airBandMid: string;
    airBandLow: string;
    glAir: string;
    glDoneCriteriaLabel: string;
    glDoneCriteria: string;
    glIntegritySlip: string;
    glTsp: string;
    glMwd: string;
    glMwdWindow: string;
    glStageCertifiedLabel: string;
    colCertified: string;
    colCertifiedYes: string;
    colCertifiedNo: string;
    glStageCertifiedBody: string;
    glStageLriLabel: string;
    glStageLriBody: string;
    glStageResetLabel: string;
    glStageResetBody: string;
  };
  /** Admin: 멘토 신청 큐·승인 UI. API 응답만 표시(render-only). */
    mentorRequestAdmin: {
    /** Admin mentor-requests page `<main>` landmark (ko/en). */
    mainRegionAria: string;
    title: string;
    description: string;
    empty: string;
    loading: string;
    actions: string;
    userId: string;
    createdAt: string;
    message: string;
    colStatus: string;
    tableCaption: string;
    queueTableAria: string;
    approveRejectGroupAria: string;
    statusPendingLabel: string;
    approve: string;
    reject: string;
    approving: string;
    rejecting: string;
    error: string;
    errorLoadQueue: string;
    errorPatch: string;
    debugLink: string;
    usersLink: string;
    arenaMembershipLink: string;
  };
  /** Admin: 일반 사용자 계정 CRUD UI. API만 호출(render-only). */
  adminUsers: {
    /** Admin users page `<main>` landmark (ko/en). */
    mainRegionAria: string;
    networkError: string;
    errLoadList: string;
    errCreateFailed: string;
    errDeleteFailed: string;
    errPasswordFailed: string;
    validateCreate: string;
    validatePassword: string;
    deleteConfirm: string;
    passwordChanged: string;
    title: string;
    description: string;
    cancel: string;
    createUser: string;
    createFormTitle: string;
    labelEmail: string;
    labelPassword: string;
    passwordPlaceholder: string;
    creating: string;
    create: string;
    emptyUsers: string;
    colCreatedAt: string;
    colActions: string;
    newPasswordPlaceholder: string;
    save: string;
    changePassword: string;
    delete: string;
    noteTitle: string;
    note1: string;
    note2: string;
  };
  /** Admin: Quality Events 허브(stub). render-only. */
  adminQuality: {
    /** Admin quality page `<main>` landmark (ko/en). */
    mainRegionAria: string;
    networkError: string;
    subtitle: string;
    refresh: string;
    loading: string;
    dbOk: string;
    dbDown: string;
    lastPrefix: string;
    events30dPrefix: string;
    summaryTitle: string;
    windowPrefix: string;
    totalEventsPrefix: string;
    avgCssPrefix: string;
    emptyEvents: string;
    topSignaturesTitle: string;
    breakdownRoute: string;
    breakdownRole: string;
    breakdownIntent: string;
  };
  /** Admin: 조직 목록(API만). render-only. */
  adminOrganizations: {
    /** Admin organizations page `<main>` landmark (ko/en). */
    mainRegionAria: string;
  };
  /** Admin: Arena 멤버십 승인 큐. API만(render-only). */
  adminArenaMembership: {
    /** Admin arena-membership page `<main>` landmark (ko/en). */
    mainRegionAria: string;
    title: string;
    description: string;
    emptyNoPending: string;
    colJobFunction: string;
    colJoinedAt: string;
    colLeaderStartedAt: string;
    colRequestedAt: string;
    colActions: string;
    approving: string;
    approve: string;
    approveAria: string;
  };
  /** Admin 로그인 (세션 쿠키). render-only. */
  adminLogin: {
    /** Admin login page `<main>` landmark (ko/en). */
    mainRegionAria: string;
    heading: string;
    emailPlaceholder: string;
    passwordPlaceholder: string;
    loginSubmit: string;
    signingIn: string;
    loginSubmitAriaBusy: string;
    loginSubmitAriaIdle: string;
    loginFailed: string;
  };
  /** `/[locale]/admin` 허브 — 디버그 등으로 안내 이동. */
  adminHub: {
    /** Admin hub `<main>` landmark while redirecting (ko/en). */
    mainRegionAria: string;
    redirecting: string;
  };
  /** Foundry 멘토 허브(1:1 대화). */
  mentorPage: {
    deleteAllHistoryConfirm: string;
    /** 멘토 허브 메인 랜드마크 */
    pageMainLandmarkAria: string;
    topicChoiceGroupAria: string;
  };
  /** Dental RPG 장비 카드 UI. 레어리티 1–5 표시. */
  dentalRpg: {
    empty: string;
    listAria: string;
    rarity1: string;
    rarity2: string;
    rarity3: string;
    rarity4: string;
    rarity5: string;
  };
  /** Healing 인덱스·Awakening 진입. Q4. */
  healing: {
    title: string;
    intro: string;
    awakeningCta: string;
    dashboardCta: string;
    navLabel: string;
    ariaAwakening: string;
    ariaDashboard: string;
    loading: string;
    /** Phase 없을 때 빈 상태 문구. */
    emptyPhase: string;
    /** API 실패 시 에러 문구. */
    loadError: string;
    /** 2차 각성 페이지 로딩 시 메인 랜드마크 라벨. */
    awakeningLoadingLabel: string;
    /** 2차 각성 페이지 메인 랜드마크: 오류·완료·해금 조건·의식 */
    awakeningMainRegionAriaError: string;
    awakeningMainRegionAriaCompleted: string;
    awakeningMainRegionAriaEligibility: string;
    awakeningMainRegionAriaRitual: string;
    /** Healing 인덱스: 단계·로딩·에러 영역 접근성 */
    phaseProgressRegionAria: string;
    /** 2차 각성: 1~3막 의식 콘텐츠 묶음 */
    awakeningRitualActsRegionAria: string;
    /** Awakening·대시보드 링크 nav 바깥 랜드마크 */
    healingBottomNavSectionAria: string;
    mainLandmarkAria: string;
    stubApiSectionTitle: string;
    phaseFieldLabel: string;
    ringTypeFieldLabel: string;
    stubRenderOnlyNote: string;
    /** Q4 GET /api/bty/awakening 액트 목록·진행 스텁 */
    awakeningActsTrackTitle: string;
    awakeningActsTrackRegionAria: string;
    awakeningActsTriggerLine: string;
    awakeningActDone: string;
    awakeningActOpen: string;
    awakeningRecordNextCta: string;
    awakeningAllActsRecorded: string;
    awakeningProgressToastOk: string;
    awakeningProgressToast409: string;
    awakeningProgressToast400: string;
    awakeningProgressToastNetwork: string;
    awakeningActsLoadError: string;
    healingAwakeningProgressHeading: string;
    healingAwakeningProgressPct: string;
    healingProgressbarValuetext: string;
    healingProgressbarDetailAllDone: string;
    healingProgressbarDetailNext: string;
    healingProgressbarDetailSync: string;
    healingAwakeningAllActsDone: string;
    healingNextActFromApi: string;
    healingNextActSyncHint: string;
    healingProgressRefreshHint: string;
    healingProgressRefreshCta: string;
    awakeningActsGridAria: string;
    awakeningActNumberLabel: string;
    healingActsOverallProgressAria: string;
    healingActsOverallProgressCaption: string;
    healingActsOverallProgressValuetext: string;
    /** healingAwakeningActBlockedMessageKey */
    healingAwakeningBlockedOrderRequired: string;
    healingAwakeningBlockedAlreadyComplete: string;
    /** healingAwakeningActLockReasonDisplayKey */
    healingActLockPrerequisite: string;
    healingActLockAlreadyComplete: string;
    /** healingPathProgressBlockedUserDisplayKey — 쿨다운(향후 API) */
    pathProgressBlockedCooldown: string;
    /** 선행 단계 미충족 등 */
    pathProgressBlockedPhase: string;
    /** Center HealingPhaseTracker — 4단계 스테퍼 (인정→성찰→재통합→갱신) */
    healingPhaseTrackerRegionAria: string;
    healingPhaseTrackerTitle: string;
    healingPhaseStep1: string;
    healingPhaseStep2: string;
    healingPhaseStep3: string;
    healingPhaseStep4: string;
    healingPhasePrompt1: string;
    healingPhasePrompt2: string;
    healingPhasePrompt3: string;
    healingPhasePrompt4: string;
    healingPhaseAdvanceCta: string;
    healingPhaseAdvancing: string;
    healingPhaseTrackerLoadError: string;
    healingPhaseAdvanceError: string;
    healingPhaseInputsHint: string;
  };
  /** 28일 훈련(Train) 사이드바·진도. */
  train: {
    title: string;
    /** 완료 버튼·코치/요약 전환 그룹 접근성 라벨 */
    completeGroupLabel: string;
    /** 오른쪽 패널 완료 요약 영역 접근성 라벨 */
    completionSummaryLabel: string;
    /** 오른쪽 패널 코치 대화 영역 접근성 라벨 */
    coachChatLabel: string;
    /** 완료 요약 내 보강 질문 블록 접근성 라벨 */
    reinforcementLabel: string;
    /** 왼쪽 사이드바 일자 목록 네비게이션 라벨 */
    dayListLabel: string;
    /** 가운데 레슨 본문 영역 라벨 */
    lessonLabel: string;
    /** 잠긴 레슨 안내(status) 라벨 */
    lockedLabel: string;
    /** 오른쪽 패널(완료 요약·코치 대화) 영역 라벨 */
    sidebarPanelLabel: string;
    /** 28일 훈련(journey) 시작 페이지 제목·안내 */
    journeyStartTitle: string;
    journeyStartIntro: string;
    journeyStartDay1Link: string;
    journeyStartDay1Aria: string;
    /** `/train/start` 페이지 `<main>` 랜드마크 */
    journeyStartMainRegionAria: string;
    /** `/train/28days` 허브 페이지 `<main>` 랜드마크 */
    track28HubMainRegionAria: string;
    /** `/train/28days/day/[day]` 레슨 페이지 `<main>` 랜드마크 */
    track28DayMainRegionAria: string;
    /** `/[locale]/train/loading` Suspense `<main>` 라벨 */
    trainRouteSuspenseMainRegionAria: string;
  };
  /** Dojo 50문항 결과 화면. 영역별 점수·Dr. Chi 코멘트 표시(render-only). */
  dojoResult: {
    title: string;
    areaScoresTitle: string;
    drChiCommentTitle: string;
    /** summaryKey별 Dr. Chi 코멘트 (API mentorComment 템플릿용). */
    resultCommentHigh: string;
    resultCommentMid: string;
    resultCommentLow: string;
    loading: string;
    noAnswers: string;
    backToAssessment: string;
    apiError: string;
    dimensionLabels: Record<string, string>;
    /** 결과 페이지 다음 액션 그룹 라벨 (접근성). */
    nextActionsLabel: string;
    /** Dojo 결과 페이지 하단 액션 그룹 라벨 (다시 진단·과거 보기). */
    resultActionsLabel: string;
    /** 50문항 현재 문항·선택지 카드 영역 */
    questionStepSectionAria: string;
    /** 결과: 점수 막대·코멘트 묶음 */
    resultScoresInsightRegionAria: string;
    /** Dojo 50문항 페이지 메인 랜드마크 */
    dojoPageMainAria: string;
    /** Dojo 과거 진단 이력 페이지 메인 영역 */
    dojoHistoryMainRegionAria: string;
    /** Dojo 결과 페이지(점수·코멘트·액션) 메인 영역 */
    dojoResultMainRegionAria: string;
  };
  /** Arena 런 페이지·컴포넌트 문구. locale=ko 시 한국어만 표시(render-only). */
  arenaRun: {
    reflectionPrompt: string;
    errorStartRun: string;
    errorSignIn: string;
    errorAlreadySubmitted: string;
    errorSubmitFailed: string;
    heroTitle: string;
    otherLabel: string;
    otherRecorded: string;
    nextScenario: string;
    otherPlaceholder: string;
    cancel: string;
    submit: string;
    liveRanking: string;
    completeErrorPrefix: string;
    completeErrorSuffix: string;
    headerTitle: string;
    headerSubtitle: string;
    pauseLabel: string;
    resetLabel: string;
    mainLabel: string;
    /** Arena header identity chip — link to profile / avatar (no metrics). */
    headerIdentityLinkAria: string;
    startSimulation: string;
    confirm: string;
    reflectionTitle: string;
    reflectionNext: string;
    reflectionOptional: string;
    reflectionPlaceholder: string;
    systemOutput: string;
    skipFollowUp: string;
    followUpSelected: string;
    step6Title: string;
    /** `/bty-arena` 본문 `<main>` — Lab·`/play`·`/record` 랜드마크 문구와 분리 */
    arenaRunPageMainRegionAria: string;
    preparingNewScenarioAria: string;
    scenarioProgressPanelAria: string;
    reflectionBusyAria: string;
    reflectionSentenceBlockAria: string;
    reflectionChoiceOptionsAria: string;
    /** 성찰 본문 글자 수·힌트 영역 */
    reflectionLengthHintAria: string;
    reflectionCharCount: string;
    youChose: string;
    keyInsight: string;
    principle: string;
    completeBtn: string;
    reflectionBonusLabel: string;
    deepeningTitle: string;
    nextActionLabel: string;
    step7Title: string;
    step7Body: string;
    choiceLabel: string;
    /** 시나리오 완료(결과 전환) 시 토스트 문구 */
    scenarioCompletedToast: string;
    /** 시나리오를 찾을 수 없을 때(빈 상태) 메시지·힌트 */
    scenarioNotFound: string;
    scenarioNotFoundHint: string;
    /** §4 Past scenarios: 제목·접기/펼치기·빈 상태·에러·재시도 */
    pastScenariosHeading: string;
    pastScenariosCollapse: string;
    pastScenariosExpand: string;
    loadingHistory: string;
    couldNotLoadHistory: string;
    /** 세션 쿠키가 API에 안 잡힐 때(Edge 등) 과거 시나리오 대신 안내 */
    pastScenariosSessionHint: string;
    retryLoadHistory: string;
    retry: string;
    noCompletedScenarios: string;
    startFirstScenario: string;
    completed: string;
    inProgress: string;
    /** POST reflect 실패 시(성찰 저장은 됨) */
    reflectDeepeningUnavailable: string;
    /** `/bty-arena` 레벨·세션 준비 로딩 */
    runPageLevelCheckMainRegionAria: string;
    /** 입문 경로 게이트(리다이렉트 전) */
    runPageBeginnerPathMainRegionAria: string;
    /** 시나리오 없음 빈 상태 */
    runPageNoScenarioMainRegionAria: string;
    /** `/bty-arena` 에러 전용 셸 `<main>` — `runPageNoScenarioMainRegionAria`·Lab·`/play`·기록과 분리 */
    arenaRunErrorMainRegionAria: string;
    arenaRunErrorTitle: string;
    arenaRunErrorDescription: string;
    arenaRunErrorHint: string;
    /** Canonical elite — section labels + initial decision hint */
    eliteSetupRoleLabel: string;
    eliteSetupPressureLabel: string;
    eliteSetupTradeoffLabel: string;
    eliteInitialDecisionHint: string;
    /** Elite step 2 — one-tap primary pick (no Confirm / Other). */
    elitePrimaryPickHint: string;
    /** Narrative block label above `scenario.context` (opening step). */
    eliteOpeningNarrativeLabel: string;
    /** One line after run complete — next steps may include action contract (snapshot authority unchanged). */
    eliteRunCompletePrepareHint: string;
    /** Elite post-choice flow (replaces generic OutputPanel for `eliteSetup` scenarios). */
    elitePostFlowRegionAria: string;
    elitePostConfirmTitle: string;
    elitePostConfirmLead: string;
    elitePostConfirmNext: string;
    elitePostExplainTitle: string;
    elitePostExplainPrompt: string;
    elitePostExplainOptionalLabel: string;
    elitePostExplainPlaceholder: string;
    elitePostExplainSubmit: string;
    elitePostIntegrationTitle: string;
    elitePostIntegrationNext: string;
    elitePostBridgeTitle: string;
    elitePostBridgeDefault: string;
    elitePostBridgeNext: string;
    elitePostCompleteTitle: string;
    elitePostCompleteBody: string;
    /** `{xp}` placeholder for API `lastXp` (render-only). */
    elitePostXpLine: string;
    /** UX_FLOW_LOCK_V1 Step 5 — Pattern Mirror */
    eliteMirrorTitle: string;
    eliteMirrorLead: string;
    eliteMirrorEmpty: string;
    eliteMirrorContinue: string;
    eliteMirrorLoadError: string;
    /** Steps 3–4 escalation + forced trade-off (UX_FLOW_LOCK_V1 §2) */
    eliteSituationUpdateHeader: string;
    eliteDecisionRequiredHeader: string;
    eliteRunStepAdvanceError: string;
    /** Canonical elite chain scenario missing dbScenarioId/dbChoiceId — do not use legacy event path */
    eliteBindingIntegrityError: string;
    /** Step 4 forced trade-off — label above each option’s cost line */
    eliteForcedTradeoffCostLabel: string;
    /** Tradeoff card — optional line from JSON `stage_2_escalation.outcome` */
    eliteTradeoffProtectsLabel: string;
    /** Tradeoff card — optional line from JSON `stage_2_escalation.risk` */
    eliteTradeoffRisksLabel: string;
    /** Canonical 3-step flow — copy above primary ChoiceList */
    arenaFlowPhasePrimaryInstruction: string;
    /** Canonical 3-step flow — copy above forced tradeoff */
    arenaFlowPhaseTradeoffInstruction: string;
    /** Canonical 3-step flow — copy above action decision */
    arenaFlowPhaseActionDecisionInstruction: string;
    /** Action decision — badge on AD1-style choice */
    eliteActionDecisionAdCommitmentBadge: string;
    /** Action decision — badge on AD2-style choice */
    eliteActionDecisionAdDeferBadge: string;
    eliteActionDecisionAd1TraitConcrete: string;
    eliteActionDecisionAd1TraitTimeBound: string;
    eliteActionDecisionAd1TraitObservable: string;
    eliteActionDecisionAd1TraitContractGate: string;
    eliteActionDecisionAd2TraitDelay: string;
    eliteActionDecisionAd2TraitNonCommitment: string;
    eliteActionDecisionAd2TraitNextOrReexposure: string;
    /** Step 5 — real-world action commitment (distinct from situation/tradeoff interpretation) */
    eliteActionDecisionTitle: string;
    eliteActionDecisionLead: string;
    eliteActionDecisionCommitmentLabel: string;
    eliteActionDecisionRegionAria: string;
    eliteV2ErrorNotConfiguredTitle: string;
    eliteV2ErrorNotConfiguredBody: string;
    eliteV2ErrorMissingBranchTitle: string;
    eliteV2ErrorMissingBranchBody: string;
    eliteV2ErrorInvalidCostTitle: string;
    eliteV2ErrorInvalidCostBody: string;
    /** Step 4 — primary choice id missing from scenario.choices (desync / bad data) */
    eliteInvalidPrimaryChoiceTitle: string;
    eliteInvalidPrimaryChoiceBody: string;
    /** After step 4 — minimal end-of-run (no mirror/contract/gate) */
    eliteRunCompleteLine: string;
    eliteRunCompleteRegionAria: string;
    eliteRunCompleteLoading: string;
    /** Primary CTA after run complete — neutral; does not promise next scenario (may sync to ACTION_REQUIRED). */
    eliteRunCompleteContinueCta: string;
    /** GET session router 409 `action_contract_pending` — Arena blocked */
    arenaPendingContractTitle: string;
    /** Shown only when `runtime_state === ACTION_REQUIRED` — bridges AD1 pick → contract gate */
    arenaObservableActionConfirmationLead: string;
    arenaPendingContractLead: string;
    arenaPendingContractActionLabel: string;
    arenaPendingContractDeadlineLabel: string;
    arenaPendingContractVerificationLabel: string;
    arenaPendingContractRetry: string;
    /** Primary: open My Page to complete the pending action contract (not Center). */
    arenaPendingContractMyPageCta: string;
    arenaPendingContractRegionAria: string;
    /** STAB-06-FIX-03 (U3): terminal completion surface for self-attest auto-approved actions. */
    arenaActionCompletedTitle: string;
    arenaActionCompletedLead: string;
    arenaActionCompletedNextCta: string;
    arenaPulseQuestion: string;
    arenaPulseLow: string;
    arenaPulseHigh: string;
    arenaPulseSkipCta: string;
    arenaPulseThanks: string;
    /** Server runtime_state placeholder — forced reset (snapshot-first gate). */
    arenaSnapshotForcedResetPlaceholder: string;
    /** FORCED_RESET_PENDING — Center completion required (not Arena). */
    arenaForcedResetGateTitle: string;
    arenaForcedResetGateLead: string;
    arenaForcedResetGoCenterCta: string;
    /** Server runtime_state placeholder — re-exposure due (snapshot-first gate). */
    arenaSnapshotReexposurePlaceholder: string;
    /** Unified `arena.reexposure.*` namespace-aligned keys. */
    arenaReexposurePanelTitle: string;
    arenaReexposurePanelDescription: string;
    arenaReexposurePanelBeginButton: string;
    arenaReexposureBlockedNextTitleV2: string;
    arenaReexposureBlockedNextDescriptionV2: string;
    arenaReexposureBlockedNextButtonV2: string;
    arenaReexposureInternalStatusInterventionSensitivityUp: string;
    arenaReexposureValidationChanged: string;
    arenaReexposureValidationUnstable: string;
    arenaReexposureValidationNoChange: string;
    /** Stale re-exposure due flag without pending outcome — recovery refetch (must not deadlock). */
    arenaReexposureStaleRecoveryMessage: string;
    /** Snapshot present but play surface not allowed (sync / non-ready runtime). */
    arenaSnapshotPlaySurfaceBlockedHint: string;
    /** Title when play UI is blocked but scenario exists — not a catalog 404. */
    arenaPlaySurfaceBlockedTitle: string;
    /** Unified loader — session router / scenario aligning (not an error). */
    arenaAligningLoaderTitle: string;
    arenaAligningLoaderLead: string;
    /** Short labels for {@link ArenaRuntimeStateBanner} gate reason. */
    arenaGateLabelContract: string;
    arenaGateLabelReexposure: string;
    arenaGateLabelReset: string;
    arenaGateLabelNext: string;
    arenaGateLabelPlay: string;
    arenaGateLabelAligning: string;
    /** `runtime_state` NEXT_SCENARIO_READY — post run/complete, before next GET session resolves. */
    arenaSnapshotNextScenarioReadyTitle: string;
    arenaSnapshotNextScenarioReadyHint: string;
    /** After contract execution (QR/submit) — desktop parity with mobile "Execution recorded / Next scenario unlocked". */
    arenaPostContractNextScenarioTitle: string;
    arenaPostContractNextScenarioHint: string;
    arenaContinueToNextScenarioCta: string;
    /** Step 6 — Action Contract */
    eliteContractTitle: string;
    eliteContractIntro: string;
    eliteContractWhoLabel: string;
    eliteContractWhoHint: string;
    eliteContractWhatLabel: string;
    eliteContractWhatHint: string;
    eliteContractHowLabel: string;
    /** Shown above the "how" field — verbatim script, no meta explanation */
    eliteContractHowInstruction: string;
    /** Lead with ownership / honesty, not accusation */
    eliteContractHowResponsibilityLead: string;
    eliteContractWhenLabel: string;
    eliteContractWhenHint: string;
    eliteContractWhoPlaceholder: string;
    eliteContractWhatPlaceholder: string;
    eliteContractHowPlaceholder: string;
    eliteContractWhenPlaceholder: string;
    eliteContractSubmit: string;
    eliteContractSubmitting: string;
    eliteContractLoadError: string;
    eliteContractEnsureFailed: string;
    eliteContractCreating: string;
    /** Heading when draft contract could not be created (no form shown) */
    eliteContractInitFailedTitle: string;
    /** Step 6 — POST returned `gated: "pattern_threshold"` (not an error) */
    eliteContractPatternThresholdTitle: string;
    eliteContractPatternThresholdBody: string;
    eliteContractPatternThresholdContinue: string;
    eliteContractRevisionRequired: string;
    /** After action contract — execution follow-up (yes/no + optional first 30s) */
    actionContractFollowupTitle: string;
    actionContractFollowupLead: string;
    actionContractFollowupQuestion: string;
    actionContractFollowupYes: string;
    actionContractFollowupNo: string;
    actionContractFollowupFirst30Label: string;
    actionContractFollowupFirst30Placeholder: string;
    actionContractFollowupSubmit: string;
    actionContractFollowupSubmitting: string;
    actionContractFollowupError: string;
    /** Step 7 — Execution Gate + §3 lock copy */
    eliteExecutionGateTitle: string;
    eliteExecutionGateIntro: string;
    eliteExecutionContractHeading: string;
    eliteExecutionVerificationRecorded: string;
    eliteExecutionAwaitingVerification: string;
    eliteExecutionVerificationNotRecorded: string;
    eliteExecutionUnderReview: string;
    eliteExecutionAdditionalConfirmation: string;
    eliteExecutionAbandonedLead: string;
    eliteExecutionResumeHint: string;
    eliteExecutionRawTextFallback: string;
    eliteExecutionNextBlocked: string;
    eliteExecutionLoadError: string;
    /** Step 7 — user continued from step 6 without a draft (pattern window below contract threshold) */
    eliteExecutionPatternThresholdDeferLine: string;
  };
  /** PROJECT_BACKLOG §8: 대시보드 감정 스탯 카드 빈 상태·에러 */
  emotionalStats: {
    emptyMessage: string;
    /** §2 v3: 섹션 제목 (오늘의 성장 / Today's growth) */
    sectionTitle: string;
    errorLoad: string;
  };
  /** 프로필 페이지: 표시용 필드(display_name) 표시·편집. API 응답/제출만 사용. */
  profile: {
    title: string;
    displayNameLabel: string;
    displayNamePlaceholder: string;
    save: string;
    saving: string;
    backToDashboard: string;
    errorLoad: string;
    errorSave: string;
    errorTooLong: string;
    avatarSettingsLink: string;
    /** 저장 버튼 접근성(스크린 리더용) */
    saveButtonAriaLabel: string;
    /** 프로필 화면 주 랜드마크(표시 이름·아바타 링크) */
    profileMainRegionAria: string;
  };
  /** Phase 1 스텁: Arena wireframe / Growth / My Page 공통 문구·네비 */
  uxPhase1Stub: {
    bottomNavArena: string;
    bottomNavGrowth: string;
    bottomNavMyPage: string;
    growthTitle: string;
    growthSubtitle: string;
    growthLead: string;
    growthReturnArena: string;
    growthWeeklyRankingLink: string;
    growthFoundryLink: string;
    growthOptionsRegionAria: string;
    wireframeLandmarkAria: string;
    /** Continue / Play primary actions — distinct from hub·beginner main labels */
    wireframeCtaRegionAria: string;
    /** Fixed BTY tab bar on wireframe screen — distinct from in-page nav regions */
    wireframeBottomNavAria: string;
    weeklySnapshotRegionAria: string;
    wireframeScreenTitle: string;
    wireframeScreenSubtitle: string;
    wireframeSystemReady: string;
    wireframePlayGame: string;
    wireframeStartScenario: string;
    wireframeContinue: string;
    wireframeResumeLast: string;
    wireframeOr: string;
    wireframeWeeklyRankLine: string;
    wireframeSeasonLine: string;
    wireframeWireRuleNote: string;
    wireframeResultStubPrefix: string;
    resultTitle: string;
    resultSubtitle: string;
    resultCoreXpSample: string;
    resultWeeklyXpSample: string;
    resultSampleNote: string;
    resultSystemTitle: string;
    resultSystemBody: string;
    resultContinueCta: string;
    resultReturnArenaCta: string;
    resultWireHubLink: string;
    growthArenaPlayLink: string;
    growthArenaWireHubLink: string;
    growthShortcutsNavAria: string;
    growthElitePageLink: string;
    wireframeFoundryEliteAria: string;
    growthHealingLink: string;
    growthAwakeningLink: string;
    resultRunCompleteHint: string;
    resultShareRegionAria: string;
    resultShareCta: string;
    resultShareCopied: string;
    resultShareFailed: string;
    resultShareClipboardLine: string;
    resultNextActionsRegionAria: string;
    resultNextScenarioSectionLabel: string;
    resultNextScenarioCta: string;
    resultNextScenarioSub: string;
    wireframeArenaPracticeNavAria: string;
    wireframeDojoLink: string;
    wireframeIntegrityLink: string;
    wireframeRunsDashboardHint: string;
    growthMyRankCardTitle: string;
    growthMyRankRegionAria: string;
    growthMyRankLoading: string;
    growthMyRankYourRank: string;
    growthMyRankWeeklyXpLine: string;
    growthMyRankNoRank: string;
    /** 이번 주 주간 XP 0·비참가 안내 */
    growthMyRankWeekNotParticipated: string;
    growthMyRankAnonymous: string;
    growthMyRankSeeLeaderboard: string;
    /** Comeback modal (3+ day return) — calm recovery tone */
    comebackTitle: string;
    comebackBody: string;
    comebackResumeJourneyCta: string;
    comebackNotNowCta: string;
    growthNavDojoTitle: string;
    growthNavDojoLine: string;
    growthNavIntegrityTitle: string;
    growthNavIntegrityLine: string;
    growthNavGuidanceTitle: string;
    growthNavGuidanceLine: string;
    growthNavReflectionTitle: string;
    growthNavReflectionLine: string;
    growthBackToGrowth: string;
    growthJourneyLandmarkAria: string;
    /** `/[locale]/growth/loading` Suspense `<main>` 라벨 */
    growthRouteSuspenseMainRegionAria: string;
    arenaHubTitle: string;
    arenaHubSubtitle: string;
    arenaHubReadyTitle: string;
    arenaHubReadyBody: string;
    arenaHubCardTitleContinue: string;
    arenaHubCardBodyResume: string;
    arenaHubCardBodyNew: string;
    arenaHubCtaWeeklyRank: string;
    arenaHubEntryLoading: string;
    arenaHubContinueCta: string;
    arenaHubPlayCta: string;
    arenaHubWeeklyRankLabel: string;
    arenaHubSeasonEndsLabel: string;
    arenaHubSummaryRegionAria: string;
    arenaHubSummaryLoadError: string;
    /** `/[locale]/bty-arena/hub` ScreenShell `<main>` 랜드마크 */
    arenaHubMainRegionAria: string;
    /** `/[locale]/bty-arena` 미션 로비(세션 준비 후) `<main>` 랜드마크 */
    arenaMissionLobbyMainRegionAria: string;
    /** 미션 로비 세션 hydration 대기 `<main>` 랜드마크 */
    arenaMissionLobbyLoadingMainRegionAria: string;
    /** `/[locale]/bty-arena/lobby` — `<main>` (허브·와이어·입문과 문구 분리) */
    lobbyLandmarkAria: string;
    /** `/[locale]/bty-arena/lobby` — hydration 대기 `<main>` */
    lobbyLoadingMainRegionAria: string;
    /** 미션 로비 히어로 **Enter / Resume** CTA 묶음 */
    lobbyCtaRegionAria: string;
    /** 미션 로비 상단 스테이지 바 (`ArenaTopBar`) */
    lobbyTopBarAria: string;
    /** `/[locale]/bty-arena/loading` — 라우트 세그먼트 로딩 `<main>` 랜드마크 */
    arenaBtyArenaRouteSegmentLoadingMainRegionAria: string;
    /** `/bty-arena/play` 로딩 `<main>` 랜드마크 */
    arenaMissionPlayLoadingMainRegionAria: string;
    /** `/bty-arena/play` 플레이 단계 `<main>` 랜드마크 */
    arenaMissionPlayMainRegionAria: string;
    /** `/bty-arena/play` — `ArenaTopBar` (`lobbyTopBarAria`·record과 문구 분리) */
    arenaMissionPlayTopBarAria: string;
    /** `/bty-arena/play` — 좌측 시각 장면 패널 (`role="region"`) */
    arenaMissionPlaySceneRegionAria: string;
    /** `/bty-arena/play` — 우측 시나리오·선택·확정 (`lobbyCtaRegionAria`·`recordCtaRegionAria`와 분리) */
    arenaMissionPlayDecisionsRegionAria: string;
    /** `/bty-arena/result` (미션 결정 기록·해석) 로딩 `<main>` 랜드마크 */
    arenaMissionResultLoadingMainRegionAria: string;
    /** `/bty-arena/result` 본문 `<main>` 랜드마크 */
    arenaMissionResultMainRegionAria: string;
    /** `DelayedOutcomeBanner` — 7일 지연 결과 (ScenarioSessionShell) */
    delayedOutcomeBannerRegionAria: string;
    delayedOutcomeIntro: string;
    delayedOutcomeDismiss: string;
    delayedOutcomeFlagHeroTrap: string;
    delayedOutcomeFlagIntegritySlip: string;
    delayedOutcomeFlagClean: string;
    delayedOutcomeFlagRoleMirror: string;
    /** `/bty-arena/play/resolve` 로딩 `<main>` 랜드마크 */
    arenaResolveSessionLoadingMainRegionAria: string;
    /** `/bty-arena/play/resolve` 본문 `<main>` 랜드마크 */
    arenaResolveSessionMainRegionAria: string;
    /** `/bty-arena/lab` Leader Lab `<main>` 랜드마크 */
    arenaLabMainRegionAria: string;
    /** `/bty-arena/lab` — 일일 시도·남은 할당 (`role="region"` · record·play 랜드마크와 문구 분리) */
    arenaLabUsageRegionAria: string;
    /** `/bty-arena/lab` — 다음 행동·Arena 복귀 (`role="region"` · `<nav>`와 중첩 · record·lobby CTA와 키 분리) */
    arenaLabCtaRegionAria: string;
    /** `/bty-arena/lab` — Arena 런 선택으로 돌아가기 (`<nav>` · record CTA·하단 탭·미션 플레이와 분리) */
    arenaLabBackNavAria: string;
    arenaLabTitle: string;
    arenaLabLead: string;
    arenaLabRemainingTodayLabel: string;
    /** `남은/전체` 숫자 뒤에 붙는 접미사 (예: " 회 남음" / " remaining") */
    arenaLabRemainingSuffix: string;
    /** 사용 횟수 줄 접두 (값은 숫자로 이어짐) */
    arenaLabUsedPrefix: string;
    arenaLabBackToArena: string;
    /** 사용량 API 실패 시 기본 문구 */
    arenaLabUsageLoadError: string;
    /** Lab 점수 카드 — `calculateArenaLabScore` 표시 (`{score}`·`{max}`) */
    arenaLabScoreCardRegionAria: string;
    arenaLabScoreTitle: string;
    arenaLabScoreSlashMax: string;
    arenaLabScoreDifficultyPrefix: string;
    arenaLabScoreAttemptPrefix: string;
    arenaLabScoreDifficultyEasy: string;
    arenaLabScoreDifficultyMid: string;
    arenaLabScoreDifficultyHard: string;
    arenaLabScoreDifficultyExtreme: string;
    /** Lab — 활성 시나리오 없음 (`ArenaLabEmptyState`) */
    arenaLabEmptyRegionAria: string;
    arenaLabEmptyTitle: string;
    arenaLabEmptyMessage: string;
    arenaLabEmptyHint: string;
    /** `/bty-arena/beginner` 시나리오 준비 중(첫 페인트) */
    arenaBeginnerPathInitMainRegionAria: string;
    /** `/bty-arena/beginner` 7단계 플로 `<main>` */
    arenaBeginnerPathMainRegionAria: string;
    /** `/bty-arena/beginner` 완료·결과 화면 `<main>` */
    arenaBeginnerPathCompleteMainRegionAria: string;
    /** 상단 진행 꼬리말 — 입문/Beginner */
    arenaBeginnerPathTrackLabel: string;
    /** beginner-complete 요청 처리 중 */
    arenaBeginnerPathSubmittingAria: string;
    /** `/bty-arena/record` (기록 요약 스텁) ScreenShell `<main>` — 허브·로비·와이어·입문과 문구 분리 */
    recordLandmarkAria: string;
    /** `/bty-arena/record` — 플레이·허브 CTA 묶음 */
    recordCtaRegionAria: string;
    /** `/bty-arena/record` — 하단 3탭 (와이어와 동일 UX, 키 분리) */
    recordBottomNavAria: string;
    arenaResultEyebrow: string;
    arenaResultRecordedTitle: string;
    arenaResultRecordedSubtitle: string;
    arenaResultSystemNoteTitle: string;
    arenaResultSystemNoteBody: string;
    arenaResultContinuePlayCta: string;
    arenaResultReturnHubCta: string;
    /** Growth 허브 — 4카드 (Dojo · Integrity Mirror · Guidance · Journey) */
    growthHubSectionLabel: string;
    growthHubHeadline: string;
    growthHubLead: string;
    growthHubCardsNavAria: string;
    /** Growth 허브 본문 랜드마크 (스크린리더) */
    growthHubMainRegionAria: string;
    growthCardDojoTitle: string;
    growthCardDojoDesc: string;
    growthCardIntegrityTitle: string;
    growthCardIntegrityDesc: string;
    growthCardGuidanceTitle: string;
    growthCardGuidanceDesc: string;
    /** Arena → Growth reflection entry */
    growthReflectionRegionAria: string;
    growthReflectionEyebrow: string;
    growthReflectionTitle: string;
    growthReflectionSubtitle: string;
    growthReflectionFocusLabel: string;
    growthReflectionCueLabel: string;
    growthReflectionOpenCta: string;
    growthReflectionSuggestedTrack: string;
    growthReflectionBackHub: string;
    growthReflectionNoSeed: string;
    growthRecoveryCardTitle: string;
    growthRecoveryCardBody: string;
    /** Growth reflection entry (dark airlock) */
    growthReflectionAirlockEyebrow: string;
    growthReflectionAirlockTitle: string;
    growthReflectionAirlockLead: string;
    growthReflectionFocusChannel: string;
    growthReflectionFocusClarity: string;
    growthReflectionFocusTrust: string;
    growthReflectionFocusRegulation: string;
    growthReflectionFocusAlignment: string;
    growthReflectionFocusReady: string;
    growthReflectionSectionPrompt: string;
    growthReflectionSectionCue: string;
    growthReflectionSectionRecovery: string;
    growthReflectionOpenReflection: string;
    growthReflectionOpenRecovery: string;
    growthReflectionReturnArena: string;
    growthReflectionCueEmpty: string;
    growthRecoveryPlaceholderTitle: string;
    growthRecoveryPlaceholderLead: string;
    /** Growth reflection writing (structured chamber) */
    growthWritingGuidedLabel: string;
    growthWritingHeadline: string;
    growthWritingSubhead: string;
    growthWritingPromptSection: string;
    growthWritingCueInline: string;
    growthWritingQ1Label: string;
    growthWritingQ1: string;
    growthWritingQ2Label: string;
    growthWritingQ2: string;
    growthWritingQ3Label: string;
    growthWritingQ3: string;
    growthWritingCommitmentLabel: string;
    growthWritingCommitmentHelper: string;
    growthWritingPlaceholder: string;
    growthWritingCommitmentPlaceholder: string;
    growthWritingSystemNote: string;
    growthWritingSave: string;
    growthWritingSaved: string;
    growthWritingReturnArena: string;
    growthWritingViewMyPage: string;
    growthWritingNoSeed: string;
    /** Growth history — reflection accumulation board */
    growthHistoryRegionAria: string;
    growthHistoryEyebrow: string;
    growthHistoryHeadline: string;
    growthHistoryLead: string;
    growthHistoryEmpty: string;
    growthHistoryRecoveryStrip: string;
    growthHistoryFocusLabel: string;
    growthHistoryCommitmentLabel: string;
    growthHistoryRecoveryBadge: string;
    growthHistoryBackGrowth: string;
    growthCardHistoryTitle: string;
    growthCardHistoryDesc: string;
    growthHistoryReflectionCountLabel: string;
    growthHistoryCapsuleClarity: string;
    growthHistoryCapsuleTrust: string;
    growthHistoryCapsuleRegulation: string;
    growthHistoryCapsuleAlignment: string;
    growthHistoryArenaReflectionBadge: string;
    growthHistoryRecoveryTitle: string;
    growthHistoryOpenRecoveryCta: string;
    growthHistoryEmptyEyebrow: string;
    growthHistoryEmptyTitle: string;
    growthHistoryEmptySubtext: string;
    growthHistoryCtaOpenLatest: string;
    growthHistoryCtaReturnArena: string;
    /** Recovery entry — re-entry gate (not failure / not warning) */
    recoveryPromptLowRegulationTitle: string;
    recoveryPromptLowRegulationBody: string;
    recoveryPromptLowRegulationCue: string;
    recoveryPromptRepeatedFrictionTitle: string;
    recoveryPromptRepeatedFrictionBody: string;
    recoveryPromptRepeatedFrictionCue: string;
    recoveryPromptPressureAccumulationTitle: string;
    recoveryPromptPressureAccumulationBody: string;
    recoveryPromptPressureAccumulationCue: string;
    recoveryEntryEyebrow: string;
    recoveryEntryHeadline: string;
    recoveryEntryLead: string;
    recoveryEntryChannelLabel: string;
    recoveryChannelLowRegulation: string;
    recoveryChannelRepeatedFriction: string;
    recoveryChannelPressureAccumulation: string;
    recoveryChannelReady: string;
    recoveryEntrySignalTitle: string;
    recoveryEntryCueLabel: string;
    recoveryEntryNoPrompt: string;
    recoveryEntryQ1Label: string;
    recoveryEntryQ1: string;
    recoveryEntryQ2Label: string;
    recoveryEntryQ2: string;
    recoveryEntryQ3Label: string;
    recoveryEntryQ3: string;
    recoveryEntryPlaceholder: string;
    recoveryEntrySystemStrip: string;
    recoveryEntrySave: string;
    recoveryEntryReturnGrowth: string;
    recoveryEntryReturnArena: string;
    recoveryEntryRegionAria: string;
  };
  /** /[locale]/journal — 플레이스홀더 저널 UI */
  journal: {
    title: string;
    subtitle: string;
    prompt: string;
    placeholder: string;
    save: string;
    saveAria: string;
    footerDrChi: string;
    chatToggle: string;
    chatToggleAria: string;
    chatTitle: string;
    chatClose: string;
    chatCloseAria: string;
    chatStub: string;
    /** Journal 페이지 메인 랜드마크 (Center 계열 / dear 허브) */
    journalMainRegionAria: string;
    /** 저널 로딩·Suspense 시 `<main>` 라벨 */
    journalLoadingMainRegionAria: string;
    /** 비로그인 리다이렉트 시 `<main>` 라벨 */
    journalRedirectingMainRegionAria: string;
    /** 비로그인 리다이렉트 안내 문구 */
    journalRedirectingMessage: string;
  };
  /** my-page progress / team / leader 스텁 카피 */
  myPageStub: {
    subMyPage: string;
    progressTitle: string;
    coreXp: string;
    weeklyXp: string;
    streak: string;
    systemMsg: string;
    progressStage: string;
    progressRank: string;
    progressStreakVal: string;
    progressSystemLine: string;
    progressFootnote: string;
    teamTitle: string;
    teamTiiCard: string;
    /** Team TII numeric row until Realtime/API wired — do not show fake AIR-like decimals */
    teamTiiPlaceholder: string;
    teamStatusCard: string;
    teamInnerStatus: string;
    teamStable: string;
    teamInnerTrend: string;
    teamTrendVal: string;
    teamRankCard: string;
    teamRankCaption: string;
    teamRankLine: string;
    teamFooter: string;
    leaderTitle: string;
    leaderCardStatus: string;
    leaderBuilding: string;
    leaderCardReadiness: string;
    leaderReadinessVal: string;
    leaderCardCert: string;
    leaderCertVal: string;
    leaderSystem: string;
    leaderSystemLine: string;
    leaderFootnote: string;
    overviewHealingCardTitle: string;
    overviewHealingCardLead: string;
    overviewHealingNavAria: string;
    recentRunsCardTitle: string;
    recentRunsRegionAria: string;
    recentRunsLoading: string;
    recentRunsEmpty: string;
    recentRunsAnonymous: string;
    recentRunsFootnote: string;
    recentRunsLoadMore: string;
    recentRunsLoadingMore: string;
    recentRunsScenarioPrefix: string;
    recentRunsStartedPrefix: string;
    recentRunsStatusPrefix: string;
    recentRunsOpenDetail: string;
    runDetailPageTitle: string;
    runDetailPageSubtitle: string;
    runDetailRegionAria: string;
    runDetailBackMyPage: string;
    runDetailLoading: string;
    /** arena.run_detail.loading — 도메인 키와 동일 문구 */
    runDetailSkeletonLoading: string;
    /** arena.run_detail.empty */
    runDetailSkeletonEmpty: string;
    runDetailError: string;
    runDetailNotFound: string;
    runDetailSignIn: string;
    runDetailScenarioId: string;
    runDetailStatus: string;
    runDetailStartedAt: string;
    runDetailCompletedAt: string;
    runDetailLocaleLabel: string;
    runDetailDifficulty: string;
    /** 런 상태 API 값 → 표시 라벨 (render-only 매핑) */
    runStatusLabelDone: string;
    runStatusLabelInProgress: string;
    runStatusLabelStarted: string;
    /** My Page ScreenShell — overview / subpages */
    myPageShellOverviewTitle: string;
    myPageShellOverviewSubtitle: string;
    /** My Page 개요 카드 묶음 랜드마크 */
    myPageOverviewRegionAria: string;
    myPageShellProgressTitle: string;
    myPageShellProgressSubtitle: string;
    myPageShellTeamTitle: string;
    myPageShellTeamSubtitle: string;
    myPageShellLeaderTitle: string;
    myPageShellLeaderSubtitle: string;
    myPageCardIdentity: string;
    myPageCardProgress: string;
    myPageCardTeam: string;
    myPageLinkView: string;
    myPageLinkLeader: string;
    myPageLabelCodeName: string;
    myPageLabelStage: string;
    myPageLabelCoreProgress: string;
    myPageLabelWeeklyProgress: string;
    myPageOverviewTeamStatus: string;
    myPageProgressMovement: string;
    myPageTeamNoteHeading: string;
    myPageAccountLink: string;
    myPageTabsAria: string;
    myPageTabOverview: string;
    myPageTabProgress: string;
    myPageTabTeam: string;
    myPageTabLeader: string;
    myPageTabAccount: string;
    /** `/[locale]/my-page/loading` Suspense `<main>` 라벨 */
    myPageRouteSuspenseMainRegionAria: string;
    /** My Page leadership console (Arena accumulation) */
    leadershipRegionAria: string;
    leadershipCardInsight: string;
    leadershipCardInfluence: string;
    leadershipCardRhythm: string;
    leadershipSectionSignalPattern: string;
    leadershipSectionInfluence: string;
    leadershipSectionNext: string;
    leadershipLabelRelationalBias: string;
    leadershipLabelOperationalBias: string;
    leadershipLabelEmotionalRegulation: string;
    leadershipLabelCommunicationWeight: string;
    leadershipLabelTeamSignal: string;
    leadershipLabelInfluencePattern: string;
    leadershipLabelAlignmentTrend: string;
    leadershipLinkArena: string;
    leadershipLinkGrowth: string;
    leadershipCoreTrace: string;
    leadershipIdentityEyebrow: string;
    leadershipSystemNoteTitle: string;
    leadershipSystemNoteBodyDormant: string;
    leadershipSystemNoteBodyActive: string;
    leadershipAbbrevAir: string;
    /** AIR row footnote — separates execution integrity from Pattern Shift */
    leadershipAirExecutionFootnote: string;
    leadershipAbbrevTii: string;
    leadershipAbbrevRhythm: string;
    leadershipNextFocusHeading: string;
    leadershipDevelopmentCueHeading: string;
    leadershipSuggestedModuleLine: string;
    leadershipReflectionLayerEyebrow: string;
    leadershipReflectionDepthColumn: string;
    leadershipReflectionIntegrationColumn: string;
    leadershipRecoveryAwarenessColumn: string;
    leadershipReflectionDepthEarly: string;
    leadershipReflectionDepthActive: string;
    leadershipReflectionDepthDeepening: string;
    leadershipReflectionIntegrationForming: string;
    leadershipReflectionIntegrationImproving: string;
    leadershipRecoveryAwarenessStable: string;
    leadershipRecoveryAwarenessDetected: string;
    leadershipNextFocusWithReflectionRegulation: string;
    /** Premium identity console — field sections */
    leadershipFieldPatternTitle: string;
    leadershipPatternShiftSectionTitle: string;
    leadershipPatternShiftPlaceholder: string;
    leadershipFieldInfluenceTitle: string;
    leadershipReflectionPanelTitle: string;
    leadershipRowRecentFocus: string;
    leadershipRowIntegration: string;
    leadershipReflectionTraceEmpty: string;
    leadershipRecoveryTraceEmpty: string;
    leadershipIdentityCrestAria: string;
    leadershipRecoverySupportNote: string;
    leadershipRecoveryStableNote: string;
    leadershipSupportNoteLabel: string;
    leadershipRecoveryStatusLabel: string;
    leadershipSuggestedRouteHeading: string;
    leadershipSuggestedRouteDefault: string;
    leadershipSuggestedRouteRecovery: string;
  };
  /** My Page — open action contract hub (QR / secure link). */
  actionContract: {
    pendingTitle: string;
    pendingBody: string;
    completedTitle: string;
    completedBody: string;
    missedTitle: string;
    missedBody: string;
    btnQr: string;
    expiresIn: string;
    /** Shown when deadline is in the past (formatDeadline). */
    expiredShort: string;
    qrWitnessNotice: string;
    dismiss: string;
    awaitingListTitle: string;
    /** Pending Action list (/bty hub slice) — list title, empty/error, status badges. */
    pendingListTitle: string;
    pendingEmptyMessage: string;
    pendingErrorMessage: string;
    badgeQrRequired: string;
    badgeAwaitingVerification: string;
    badgeInProgress: string;
    tierPersonal: string;
    tierManager: string;
    scanVerified: string;
    scanAlready: string;
    scanFailed: string;
    scanValidationRequired: string;
    qrValidationRequired: string;
    goToValidationCta: string;
    witnessPromiseTitle: string;
    witnessConfirmQuestion: string;
    witnessConfirmCta: string;
    witnessConfirming: string;
    witnessConfirmSuccess: string;
    witnessLoadFailed: string;
    witnessSelfBlocked: string;
    actorCompletedTitle: string;
    actorReflectionPrompt: string;
    actorContinueCta: string;
  };
  legal: {
    accept: {
      title: string;
      placeholder_notice: string;
      section_heading: string;
      checkbox_label: string;
      submit_button: string;
      submitting: string;
      error_required: string;
      error_network: string;
      error_server: string;
      error_rate_limit: string;
      success_message: string;
    };
  };
};

const ko: Messages = {
  today: {
    dailyOs: {
      pulseHeading: "지금, 나의 관계들",
      doorHeading: "오늘, 하나를 선택하세요",
      mirrorHasEvidence: "어제, 하나가 현실로 넘어갔어요.",
      mirrorNoEvidence: "아직 조용합니다. 오늘 하나를 선택할 수 있어요.",
      openLoopTitle: "닫히지 않은 약속",
      openLoopBody: "아직 닫히지 않은 약속이 있습니다.",
      openLoopCta: "약속으로 돌아가기",
      centerFirstTitle: "잠깐, 나에게 먼저",
      centerFirstBody: "지금은 나에게 돌아갈 시간이에요.",
      centerFirstCta: "센터로 돌아가기",
      reexposureTitle: "다시 마주할 순간",
      reexposureBody: "지난 선택이 당신을 기다리고 있어요.",
      reexposureCta: "아레나로 들어가기",
      firstDayTitle: "오늘, 처음",
      firstDayBody: "BTY의 첫 날이에요. 하나만 선택해보세요.",
      doorCenterTitle: "나와의 관계",
      doorCenterSub: "나에게 돌아가기",
      doorArenaTitle: "이웃과의 관계",
      doorArenaSub: "관계 안으로 들어가기",
      doorFoundryTitle: "세상과의 관계",
      doorFoundrySub: "세상에 하나 심기",
      exitLine: "하나만 선택하고, 오늘로 돌아가세요.",
      loadingLabel: "오늘을 여는 중…",
      pulse: {
        self: {
          quiet: "나와의 관계는 아직 조용합니다.",
          living: "요즘 나에게 돌아오고 있어요.",
          connected: "나와 자주 마주하고 있어요.",
          deepening: "나와 깊어지는 중이에요.",
        },
        others: {
          quiet: "이웃과의 관계는 아직 조용합니다.",
          living: "이웃과 만나기 시작했어요.",
          connected: "이웃과 자주 마주하고 있어요.",
          deepening: "이웃과 깊어지는 중이에요.",
        },
        ground: {
          quiet: "세상과의 관계는 아직 조용합니다.",
          living: "세상에 무언가 심기 시작했어요.",
          connected: "세상에 자주 남기고 있어요.",
          deepening: "세상과 깊어지는 중이에요.",
        },
      },
      intel: {
        focusLabel: "오늘의 관계 초점",
        focus: {
          Self: {
            title: "나와의 관계",
            line: "오늘은 나 자신과의 관계를 먼저 세우는 날입니다.",
          },
          Others: {
            title: "사람과의 관계",
            line: "오늘은 사람과의 관계 안에서 하나의 행동을 닫는 날입니다.",
          },
          World: {
            title: "현실과의 관계",
            line: "오늘은 현실을 더 선명하게 다루는 날입니다.",
          },
          CleanStart: {
            title: "오늘, 새로",
            line: "오늘은 새로 열립니다. 아직 해석할 증거는 없습니다.",
          },
          ContinuePending: {
            title: "아직 열린 하나",
            line: "오늘은 새 과제가 아니라, 아직 열린 행동을 닫는 날입니다.",
          },
        },
        ctaSelf: "나에게 돌아가기",
        ctaOthers: "관계 안으로 들어가기",
        ctaWorld: "세상에 하나 심기",
      },
    },
    eyebrow: "BTY 데일리",
    title: "오늘 마주할 선택은 무엇인가요?",
    greeting: "좋은 아침입니다.",
    nowReserved: "예약됨",
    todayLabel: "오늘의 초점",
    promise: "오늘은 한 가지만 품으세요. 나머지는 기다려도 됩니다.",
    journeyPlaceholder: "당신의 길, 곧 공개됩니다.",
    pendingPlaceholder: "진행 중인 항목이 여기에 표시됩니다.",
    reflectionPlaceholder: "오늘 밤, 돌아보는 시간을 가져요.",
    beginButton: "오늘 시작하기",
    beginConfirmation: "오늘이 시작되었습니다. 그 순간이 오면 다시 돌아오세요.",
    profileTitle: "오늘의 나",
    coreXpLabel: "Core XP",
    levelStub: "레벨과 리더십 상태는 곧 공개됩니다",
    companionName: "Dr. Chi",
    companionLine: "오늘 한 걸음이면 충분합니다.",
    situationTitle: "오늘의 선택",
    situationEmpty: "지금은 대기 중인 행동이 없습니다.",
    beginCta: "시작하기",
    pointsTitle: "오늘의 포인트",
    pointsLabel: "획득한 XP",
    airTitle: "실행 신뢰도",
    airLabel: "AIR",
    airBandLow: "낮음",
    airBandMid: "중간",
    airBandHigh: "높음",
    airBandUnknown: "—",
    streakLabel: "{n}일 연속",
    stageLabel: "{n}단계 / 4 · {name}",
    greetingNamed: "좋은 아침입니다, {name}님.",
    greetingPlain: "좋은 아침입니다.",
    promiseLine: "오늘은 하나의 약속이 있습니다.",
    companionCardTitle: "오늘의 동행",
    choiceWaiting: "하나의 선택이 당신을 기다립니다.",
    behaviorTitle: "오늘의 행동",
    behaviorWaiting: "기다리는 중",
    growthTitle: "성장",
    orbHint: "꾹 눌러 시작",
  },
  // KO membership copy: EN fallback until the KO translation lane lands.
  // Convention: untranslated KO == EN verbatim; track remaining work via ko-vs-en git diff.
  membership: {
    form: {
      title: "Team membership",
      intro:
        "Submit your team membership request. An admin reviews and approves it before you can enter the Arena.",
      fullName: { label: "이름" },
      jobFunction: { label: "Role", staff: "Staff", leader: "Leader" },
      joinedAt: { label: "Join date" },
      leaderStartedAt: {
        label: "Leadership start date",
        hint: "Required when your role is Leader.",
      },
      submit: "Submit request",
      submitting: "Submitting…",
      errorGeneric: "Something went wrong. Please try again.",
      success: "Submitted. Awaiting admin approval.",
    },
    status: {
      pending: "Your membership request is pending admin approval.",
      approved: "Your team membership is approved.",
      approvedOn: "Approved on",
    },
    gate: {
      redirectCopy: "Membership required. Submit your team membership request to continue.",
    },
  },
  loading: {
    message: "잠시만 기다려 주세요.",
    hint: "첫 로딩은 1–2분 걸릴 수 있어요.",
    localeRouteSuspenseMainRegionAria: "앱 — 불러오는 중",
  },
  nav: { center: "Center", bty: "Foundry", arena: "Arena", en: "English", ko: "한국어", skipToMainContent: "본문으로 건너뛰기" },
  login: {
    title: "bty 로그인",
    afterLoginGoTo: "로그인 후",
    email: "이메일",
    password: "비밀번호",
    submit: "로그인",
    submitting: "로그인 중...",
    cookieNotice: "쿠키 기반 세션으로 동작합니다. 로그인 성공 직후 새로고침/이동이 발생할 수 있습니다.",
    errorDefault: "로그인에 실패했습니다.",
    forgotPassword: "비밀번호 찾기",
    forgotPasswordSent: "재설정 링크를 이메일로 보냈습니다. 받은편지함(또는 스팸)을 확인해주세요.",
    forgotPasswordError: "요청에 실패했습니다. 이메일을 확인하거나 잠시 후 다시 시도해주세요.",
    forgotPasswordMainRegionAria: "비밀번호 찾기",
    btyPublicRouteSuspenseMainRegionAria: "Foundry 로그인 — 불러오는 중",
  },
  auth: { backToLogin: "로그인으로 돌아가기", loading: "인증 처리 중...", callbackError: "인증 처리에 실패했습니다. 다시 시도해주세요." },
  logout: "로그아웃",
  arena: {
    weeklyTierBronze: "브론즈 (주간)",
    weeklyTierSilver: "실버 (주간)",
    weeklyTierGold: "골드 (주간)",
    weeklyTierPlatinum: "플래티넘 (주간)",
    runStateInProgress: "진행 중",
    runStateCompleted: "완료",
    runStateAborted: "중단",
    tieRankSuffix: "동순위",
    sessionChoiceToastExecutionIntegrity: "실행 무결성 신호가 갱신되었습니다.",
    sessionSummaryExecutionIntegrityHeading: "실행 무결성 (AIR)",
    sessionSummaryAirBandBefore: "이전 밴드",
    sessionSummaryAirBandAfter: "이후 밴드",
    sessionSummaryContextNoteHeading: "세션 노트",
    sessionSummaryPatternShiftHeading: "패턴 시프트 (행동 검증)",
    sessionSummaryPatternShiftPlaceholder:
      "행동 검증용 패턴 시프트 결과는 아직 연결되지 않았습니다. AIR과 별도 지표이며, 데이터가 준비되면 이 영역이 열립니다.",
  },
  reflectHints: {
    reflect_hint_empty: "한 문장이라도 적어 보면 다음 단계로 이어져요.",
    reflect_hint_short: "조금만 더 구체적으로 적어 볼까요?",
    reflect_hint_developing: "좋아요. 느낌이 드러나고 있어요.",
    reflect_hint_substantial: "충분히 담겼어요. 마음을 더 덧붙여도 좋아요.",
    reflect_hint_near_limit: "거의 한도에 가까워요. 마무리만 정리해 주세요.",
    reflect_hint_at_limit: "이 길이로 제출할 수 있어요.",
  },
  integrity: {
    title: "역지사지 시뮬레이터",
    subtitle: "Dr. Chi와 함께 갈등 상황을 돌려보세요.",
    stepLabelGuide: "2. 안내",
    guideMessage: "상대 입장을 잠시 돌려보는 연습이에요. 겪었던 갈등을 한 줄로 적으면, Dr. Chi가 역지사지 질문으로 비춰드려요.",
    startPractice: "연습 시작",
    intro: "겪었던 갈등을 한 줄로 적어보세요. Dr. Chi가 역지사지 질문으로 도와드릴게요.",
    placeholder: "예: 직원에게 말했는데 상대가 불쾌해했어요",
    send: "전송",
    thinking: "Dr. Chi가 생각 중…",
    emptyHint: "갈등 상황을 입력하고 전송해보세요.",
    emptyStateHint: "첫 메시지를 보내보세요.",
    backToFoundry: "Foundry로 돌아가기",
    reply: "만약 입장이 반대라면 어떨까요?",
    doneTitle: "오늘의 연습 완료",
    doneSub: "한 걸음씩 상대의 입장을 돌려보는 연습이 쌓이고 있어요.",
    doneCtaComplete: "연습 완료하기",
    doneCtaMentor: "멘토와 대화하기",
    doneCtaFoundry: "Foundry로",
    doneCtaAssessment: "50문항 진단",
    apiError: "연결이 불안정해요. 다시 시도해주세요.",
    errorTitle: "오류",
    replyFallback: "조금 더 말해주실래요?",
    ariaStartPractice: "연습 시작하기",
    ariaCompletePractice: "연습 마치기",
    ariaInputLabel: "갈등 상황을 입력하세요",
    ariaWaitingChi: "Dr. Chi 응답 대기 중…",
    mentorName: "Dr. Chi",
    conversationSimulatorAria: "역지사지 대화 내용",
    messageComposerAria: "메시지 입력·전송",
    integrityMainRegionAria: "역지사지 시뮬레이터 — 안내·대화·완료",
  },
  center: {
    loading: "잠시만 기다려 주세요.",
    mainAriaLabel: "나에게 쓰는 편지",
    headerLabel: "Dear Me 제목 및 안내",
    contentLabel: "Dear Me 본문",
    title: "Center",
    heroTitleMain: "센터,",
    heroTitleAccent: "듣고 있어요.",
    tagline: "아늑한 방에서 쉬어가요. 나는 안전해요.",
    ctaToFoundry: "문 열고 연습하러 가기",
    linkToBty: "어제보다 나은 연습하러 가기 (bty)",
    assessmentCta: "자존감 진단 (50문항)",
    assessmentCtaSub: "자기 존중감을 짧게 점검해 보세요.",
    assessmentDetailHint: "더 자세한 테스트를 원하시면 클릭하세요.",
    entryIntro: "말 못 할 마음을 비추는 안전한 공간이에요. 조언이 아니라 그대로 비춰드려요. 치유받는 방이에요.",
    startCta: "시작하기",
    todayStepTitle: "오늘의 나",
    todayMoodLabel: "지금 기분은 어때요?",
    todayEnergyLabel: "에너지 수준 (1=많이 지침, 5=괜찮음)",
    todayOneWordLabel: "한 단어로 오늘을 표현하면?",
    todayMoodPlaceholder: "예: 평온해요, 조금 불안해요",
    todayOneWordPlaceholder: "예: 고맙다, 지쳤다",
    todayNext: "다음",
    todaySkip: "건너뛰기",
    bridgeHeading: "이제 문을 열고 밖으로 나가볼까요?",
    bridgeSub: "(연습하러 가기)",
    letterStepTitle: "나에게 쓰는 편지",
    letterFormLabel: "편지 입력 및 보내기",
    letterPrompt: "지금 마음에 있는 말을 편하게 적어보세요. 여기선 조언이 아니라 그대로 비춰드릴게요.",
    letterPlaceholder: "오늘 누구에게도 말 못했던 마음이 있나요?",
    submitLetter: "보내기",
    sendingLetter: "글을 읽고 있어요…",
    replyStepTitle: "답장",
    replySummaryLabel: "답장 요약",
    completedTitle: "오늘의 편지 완료",
    completedSub: "마음을 나눠줘서 고마워요. 더 말하고 싶으면 챗에서 이어서 나눠 보세요.",
    continueToChat: "챗으로 이어하기",
    letterHistoryTitle: "보낸 편지 이력",
    letterHistoryListAria: "보낸 편지 날짜별 목록",
    letterHistoryEmpty: "아직 보낸 편지가 없어요.",
    letterHistoryError: "편지 이력을 불러올 수 없어요.",
    letterHistoryLoading: "편지 이력을 불러오는 중…",
    letterHistoryReplied: "답장 있음",
    letterHistoryNoReply: "답장 없음",
    calendarTitle: "편지 캘린더",
    calendarPrevMonth: "이전 달",
    calendarNextMonth: "다음 달",
    calendarWeekdays: ["일", "월", "화", "수", "목", "금", "토"],
    calendarMonths: ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"],
    calendarSelectedEmpty: "이 날엔 편지가 없어요.",
    dearMeComposerRegionAria: "Dear Me 편지 작성",
    dearMeComposerCharCount: "{n} / 최소 100 · 최대 1000",
    dearMeComposerSubmit: "편지 보내기",
    dearMeComposerSubmitting: "저장 중…",
    dearMeComposerSaved: "편지가 저장되었습니다",
    dearMeComposerLoadError: "불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
    dearMeComposerSubmitError: "저장하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    dearMeComposerPreviousTitle: "이전 편지",
    replyActionsLabel: "답장 후 액션",
    footerLabel: "Center로 돌아가기",
    dearMeSuspenseMainRegionAria: "Dear Me — 불러오는 중",
    dearMeErrorMainRegionAria: "Dear Me — 오류",
    centerSuspenseMainRegionAria: "Center — 불러오는 중",
    centerErrorMainRegionAria: "Center — 오류",
    currentStateTitle: "현재 상태",
    forcedResetRegionAria: "무결성 리셋 — 잠금·진단·복귀",
    forcedResetBannerTitle: "Arena는 무결성 리셋 기간 동안 잠겨 있어요.",
    forcedResetCountdownLive: "잠금 해제까지 남은 시간",
    forcedResetArenaNavLabel: "Arena로 이동",
    forcedResetArenaBlockedHint: "잠금 중에는 Arena로 이동할 수 없어요.",
    forcedResetChecklistTitle: "Center 진단 체크리스트",
    forcedResetItemStabilize: "호흡·신경계 안정 점검 완료",
    forcedResetItemBoundary: "경계·회복 계획 확인 완료",
    forcedResetItemAccountability: "책임·복귀 약속 정리 완료",
    forcedResetProgress: "{done} / {total} 완료",
    forcedResetReturnCta: "복귀 신청",
    forcedResetSubmitting: "처리 중…",
    forcedResetError: "복귀 신청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    forcedResetWindowElapsed: "48시간 창이 지났어요. 아래 진단을 완료한 뒤 복귀를 신청하세요.",
  },
  bty: {
    title: "bty",
    tagline: "어제보다 나은 연습. Integrity & Practice.",
    linkToCenter: "Center로 가기",
    entryIntro: "대시보드, 멘토, 역지사지 연습. 오늘 할 훈련을 고르세요.",
    startCta: "시작하기",
    arenaCta: "Arena 플레이",
    dashboardLabel: "Dashboard",
    leaderboardLabel: "Leaderboard",
    leStageLabel: "LE Stage",
    airLabel: "AIR",
    recommendationLabel: "추천",
    recommendationEmptyPlaceholder:
      "지금은 맞춤 추천이 없어요. Arena·Foundry·Center에서 활동하면 여기에 다음 행동이 안내돼요.",
    airUnavailable: "AIR 지표를 불러올 수 없습니다.",
    weeklyReflectionRegion: "주간 성찰 진행",
    personalRecordRegion: "이번 주 개인 기록",
    streakLabel: "연속 일수",
    pointsTodayLabel: "오늘 획득 XP",
    lifetimeProgressLabel: "평생 진행 (Core XP)",
    dojoPracticeLabel: "Dojo 연습",
    dojoPracticeLinksRegion: "Dojo 연습 링크",
    leaderboardWeekResetRegion: "이번 주 리셋 일시",
    dashboardTierCardLabel: "Arena 티어",
    dashboardTierCardSubline:
      "서버에서 계산한 티어입니다. Core XP로부터 UI에서 재계산하지 않습니다.",
    dashboardTierCardRegionAria: "Arena 티어 (API)",
    leAirSummarySectionAria: "리더십 엔진·AIR 요약 위젯",
    dashboardShortcutGoArena: "Arena 플레이",
    dashboardShortcutWeeklyRanking: "주간 랭킹 보기",
    leaderboardMainRegionAria: "리더보드 카드·순위 목록",
    leaderboardRouteSuspenseMainRegionAria: "리더보드 — 불러오는 중",
    leaderboardWidgetRegionAria: "주간 XP 상위 20명",
    leaderboardWidgetTitle: "주간 리더보드 (상위 20)",
    leaderboardWidgetResetCountdown: "주간 초기화까지",
    leaderboardWidgetLoading: "순위 불러오는 중…",
    leaderboardWidgetError: "리더보드를 불러오지 못했습니다.",
    leaderboardWidgetEmpty: "아직 순위 데이터가 없습니다.",
    leaderboardWidgetTop20Aria: "주간 XP 상위 20명 목록",
    avatarRendererRegionAria: "아바타 표시 티어",
    avatarRendererLoading: "아바타 불러오는 중…",
    avatarRendererError: "아바타 상태를 불러오지 못했습니다.",
    avatarRendererNextTierProgress: "다음 티어까지 (Core XP)",
    avatarRendererMaxTier: "최고 티어",
    avatarRendererUnlockedBadges: "해금 자산",
    avatarRendererCoreXpLabel: "Core XP",
    avatarRendererCoreXpSources: "Core XP 출처",
    avatarRendererCoreXpSegmentsAria: "획득 Core XP 대비 출처 비율",
    avatarRendererXpArena: "ARENA",
    avatarRendererXpLab: "LAB",
    avatarRendererXpFoundry: "FOUNDRY",
    avatarRendererXpMentor: "MENTOR",
    avatarRendererXpOther: "기타",
    profileRouteSuspenseMainRegionAria: "프로필 — 불러오는 중",
    dashboardHeroSubtitle: "Arena 진행을 한눈에 볼 수 있어요.",
    dashboardWeeklyRankWidgetTitle: "이번 주 랭킹",
    dashboardWeeklyWidgetLoading: "주간 순위 불러오는 중…",
    dashboardWeeklyWidgetAria: "이번 주 Arena 주간 XP·순위",
    dashboardWeeklyXpCaption: "이번 주 획득 XP",
    dashboardWeeklyTierCaption: "이번 주 경쟁 티어 (주간 XP 기준)",
    dashboardWeeklyCompetitionStageCaption: "주간 경쟁 스테이지 (밴드)",
    weeklyCompetitionStageBandBronze: "브론즈 밴드 — 주간 XP로 올라가는 첫 구간이에요.",
    weeklyCompetitionStageBandSilver: "실버 밴드 — 꾸준히 쌓이고 있어요.",
    weeklyCompetitionStageBandGold: "골드 밴드 — 상위권 경쟁 구간이에요.",
    weeklyCompetitionStageBandPlatinum: "플래티넘 밴드 — 이번 주 최상위 밴드예요.",
    leStageLeProgressBarAria: "리더십 엔진 현재 단계 진행률",
    dashboardWeeklyRankSeeLeaderboard: "전체 리더보드 보기",
    dashboardWeeklyRankHint: "실시간 순위는 리더보드에서 확인하세요.",
    dashboardOpenLiveLeaderboardCta: "라이브 순위 미리보기",
    dashboardLiveLeaderboardModalTitle: "이번 주 라이브 순위",
    dashboardLiveLeaderboardModalAria: "주간 XP 리더보드 미리보기",
    dashboardLiveLeaderboardLoading: "순위 불러오는 중…",
    dashboardLiveLeaderboardEmpty: "표시할 순위가 없어요.",
    dashboardLiveLeaderboardClose: "닫기",
    dashboardLiveLeaderboardFullPage: "전체 리더보드로",
    dashboardLiveLeaderboardFailed: "불러오지 못했어요.",
    dashboardLiveLeaderboardModalDesc:
      "이번 주 주간 XP 기준 순위입니다. 내 순위 주변 또는 상위 플레이어가 표시될 수 있어요.",
    dashboardLiveLeaderboardListAria: "주간 XP 순위 목록",
    dashboardLiveLeaderboardNotRankedBanner:
      "이번 주 아직 순위에 없어요. 아래는 참가 중인 플레이어 미리보기예요.",
    dashboardLiveLeaderboardNearMeFallbackBanner:
      "주변 순위 구간이 비어 있어 상위 순위를 표시합니다.",
    dashboardLiveLeaderboardCloseAria: "라이브 순위 미리보기 닫기",
    leStageSummaryWidgetDesc:
      "스테이지·리셋 기한·Arena 요약·행동 패턴은 서버 응답을 그대로 표시합니다.",
    profileCodeNameCardLabel: "코드명 · 서브 이름",
    profileIdentitySubline:
      "대시보드에 표시되는 이름이에요. 리더보드에 올라가려면 Arena에서 시나리오를 한 번 완료해 주세요.",
    profileRenamePlaceholder: "서브 이름 변경 (7자, 코드당 1회)",
    profileSaveSubName: "저장",
    profileSaving: "저장 중…",
    eliteMeContentUnlockedLabel: "Elite 전용 콘텐츠 해금 (me/elite)",
    eliteMeContentUnlockedYes: "예",
    eliteMeContentUnlockedNo: "아니오",
    eliteSpecWidgetRegionAria: "Elite — 인증·멘토 신청 큐",
    eliteSpecWidgetCertifiedBadge: "Certified Leader",
    eliteSpecWidgetCertifiedYes: "인증됨 (리더십 엔진 기준)",
    eliteSpecWidgetCertifiedNo: "미인증",
    eliteSpecWidgetLriHint: "LRI·Certified 판정은 서버·도메인 단일 기준입니다.",
    eliteSpecWidgetQueueTitle: "Dr. Chi 1:1 신청 큐",
    eliteSpecWidgetQueueForbidden: "멘토/관리자만 큐를 볼 수 있어요.",
    eliteSpecWidgetStatusPending: "대기",
    eliteSpecWidgetStatusApproved: "승인",
    eliteSpecWidgetStatusRejected: "거절",
    eliteSpecWidgetApprove: "승인",
    eliteSpecWidgetReject: "거절",
    eliteSpecWidgetLoading: "불러오는 중…",
    eliteSpecWidgetError: "불러오지 못했습니다.",
    leEngineTiiCertifiedBandAria: "팀 TII·AIR 요약·인증 상태",
    weeklySeasonActivityAria: "시즌·주간 XP 및 이벤트 수",
    dashboardPageTitle: "대시보드",
    dashboardMainRegionAria: "대시보드 — Arena 진행·주간 랭킹·추천",
    dashboardSuspenseMainRegionAria: "대시보드 — 불러오는 중",
    dojoSuspenseMainRegionAria: "Dojo — 불러오는 중",
    eliteSuspenseMainRegionAria: "Elite — 불러오는 중",
    healingSuspenseMainRegionAria: "Healing — 불러오는 중",
    mentorSuspenseMainRegionAria: "멘토 — 불러오는 중",
    integrityPracticeSuspenseMainRegionAria: "역지사지 연습 — 불러오는 중",
    foundryHubMainLandmarkAria: "Foundry 연습 허브",
    foundryHubSuspenseMainRegionAria: "Foundry 연습 허브 — 불러오는 중",
    btyIndexMainRegionAria: "bty 메인 — Arena·Center·Foundry 허브",
    foundryFeatureCardsRegionAria: "Dojo·역지사지·멘토·대시보드·Elite 연결",
    foundryBackToBtyHome: "← bty 메인",
    programRecommenderWidgetRegionAria: "추천 학습 프로그램",
    programRecommenderWidgetTitle: "추천 프로그램",
    programRecommenderWidgetLoading: "불러오는 중…",
    programRecommenderWidgetError: "추천을 불러오지 못했습니다.",
    programRecommenderWidgetEmpty: "아직 추천된 프로그램이 없습니다. Arena·힐링 활동 후 다시 확인해 보세요.",
    programRecommenderWidgetLocked: "잠금 — Arena에서 해금",
    foundryProgramDetailMainAria: "Foundry 프로그램 상세",
    indexThreeHubsExplainer: "Arena는 시나리오·랭킹, Center는 쉼·편지, Foundry는 대시보드·멘토·연습입니다.",
    indexHubEntriesRegionAria: "Arena·Center·Foundry 허브 선택",
    pointsTodaySubline: "오늘(UTC 기준) 획득한 XP입니다.",
    airIntegritySlip: "무결성 이탈 감지",
    leStageSummaryUnavailable: "스테이지 요약을 불러올 수 없습니다.",
    leAirAria7d: "AIR 7일",
    leAirAria14d: "AIR 14일",
    leAirAria90d: "AIR 90일",
    leAirAriaGrid: "AIR 지표: 7일·14일·90일",
    leAirLoading: "AIR 지표 불러오는 중…",
    leStageSummarySectionAria: "리더십 엔진 스테이지 요약",
    leStageStagePrefix: "단계",
    leStageResetDueLabel: "Reset 완료 기한",
    leStageArenaSummaryHeading: "Arena 결과 요약",
    leStageBehaviorHeading: "행동 패턴",
    leadershipEngineWidgetRegionAria: "리더십 엔진 — AIR·TII·LRI",
    leadershipEngineWidgetTitle: "Leadership Engine",
    leadershipEngineAir14Aria: "AIR 최근 14일",
    leadershipEngineTiiAria: "팀 무결성 지수 TII",
    leadershipEngineLriAria: "LRI 준비도",
    leadershipEngineReadinessReady: "준비됨",
    leadershipEngineReadinessNotReady: "준비 중",
    leadershipEngineCertifiedBadge: "Certified Leader — AIR 14일 80% 이상",
    leadershipEngineWidgetLoading: "리더십 지표 불러오는 중…",
    leadershipEngineWidgetError: "지표를 불러오지 못했습니다.",
    leadershipEngineAirTrendSparklineAria: "AIR 30일 추세",
    leadershipEnginePromotionBlockedBadge: "승진 보류",
    leadershipEngineCertifiedExpiresIn: "Certified 만료까지",
    leadershipEngineRenewalHistoryAria: "Certified 인증 갱신 시도 기록",
    leadershipEngineRenewalEmpty: "갱신 시도 기록이 없습니다.",
    leadershipEngineRenewalRowRenewed: "갱신됨",
    leadershipEngineRenewalRowNotRenewed: "갱신 안 됨",
    leadershipEngineElitePendingBadge: "Elite Spec 심사 대기",
    leadershipEngineEliteApprovedBadge: "Elite Spec 승인됨",
    leadershipEngineWeeklyStreak: "주간 연속",
    leadershipEngineWeeklyCompletion: "주간 완료율",
    leadershipEngineIntegrityRegionAria: "전체 무결성 등급",
    leadershipEngineIntegrityLabel: "무결성 종합",
    leadershipEngineEliteCta: "Elite 후보 등록",
    leadershipEngineEliteCtaBusy: "등록 중…",
    tiiWidgetRegionAria: "팀 무결성 지수 TII — 팀 건강도",
    tiiWidgetTitle: "팀 무결성 지수 (TII)",
    tiiWidgetHealthBarAria: "TII 팀 건강도 막대. 빨강 0–59, 노랑 60–79, 초록 80–100.",
    tiiWidgetBandRed: "주의 (0–59)",
    tiiWidgetBandYellow: "양호 (60–79)",
    tiiWidgetBandGreen: "건강 (80–100)",
    tiiWidgetMembersLabel: "점수에 반영된 팀원 수",
    tiiWidgetLastCalculated: "마지막 계산",
    tiiWidgetNoSnapshot: "아직 TII 스냅샷이 없습니다.",
    tiiWidgetLoading: "TII 불러오는 중…",
    tiiWidgetError: "TII를 불러오지 못했습니다.",
    tiiWidgetScorePct: "{pct}%",
    leStageLoading: "스테이지 요약 불러오는 중…",
    airTrendWidgetRegionAria: "AIR 30일 추세",
    airTrendWidgetTitle: "AIR 추세 (30일)",
    airTrendWidgetSevenDayLabel: "최근 7일 실행 무결성 밴드",
    airTrendWidgetSevenDayAria: "최근 7일 AIR 실행 무결성 밴드(저·중·고)",
    airTrendWidgetCertifiedExpiryPrefix: "Certified Leader 만료까지",
    airTrendWidgetWarningBanner: "AIR 롤링 추세가 연속 하락 중입니다. 활동 패턴을 점검해 보세요.",
    airTrendWidgetLoading: "AIR 추세 불러오는 중…",
    airTrendWidgetError: "AIR 추세를 불러오지 못했습니다.",
    airBandLow: "저밴드",
    airBandMid: "중밴드",
    airBandHigh: "고밴드",
    airExecutionIntegrityFootnote:
      "AIR는 실행 무결성(선택·완료·검증)만 나타냅니다. 패턴 시프트(행동 변화)와 합치지 않습니다.",
    weeklyReportCardRegionAria: "주간 AIR 리포트",
    weeklyReportCardAirTrendCaption: "실행 무결성 추세 (30일)",
    weeklyReportCardTitle: "주간 리포트",
    weeklyReportCardWeekLabel: "주간 (UTC 월요일 기준)",
    weeklyReportCardCompletionLabel: "시나리오 완료율",
    weeklyReportCardStreakLabel: "연속 플레이",
    weeklyReportCardStreakDays: "{n}일",
    weeklyReportCardFlagHero: "히어로 트랩",
    weeklyReportCardFlagIntegrity: "무결성 이탈",
    weeklyReportCardFlagClean: "클린·기타",
    weeklyReportCardFlagBarAria: "플래그 유형 비율",
    weeklyReportCardPhaseLabel: "힐링 단계 진행",
    weeklyReportCardResilienceLabel: "회복력 지수",
    weeklyReportCardIntegrityRegionAria: "전체 무결성 등급",
    weeklyReportCardIntegrityLabel: "전체 무결성 등급",
    weeklyReportCardIntegritySubline: "등급 {grade} · 종합 {score}점 (AIR·LRI·회복력 가중)",
    weeklyReportCardLoading: "주간 리포트 불러오는 중…",
    weeklyReportCardError: "주간 리포트를 불러오지 못했습니다.",
    weeklyReportCardNoReport: "아직 이번 주 리포트가 없습니다. 잠시 후 다시 확인해 주세요.",
    weeklyReportCardCtaStart: "센터 진단 시작",
    mentorChatShellRegionAria: "Dr. Chi 멘토 채팅",
    mentorChatShellTitle: "Dr. Chi",
    mentorChatShellPhaseBadge: "힐링 단계",
    mentorChatShellFlagLabel: "플래그",
    mentorChatShellRecsCta: "Foundry 프로그램 추천 보기",
    mentorChatShellPlaceholder: "메시지를 입력하세요…",
    mentorChatShellSend: "보내기",
    mentorChatShellLoading: "세션 준비 중…",
    mentorChatShellError: "채팅을 불러오지 못했습니다.",
    dojoAssessmentShellRegionAria: "Foundry Dojo 미니 역량 진단",
    dojoAssessmentShellLoading: "진단을 준비하는 중…",
    dojoAssessmentShellError: "진단을 불러오지 못했습니다.",
    dojoAssessmentShellProgress: "문항 {current} / {total}",
    dojoAssessmentShellNext: "다음",
    dojoAssessmentShellSubmit: "제출",
    dojoAssessmentShellPass: "합격",
    dojoAssessmentShellFail: "미합격",
    dojoAssessmentShellScoreLabel: "영역 점수",
    dojoAssessmentShellHintLabel: "개선 힌트",
    dojoAssessmentShellReturnToFoundry: "Foundry 프로그램으로 돌아가기",
    dojoAssessmentShellLikert1: "전혀 그렇지 않다",
    dojoAssessmentShellLikert2: "그렇지 않다",
    dojoAssessmentShellLikert3: "그렇다",
    dojoAssessmentShellLikert4: "매우 그렇다",
    programProgressShellRegionAria: "Foundry 프로그램 진행",
    programProgressShellLoading: "진행 정보를 불러오는 중…",
    programProgressShellError: "불러오지 못했습니다.",
    programProgressShellPhaseLabel: "단계",
    programProgressShellCompletionLabel: "완료율",
    programProgressShellModulesHeading: "모듈",
    programProgressShellModuleTap: "탭하여 완료",
    programProgressShellCompleteOverlay: "프로그램을 완료했습니다",
    programProgressShellArenaCta: "Arena로 돌아가기",
    programProgressShellDojoCta: "Dojo 평가 시작",
    awakeningMilestoneTrackerRegionAria: "어웨이크닝 마일스톤",
    awakeningMilestoneTrackerTitle: "RENEWAL 어웨이크닝 마일스톤",
    awakeningMilestoneTrackerNextLabel: "다음 목표",
    awakeningMilestoneTrackerRenewalUnlocked: "RENEWAL 배지 해금",
    awakeningMilestoneTrackerRenewalSub: "네 가지 마일스톤을 모두 완료했습니다.",
    awakeningMilestoneTrackerDismiss: "닫기",
    awakeningMilestoneTrackerChatCta: "Dr. Chi와 대화하기",
    awakeningMilestoneTrackerCollapseChat: "채팅 접기",
    awakeningMilestoneTrackerLoading: "마일스톤 불러오는 중…",
    awakeningMilestoneTrackerError: "마일스톤을 불러오지 못했습니다.",
  },
  assessmentResult: {
    mainContentRegionAria: "진단 결과 요약: 영역별 점수·권장 트랙",
    nextStepsCtaGroupAria: "진단 후 다음 단계",
    start28ProgramCta: "28일 프로그램 시작",
    retakeCta: "다시 검사하기",
    ctaSrOnlyHeading: "진단 후 다음 단계",
  },
  landing: {
    heroTitle: "어제보다 나은 나",
    heroSubtitle: "오늘 어디로 가볼까요?",
    recommended: "추천",
    arenaTitle: "Arena",
    arenaDesc: "시나리오를 플레이하면서 선택과 성장을 쌓아요. XP, 주간 퀘스트, 리더보드.",
    arenaCta: "플레이하기",
    foundryTitle: "Foundry",
    foundryDesc: "대시보드, 멘토, 역지사지 연습. 오늘 할 연습을 고르세요.",
    foundryCta: "Foundry 가기",
    centerTitle: "Center",
    centerDesc: "말 못 할 마음을 비추는 안전한 공간. 조언이 아니라 그대로 비춰드려요.",
    centerCta: "쉬러 가기",
    todayGrowthLink: "오늘의 성장",
    footerHint: "위에서 가고 싶은 곳을 골라주세요.",
    assessmentMainRegionAria: "자존감 50문항 자가 진단",
    assessmentErrorMainRegionAria: "자존감 진단 — 오류",
    assessmentSuspenseMainRegionAria: "자존감 진단 — 불러오는 중",
    landingHubMainRegionAria: "BTY 랜딩 — Arena·Foundry·Center 진입",
  },
  safeMirror: {
    title: "안전한 거울",
    subtitle: "지금 느끼는 말을 편하게 적어보세요. 여기선 조언이 아니라, 그 마음을 그대로 비춰드릴게요.",
    placeholder: "오늘 누구에게도 말 못했던 마음이 있나요?",
    submit: "적기",
    submitting: "글을 읽고 있어요…",
    fallbackReply: "지금은 답장을 적기 어려워요. 그 마음이 들었다는 것만으로도 충분해요.",
  },
  smallWins: {
    title: "작은 승리",
    subtitle: "거창한 게 아니라, 오늘 한 아주 작은 일을 눌러 기록해보세요.",
    add: "추가",
    customPlaceholder: "직접 입력 (예: 물 한 잔 마시기)",
    count: "오늘의 작은 승리",
    suggested: ["오늘 이불 개기", "물 한 잔 마시기", "창문 열어 환기하기", "손 씻기", "스트레칭 1분", "한 입 먹기", "일어나서 세수하기", "양치하기"],
  },
  selfEsteem: {
    title: "자존감 알아보기",
    subtitle: "나와 조금씩 대화해 보세요. 맞는 말에 가깝다고 느끼는 칸을 골라주시면 돼요. 정답은 없어요.",
    again: "다시 알아보기",
    choices: [
      { value: 1, label: "거의 그렇지 않다" },
      { value: 2, label: "그렇지 않은 편이다" },
      { value: 3, label: "보통이다" },
      { value: 4, label: "그런 편이다" },
      { value: 5, label: "매우 그렇다" },
    ],
    questions: [
      "나는 내가 해낸 일에 대해 만족하는 편이다.",
      "나는 대체로 나 자신에 대해 괜찮다고 느낀다.",
      "나는 내 단점도 받아들이려 노력한다.",
      "실패해도 나는 나 자신을 존중할 수 있다.",
      "나는 나에게 친절하게 말하려 노력한다.",
    ],
    storyLabels: ["첫 번째 이야기", "두 번째 이야기", "세 번째 이야기", "네 번째 이야기", "다섯 번째 이야기"],
    results: {
      high: "지금 이 순간에도 스스로를 괜찮다고 느끼려 한 걸음씩 내딛고 있어요. 그 마음이 여기까지 와 있다는 것만으로도 충분히 의미 있어요.",
      mid: "가끔은 나 자신이 불안하거나 부족하게 느껴질 수 있어요. 그런 날에도 여기선 그냥 그대로 있어도 괜찮아요.",
      low: "자신에 대해 엄하게 느껴지는 날이 있을 수 있어요. 그런 마음이 드는 건 당신이 부족해서가 아니라, 스스로를 소중히 여기고 싶어하기 때문이에요. 여기는 그 마음을 그대로 두어도 되는 곳이에요.",
      strengthHigh: "자기를 돌보고 스스로를 인정하는",
      strengthMid: "조금씩 나를 알아가고 있는",
      strengthLow: "지금도 스스로를 소중히 여기려 하는",
      scoreSuffix: "점",
    },
  },
  chat: {
    title: "말걸기",
    placeholder: "하고 싶은 말을 적어보세요",
    send: "보내기",
    thinking: "생각 중…",
    connectionError: "연결이 끊겼어요. 다시 시도해 주세요.",
    retry: "다시 시도",
    introFoundry: "Dr. Chi예요. 다른 사람 입장에서 생각해보는 연습을 도와드릴게요. 편하게 한마디부터 적어 주세요.",
    introCenter: "Dr. Chi예요. 지금 느낌 그대로 괜찮아요. 여기는 안전한 곳이에요. 하고 싶은 말이 있으면 적어 주세요.",
    spaceHintFoundry: "지금은 Foundry(연습 공간)예요. 시나리오·선택·역할 연습에 맞춰 대화해요.",
    spaceHintCenter: "지금은 Center(마음 쉼 공간)예요. 위로와 공감에 맞춰 편하게 나눠요.",
    introArena: "Dr. Chi예요. 시나리오 선택·트레이드오프·우선순위를 같이 정리해 볼게요. 하고 싶은 말 있으면 적어 주세요.",
    spaceHintArena: "지금은 Arena예요. 시뮬레이션·선택·성찰에 맞춰 대화해요.",
    exampleLabel: "예시로 시작하기",
    examplePhrasesFoundry: [
      "상대방 입장에서는 어떤 생각이 들까요?",
      "이 상황에서 제가 바꿀 수 있는 건 뭘까요?",
      "다음에 비슷할 때 시도해볼 말 한 가지가 있을까요?",
      "가치가 충돌할 때 우선순위를 어떻게 둘까요?",
    ],
    examplePhrasesCenter: [
      "요즘 가장 힘든 점이 뭔지 말해보고 싶어요.",
      "지금 기분을 한 단어로 하면요.",
      "오늘 하루 중 조금이라도 좋았던 순간이 있어요.",
      "그냥 들어주기만 해도 돼요.",
    ],
    examplePhrasesArena: [
      "이 판에서 하나만 가져가면 뭘 고를까요?",
      "선택한 걸 다음 시나리오에서도 써보고 싶어요.",
    ],
    rememberConversation: "대화 기억하기",
    deleteHistory: "기록 삭제",
  },
  resilience: {
    title: "회복 탄력성",
    subtitle: "떨어졌다가 다시 올라가는, 파도 같은 흐름이에요.",
    dailyTrajectorySubtitle: "매일의 5문항/활동에 따른 궤적이에요. 날짜별로 쌓인 회복의 흐름이에요.",
    emptyMessage: "아직 궤적이 없어요. Center에서 오늘의 나·편지 단계를 진행하면 쌓여요.",
    past: "과거",
    now: "지금",
    levelLow: "낮음 (에너지 1–2)",
    levelMid: "중간 (에너지 3)",
    levelHigh: "높음 (에너지 4–5)",
    levelLegendAria: "일별 회복 수준 범례",
  },
  arenaLevels: {
    membershipPending: "멤버십 승인 대기 중입니다. 승인 후 레벨이 표시됩니다.",
    noLevelsYet: "아직 기록이 없어요. 첫 시나리오를 시작해 보세요.",
    emptyCta: "Arena에서 시나리오 시작하기",
    loginRequired: "로그인이 필요합니다.",
    track: "트랙",
    unlockedUpTo: "최대 오픈 레벨",
    levelCardHint: "지금 여기까지 열렸어요.",
    l4AdminGranted: "관리자 부여",
    staff: "스태프",
    leader: "리더",
    l4Partner: "L4 (파트너)",
  },
  avatarOutfit: {
    label: "옷",
    professional: "직업군",
    fantasy: "롤플레잉",
    hint: "옷 카탈로그에서 선택합니다. 이후 Code·Tier 진행에 따라 해금 범위가 달라질 수 있어요.",
    pageTitle: "아바타",
    backToFoundry: "훈련장으로 돌아가기",
    backToFoundryAria: "훈련장으로 돌아가기",
    goToDashboard: "대시보드로 가기",
    goToDashboardAria: "대시보드로 가기",
    preview: "미리보기",
    previewAria: "아바타 미리보기",
    character: "캐릭터",
    characterLocked: "캐릭터 변경(잠김) — 다음 Code 진화 전까지 고정됩니다.",
    characterChangeHint: "캐릭터 변경은 대시보드에서 할 수 있어요.",
    outfit: "옷",
    noOutfits: "선택 가능한 옷이 없습니다.",
    save: "저장",
    saving: "저장 중",
    saveAria: "저장",
    errorLoad: "아바타 설정을 불러오지 못했습니다.",
    outfitLabels: {
      outfit_1_basic_clinic_scrubs: "기본 클리닉 스크럽",
      outfit_2_assistant_utility_uniform: "어시스턴트 유틸리티 유니폼",
      outfit_3_hygiene_scrubs: "위생 스크럽",
      outfit_4_standard_dental_coat: "표준 치과 가운",
      outfit_5_clinic_utility_uniform: "클리닉 유틸리티 유니폼",
      outfit_6_digital_dentistry_coat: "디지털 덴티스트리 코트",
      outfit_7_imaging_technician_suit: "영상기사 슈트",
      outfit_8_surgical_scrubs: "수술 스크럽",
      outfit_9_orthodontic_specialist_coat: "교정 전문 코트",
      outfit_10_pediatric_dentistry_uniform: "소아치과 유니폼",
      outfit_11_implant_surgery_armor_scrubs: "임플란트 수술 아머 스크럽",
      outfit_12_ai_diagnostic_suit: "AI 진단 슈트",
      outfit_13_research_lab_coat: "연구실 코트",
      outfit_14_master_hygienist_uniform: "마스터 위생사 유니폼",
      outfit_15_prosthetic_design_coat: "보철 디자인 코트",
      outfit_16_neurosight_clinical_suit: "뉴로사이트 클리닉 슈트",
      outfit_17_bty_arena_champion_uniform: "BTY 아레나 챔피언 유니폼",
      outfit_18_foundry_engineer_outfit: "파운드리 엔지니어 의상",
      outfit_19_master_surgeon_robe: "마스터 서전 로브",
      outfit_20_digital_dentistry_elite_coat: "디지털 덴티스트리 엘리트 코트",
      professional_outfit_scrub_general: "일반 스크럽",
      professional_outfit_figs_scrub: "Figs 스크럽",
      professional_outfit_doctor_gown: "의사 가운",
      professional_outfit_surgery_coat_suit: "수술 코트 + 정장",
      professional_outfit_brand_suit: "브랜드 정장",
      professional_outfit_figs_scrub_short: "반팔 Figs 스크럽",
      professional_outfit_shorts_tee: "반바지, 반팔티",
      fantasy_outfit_apprentice: "Apprentice",
      fantasy_outfit_adventurer: "Adventurer",
      fantasy_outfit_journeyer: "Journeyer",
      fantasy_outfit_warrior_mage_mid: "Warrior/Mage/Merchant mid",
      fantasy_outfit_senior: "Senior",
      fantasy_outfit_senior_plus: "Senior+",
      fantasy_outfit_master: "Master",
    },
    currentLevelAccessories: "현재 레벨 악세사리",
    currentOutfitPrefix: "현재:",
    pickCharacterTitle: "캐릭터 선택",
    dashboardCharacterLockedGridHint:
      "캐릭터는 이미 저장되어 변경할 수 없습니다. 아래는 전체 캐릭터 목록입니다.",
    avatarSettingsMainRegionAria: "아바타 설정 — 캐릭터·옷·악세사리",
  },
  arenaMembership: {
    label: "Arena 가입",
    approved: "승인됨",
    pending: "승인 대기 중입니다. Admin 승인 후 레벨이 표시됩니다.",
    hint: "직군·입사일·리더시작일을 입력하면 Admin 승인 후 Arena 레벨이 열립니다.",
    jobFunction: "직군",
    joinedAt: "입사일",
    leaderStartedAt: "리더시작일 (선택, 리더 직군인 경우)",
    submit: "제출",
    submitting: "제출 중…",
    submitError: "제출에 실패했습니다.",
    submitSuccess: "승인 대기 중입니다. Admin 승인 후 레벨이 열립니다.",
    seniorDoctorHint: "Senior Doctor는 보통 3년 이상 경력 시 선택합니다.",
    validationRequired: "직군과 입사일을 입력해 주세요.",
  },
  mentorRequest: {
    cardTitle: "Dr. Chi 1:1 대화 신청",
    cardDesc: "Elite(상위 5%)만 신청할 수 있어요. 승인 후 멘토 페이지에서 심화 대화를 이어갈 수 있습니다.",
    requestCta: "대화 신청하기",
    submitting: "신청 중…",
    statusPending: "승인 대기 중입니다.",
    statusApproved: "승인되었습니다. 멘토 페이지에서 대화를 이어가세요.",
    statusRejected: "이번에는 승인되지 않았어요. 다음 기회에 다시 신청해 주세요.",
    messageLabel: "하고 싶은 말 (선택)",
    messagePlaceholder: "예: 리더십 피드백을 받고 싶어요",
    errorEliteOnly: "상위 5%에 있을 때만 신청할 수 있어요.",
    errorPendingExists: "이미 대기 중인 신청이 있어요.",
    errorSubmit: "신청에 실패했어요. 다시 시도해 주세요.",
    approvedCta: "멘토 페이지로 이동",
    listTitle: "내 신청 목록",
    listEmpty: "아직 신청 내역이 없습니다.",
    colDate: "신청 일시",
    colStatus: "상태",
    slaImminentBadge: "멘토 응답 기한 임박 — 곧 안내가 올 수 있어요.",
    eliteDomainTerminalApproved: "멘토 신청 · 승인됨",
    eliteDomainTerminalRejected: "멘토 신청 · 거절됨",
    eliteDomainPendingStale: "신청 후 2주 이상 경과했어요. 응답을 기다리는 중입니다.",
    eliteStatusBadgePending: "멘토 신청 · 대기 중",
    eliteStatusBadgeApproved: "멘토 신청 · 승인",
    eliteStatusBadgeRejected: "멘토 신청 · 거절",
  },
  elitePage: {
    circleCardTitle: "서클(Circle) 모임",
    circleCardDesc: "Elite 전용 소규모 모임(주 1회 또는 월 1회). 일정·참여는 아래 안내를 확인해 주세요.",
    circleCardPlaceholder: "준비 중",
    badgeLabels: { weekly_elite: "이번 주 상위 5%" },
    unlockConditionTitle: "해금 조건",
    unlockConditionMet: "주간 리더보드 상위 5% 달성(충족).",
    unlockConditionLocked: "주간 리더보드 상위 5% 달성 시 이용 가능.",
    unlockExposureTitle: "노출",
    unlockExposureMet: "Elite 전용 콘텐츠(멘토 대화 신청, 서클 모임, 배지 등)를 이용할 수 있습니다.",
    unlockExposureLocked: "상위 5% 달성 시 Elite 전용 페이지·콘텐츠 이용 가능.",
    mentorDeepLinksListAria: "Elite 멘토·심화 대화 링크",
    loadingElitePageAria: "Elite 페이지 불러오는 중",
    elitePageMainRegionAria: "Elite 전용 — 해금 조건·배지·멘토 신청",
    pageTitle: "Elite 전용",
    pageIntroElite:
      "주간 리더보드 상위 5%에 진입하셨습니다. 여기서는 Elite 전용 콘텐츠를 이용할 수 있습니다.",
    badgesSectionTitle: "증정 배지",
    tableColActions: "동작",
  },
  leadershipMetricsAdmin: {
    networkError: "네트워크 오류",
    title: "리더십 지표",
    computedPrefix: "계산:",
    refresh: "새로고침",
    loading: "데이터 로드 중…",
    emptyAir: "Action Contract 데이터가 없습니다.",
    emptyStage: "Leadership Engine State 데이터가 없습니다.",
    emptyMwd: "Micro Win 활성화 데이터가 없습니다.",
    emptyTii: "TII 스냅샷 데이터가 없습니다.",
    emptyTeamWeekly: "team_weekly_metrics 스냅샷 데이터가 없습니다.",
    colEmail: "이메일",
    colStatus: "상태",
    colDoneSelected: "완료 / 선택",
    colIncomplete: "미완료",
    colLastActivity: "마지막 활동",
    colStageEntry: "Stage 진입",
    colDaysElapsed: "경과일",
    colMwd7: "MWD 7일",
    colMwd14: "MWD 14일",
    colDoneTotal: "완료 / 전체",
    colCompletionRate: "완료율",
    colLeagueTeam: "리그 / 팀",
    colMembers: "구성원",
    colBaseWeek: "기준 주",
    resetBadge: "리셋",
    daysSuffix: "일",
    airBandHigh: "High",
    airBandMid: "Mid",
    airBandLow: "Low",
    glAir: "= 완료된 행동 ÷ 선택된 행동 (draft 제외)",
    glDoneCriteriaLabel: "완료 기준",
    glDoneCriteria: ": approved / completed / DONE, 또는 verified_at 있음",
    glIntegritySlip: ": 3회 연속 미완료 발생 횟수",
    glTsp: ": TSP (주간 설문 점수)",
    glMwd: ": 최소 1개의 Micro Win 완료가 있는 고유 날짜 수",
    glMwdWindow: ": 최근 7일 / 14일 기준",
    glStageCertifiedLabel: "Certified Leader",
    colCertified: "인증",
    colCertifiedYes: "인증",
    colCertifiedNo: "미달",
    glStageCertifiedBody:
      ": 4개 조건 충족 시 인증 — 14일 AIR ≥ 80%, MWD 기준 이상, 강제 리셋 준수, 14일 내 integrity_slip 없음. 분기·주간 재평가로 갱신되며 조건 미충족 시 자동 해제(별도 박탈 절차 없이 재계산). Arena Elite(주간 상위 5%)와는 별개 개념.",
    glStageLriLabel: "LRI",
    glStageLriBody:
      ": 비(非)리더 준비도 측정. (14일 AIR × 50%) + (MWD × 30%) + (pulse × 20%). LRI ≥ 0.80 + integrity_slip 없음 → readiness. (팀 지표 TII 60/25/15와 다름.)",
    glStageResetLabel: "Forced Reset",
    glStageResetBody:
      ": Stage 4(무결성 리셋) 강제 트리거 — 다음 중 2개 이상 충족 시: 14일 내 Stage 3 2회 선택 · AIR_7d 2주 연속 80% 미만 · 7일간 QR 인증 없음 · TSP 2주 연속 하락. 최대 48시간 지연 가능(영구 무시 불가), 완료 시 Stage 1 복귀.",
  },
  mentorRequestAdmin: {
    mainRegionAria: "멘토 신청 승인 — 관리",
    title: "멘토 대화 신청 승인",
    description: "Elite 멘토(1:1) 신청 큐. 승인/거절 처리합니다.",
    empty: "대기 중인 신청이 없습니다.",
    loading: "목록 불러오는 중…",
    actions: "동작",
    userId: "유저 ID",
    createdAt: "신청 일시",
    message: "메시지",
    colStatus: "상태",
    tableCaption: "Elite 멘토 신청 대기 목록",
    queueTableAria: "멘토 신청 대기 큐 테이블",
    approveRejectGroupAria: "이 신청 승인 또는 거절",
    statusPendingLabel: "대기",
    approve: "승인",
    reject: "거절",
    approving: "승인 중…",
    rejecting: "거절 중…",
    error: "처리 실패",
    errorLoadQueue: "목록을 불러오지 못했습니다.",
    errorPatch: "승인·거절 처리에 실패했습니다.",
    debugLink: "디버깅",
    usersLink: "사용자 관리",
    arenaMembershipLink: "Arena 멤버십 승인",
  },
  adminUsers: {
    mainRegionAria: "사용자 계정 — 관리",
    networkError: "네트워크 오류",
    errLoadList: "사용자 목록을 불러올 수 없습니다.",
    errCreateFailed: "사용자 생성에 실패했습니다.",
    errDeleteFailed: "사용자 삭제에 실패했습니다.",
    errPasswordFailed: "비밀번호 변경에 실패했습니다.",
    validateCreate: "이메일과 비밀번호(6자 이상)를 입력해주세요.",
    validatePassword: "비밀번호는 6자 이상이어야 합니다.",
    deleteConfirm: "정말로 {email} 사용자를 삭제하시겠습니까? 계정과 관련 데이터가 모두 삭제되며 되돌릴 수 없습니다.",
    passwordChanged: "비밀번호가 변경되었습니다.",
    title: "사용자 관리",
    description: "일반 사용자 계정을 생성, 수정, 삭제할 수 있습니다.",
    cancel: "취소",
    createUser: "새 사용자 생성",
    createFormTitle: "새 사용자 생성",
    labelEmail: "이메일",
    labelPassword: "비밀번호",
    passwordPlaceholder: "6자 이상",
    creating: "생성 중…",
    create: "생성",
    emptyUsers: "등록된 사용자가 없습니다.",
    colCreatedAt: "생성일",
    colActions: "작업",
    newPasswordPlaceholder: "새 비밀번호",
    save: "저장",
    changePassword: "비밀번호 변경",
    delete: "삭제",
    noteTitle: "⚠️ 참고사항",
    note1: "사용자 관리는 Supabase Auth를 사용합니다.",
    note2: "목록·생성·삭제·비밀번호 변경은 Supabase 대시보드에서도 가능합니다.",
  },
  adminQuality: {
    mainRegionAria: "품질 이벤트 — 관리",
    networkError: "네트워크 오류",
    subtitle: "Quality Events 대시보드",
    refresh: "새로고침",
    loading: "품질 데이터 로드 중…",
    dbOk: "정상",
    dbDown: "연결 안됨",
    lastPrefix: "마지막:",
    events30dPrefix: "30일 이벤트:",
    summaryTitle: "이벤트 요약",
    windowPrefix: "기간:",
    totalEventsPrefix: "총 이벤트:",
    avgCssPrefix: "평균 CSS:",
    emptyEvents: "이벤트 데이터가 없습니다. bty-ai-core 백엔드 연동 후 표시됩니다.",
    topSignaturesTitle: "주요 이슈 시그니처",
    breakdownRoute: "라우트별 분포",
    breakdownRole: "역할별 분포",
    breakdownIntent: "의도별 분포",
  },
  adminOrganizations: {
    mainRegionAria: "조직 목록 — 관리",
  },
  adminArenaMembership: {
    mainRegionAria: "Arena 멤버십 승인 — 관리",
    title: "Arena 멤버십 승인",
    description:
      "pending 요청을 검토한 뒤 승인합니다. 승인 시 해당 유저의 tenure(입사일·리더시작일)가 반영됩니다.",
    emptyNoPending: "대기 중인 요청이 없습니다.",
    colJobFunction: "직군",
    colJoinedAt: "입사일",
    colLeaderStartedAt: "리더시작일",
    colRequestedAt: "요청일",
    colActions: "동작",
    approving: "처리 중…",
    approve: "승인",
    approveAria: "멤버십 승인",
  },
  adminLogin: {
    mainRegionAria: "관리자 로그인",
    heading: "Admin 로그인",
    emailPlaceholder: "이메일",
    passwordPlaceholder: "비밀번호",
    loginSubmit: "로그인",
    signingIn: "로그인 중…",
    loginSubmitAriaBusy: "로그인 처리 중",
    loginSubmitAriaIdle: "관리자 로그인 제출",
    loginFailed: "로그인에 실패했습니다.",
  },
  adminHub: {
    mainRegionAria: "관리자 영역 — 이동 중",
    redirecting: "관리 화면으로 이동 중…",
  },
  mentorPage: {
    deleteAllHistoryConfirm: "저장된 멘토 대화 기록을 모두 삭제할까요?",
    pageMainLandmarkAria: "Dr. Chi 멘토 대화",
    topicChoiceGroupAria: "대화 주제 선택",
  },
  dentalRpg: {
    empty: "장비가 없습니다.",
    listAria: "장비 목록",
    rarity1: "일반",
    rarity2: "고급",
    rarity3: "희귀",
    rarity4: "에픽",
    rarity5: "전설",
  },
  healing: {
    title: "Healing",
    intro: "Healing과 Awakening 경로의 진입점입니다. Awakening에서 2차 각성 리듀얼을 진행할 수 있어요.",
    awakeningCta: "Awakening →",
    dashboardCta: "← 대시보드",
    navLabel: "Healing 메뉴",
    ariaAwakening: "Awakening으로",
    ariaDashboard: "대시보드로",
    loading: "불러오는 중…",
    emptyPhase: "진도 단계 정보를 불러올 수 없습니다. Awakening으로 이동해 보세요.",
    loadError: "진도 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
    awakeningLoadingLabel: "2차 각성 불러오는 중",
    awakeningMainRegionAriaError: "2차 각성 오류",
    awakeningMainRegionAriaCompleted: "2차 각성 완료",
    awakeningMainRegionAriaEligibility: "2차 각성 해금 조건",
    awakeningMainRegionAriaRitual: "2차 각성 의식",
    phaseProgressRegionAria: "Healing 단계·진행 상태",
    awakeningRitualActsRegionAria: "2차 각성 의식 1~3막",
    healingBottomNavSectionAria: "각성·대시보드 이동",
    mainLandmarkAria: "Healing 허브",
    stubApiSectionTitle: "서버 진행 상태 (표시 전용)",
    phaseFieldLabel: "단계",
    ringTypeFieldLabel: "링 유형",
    stubRenderOnlyNote: "표시값은 API 응답이며, UI에서 단계를 계산하지 않습니다.",
    awakeningActsTrackTitle: "Awakening 액트 (서버 기록)",
    awakeningActsTrackRegionAria: "Awakening 액트 목록 및 완료 기록",
    awakeningActsTriggerLine: "여정을 더 쌓으면 의식이 해금돼요.",
    awakeningActDone: "기록됨",
    awakeningActOpen: "미기록",
    awakeningRecordNextCta: "다음 액트 완료 기록",
    awakeningAllActsRecorded: "세 액트 기록이 모두 완료되었어요.",
    awakeningProgressToastOk: "기록이 저장되었습니다.",
    awakeningProgressToast409: "이미 기록된 액트예요.",
    awakeningProgressToast400: "순서에 맞지 않거나 잘못된 요청이에요.",
    awakeningProgressToastNetwork: "저장에 실패했어요. 다시 시도해 주세요.",
    awakeningActsLoadError: "액트 정보를 불러오지 못했어요.",
    healingAwakeningProgressHeading: "Awakening 진행 (서버)",
    healingAwakeningProgressPct: "진행률 {n}%",
    healingProgressbarValuetext: "진행률 {pct}퍼센트. {detail}",
    healingProgressbarDetailAllDone: "모든 Act를 완료했습니다.",
    healingProgressbarDetailNext: "다음 막: {name}.",
    healingProgressbarDetailSync: "다음 막은 서버와 동기화되면 표시됩니다.",
    healingAwakeningAllActsDone: "세 액트를 모두 기록했습니다.",
    healingNextActFromApi: "다음 액트(서버): {name}",
    healingNextActSyncHint:
      "기록 순서가 어긋난 경우 아래에서 순서대로 다시 기록해 주세요.",
    healingProgressRefreshHint:
      "기록 후 목록이 바로 안 바뀌면 아래를 눌러 서버 상태를 다시 불러오세요.",
    healingProgressRefreshCta: "진행 상태 새로고침",
    awakeningActsGridAria: "Awakening 액트 1~3 그리드",
    awakeningActNumberLabel: "액트 {n}",
    healingAwakeningBlockedOrderRequired: "먼저 이전 액트를 순서대로 완료해 주세요.",
    healingAwakeningBlockedAlreadyComplete: "이미 기록된 액트입니다.",
    healingActLockPrerequisite: "먼저 이전 액트를 순서대로 완료해 주세요.",
    healingActLockAlreadyComplete: "이미 기록된 액트입니다.",
    pathProgressBlockedCooldown: "잠시 후 다시 시도해 주세요. (쿨다운)",
    pathProgressBlockedPhase: "이전 단계를 먼저 완료한 뒤 진행할 수 있어요.",
    healingActsOverallProgressAria: "Awakening 액트 기록 진행률",
    healingActsOverallProgressCaption: "기록된 액트",
    healingActsOverallProgressValuetext: "{done}개 중 {total}개 기록됨, 진행률 {pct}퍼센트",
    healingPhaseTrackerRegionAria: "힐링 여정 — 네 단계 진행",
    healingPhaseTrackerTitle: "치유 여정 단계",
    healingPhaseStep1: "인정",
    healingPhaseStep2: "성찰",
    healingPhaseStep3: "재통합",
    healingPhaseStep4: "갱신",
    healingPhasePrompt1:
      "자기 자존감 진단을 완료해 지금의 나를 인정하는 출발점을 세우세요. (Center 50문항 1회 이상)",
    healingPhasePrompt2:
      "Dear Me 편지로 감정과 생각을 말로 옮겨 성찰을 깊게 하세요. (진단 1회 + 편지 1통 이상)",
    healingPhasePrompt3:
      "Healing 허브에서 '다음 액트 기록' 버튼을 눌러 첫 번째 성찰 의식(Reflection Chamber)을 완료하세요.",
    healingPhasePrompt4:
      "Healing 허브에서 나머지 두 액트(Transition · Awakening)를 순서대로 기록해 여정을 마무리하세요.",
    healingPhaseAdvanceCta: "다음 단계로 진행",
    healingPhaseAdvancing: "진행 중…",
    healingPhaseTrackerLoadError: "단계 정보를 불러오지 못했어요.",
    healingPhaseAdvanceError: "다음 단계로 넘어가지 못했어요. 조건을 확인해 주세요.",
    healingPhaseInputsHint:
      "요약: 진단 {a}회 · 편지 {l}통 · 액트 기록 {aw}개 (개인 점수는 표시하지 않습니다)",
  },
  train: {
    title: "28일 훈련",
    completeGroupLabel: "완료 처리 및 코치·요약 전환",
    completionSummaryLabel: "완료 요약",
    coachChatLabel: "코치 대화",
    reinforcementLabel: "보강 질문",
    dayListLabel: "일자 목록",
    lessonLabel: "오늘의 레슨",
    lockedLabel: "잠긴 레슨 안내",
    sidebarPanelLabel: "완료 요약 및 코치 대화",
    journeyStartTitle: "28일 훈련 시작",
    journeyStartIntro: "왼쪽에서 Day를 선택하면 레슨이 보이고, 오른쪽에서 코치와 대화할 수 있어요.",
    journeyStartDay1Link: "Day 1부터 시작하기",
    journeyStartDay1Aria: "Day 1부터 시작하기",
    journeyStartMainRegionAria: "28일 훈련 시작",
    track28HubMainRegionAria: "28일 훈련 허브",
    track28DayMainRegionAria: "28일 훈련 레슨",
    trainRouteSuspenseMainRegionAria: "28일 훈련 — 불러오는 중",
  },
  dojoResult: {
    title: "Today-Me 50문항 결과",
    areaScoresTitle: "영역별 점수",
    drChiCommentTitle: "Dr. Chi 코멘트",
    resultCommentHigh: "역지사지·소통·리더십 영역에서 잘 발휘하고 있어요. 강점을 유지하면서 조금씩 확장해 보세요.",
    resultCommentMid: "몇몇 영역은 이미 잘하고 있고, 몇몇은 더 연습할 여지가 있어요. 한 번에 하나씩 골라서 연습해 보세요.",
    resultCommentLow: "지금이 출발선이에요. Foundry 역지사지 연습과 50문항 진단을 반복하면 단계적으로 성장할 수 있어요.",
    loading: "결과를 불러오는 중…",
    noAnswers: "결과를 보려면 50문항을 먼저 완료해 주세요.",
    backToAssessment: "진단하러 가기",
    apiError: "결과를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
    dimensionLabels: {
      core: "자기 가치",
      compassion: "자기 연민",
      stability: "안정성",
      growth: "성장",
      social: "관계",
      perspective_taking: "역지사지",
      communication: "소통·경청",
      leadership: "리더십·책임",
      conflict: "갈등·협상",
      teamwork: "팀·협업",
    },
    nextActionsLabel: "다음 액션",
    resultActionsLabel: "결과 후 액션",
    questionStepSectionAria: "현재 문항 및 응답 선택",
    resultScoresInsightRegionAria: "영역별 점수 및 코멘트",
    dojoPageMainAria: "Dojo 역량 진단 50문항",
    dojoHistoryMainRegionAria: "Dojo 과거 진단 이력 — 제출일·요약 목록",
    dojoResultMainRegionAria: "Dojo 역량 진단 결과 — 요약·영역 점수·다음 액션",
  },
  arenaRun: {
    reflectionPrompt: "한 문장으로: 이 판에서 가져갈 것은?",
    errorStartRun: "런을 시작할 수 없습니다.",
    errorSignIn: "로그인이 필요합니다.",
    errorAlreadySubmitted: "이미 제출되었거나 세션이 만료되었습니다.",
    errorSubmitFailed: "제출에 실패했습니다. 다시 시도해 주세요.",
    heroTitle: "오늘도 한 걸음, Arena에서.",
    otherLabel: "기타 (직접 입력)",
    otherRecorded: "기타 기록됨.",
    nextScenario: "다음 시나리오",
    otherPlaceholder: "직접 입력해 주세요...",
    cancel: "취소",
    submit: "제출",
    liveRanking: "실시간 순위",
    completeErrorPrefix: "저장 실패: ",
    completeErrorSuffix: ". 리더보드에 반영되지 않았을 수 있어요.",
    headerTitle: "시뮬레이션",
    headerSubtitle: "한 판으로 끝. 멈춰도 이어짐. (MVP: 1 + 보완 1)",
    pauseLabel: "일시정지",
    resetLabel: "초기화",
    mainLabel: "메인",
    headerIdentityLinkAria: "프로필·아바타로 이동",
    startSimulation: "시뮬레이션 시작",
    confirm: "확인",
    reflectionTitle: "성찰",
    reflectionNext: "다음",
    reflectionOptional: "옵션",
    reflectionPlaceholder: "예: 다음엔 반응하기 전에 한 번 멈춰 보겠다.",
    systemOutput: "시스템 출력",
    skipFollowUp: "따라하기 건너뛰기 · 다음 시나리오",
    followUpSelected: "선택한 따라하기",
    step6Title: "Step 6 · 정리",
    arenaRunPageMainRegionAria: "Arena 런 — 시나리오 허브·선택·성찰·이력",
    preparingNewScenarioAria: "새 시나리오 준비 중",
    scenarioProgressPanelAria: "시나리오 진행·완료",
    reflectionBusyAria: "반영 제출 중",
    reflectionSentenceBlockAria: "한 문장 성찰 입력",
    reflectionChoiceOptionsAria: "성찰 선택지",
    reflectionLengthHintAria: "성찰 글 길이 안내",
    reflectionCharCount: "{n} / {max}자",
    youChose: "선택한 항목",
    keyInsight: "핵심 통찰:",
    principle: "원칙: \"사람을 먼저 안정시키고, 그다음 시스템을 고칩니다.\"",
    completeBtn: "완료",
    reflectionBonusLabel: "성찰 보너스",
    deepeningTitle: "성찰 심화",
    nextActionLabel: "다음 행동",
    step7Title: "Step 7 · 완료",
    step7Body: "저장되었습니다. 아래 버튼으로 다음 시나리오로 이동합니다.",
    choiceLabel: "선택",
    scenarioCompletedToast: "선택이 기록되었습니다.",
    scenarioNotFound: "시나리오를 찾을 수 없습니다.",
    scenarioNotFoundHint: "다른 시나리오를 선택하거나 새로고침해 보세요.",
    pastScenariosHeading: "지난 시나리오",
    pastScenariosCollapse: "접기",
    pastScenariosExpand: "펼치기",
    loadingHistory: "이력 불러오는 중…",
    couldNotLoadHistory: "이력을 불러오지 못했어요.",
    pastScenariosSessionHint:
      "이 기기에서 세션이 잠시 끊긴 것 같아요. bty 메인에서 다시 로그인하거나 페이지를 새로고침하면 이력이 보일 수 있어요.",
    retryLoadHistory: "이력 다시 불러오기",
    retry: "다시 시도",
    noCompletedScenarios: "아직 완료한 시나리오가 없어요.",
    startFirstScenario: "첫 시나리오를 시작해 보세요!",
    completed: "완료",
    inProgress: "진행 중",
    reflectDeepeningUnavailable:
      "심화 해석은 잠시 불러오지 못했어요. 성찰은 저장되었습니다.",
    runPageLevelCheckMainRegionAria: "미션 실행 — 레벨·세션 확인 중",
    runPageBeginnerPathMainRegionAria: "입문 경로로 이동 중",
    runPageNoScenarioMainRegionAria: "미션 실행 — 시나리오 없음",
    arenaRunErrorMainRegionAria: "Arena 런 — 오류",
    arenaRunErrorTitle: "문제가 발생했습니다",
    arenaRunErrorDescription:
      "Arena 런을 불러오지 못했어요. 연결을 확인한 뒤 다시 시도해 주세요.",
    arenaRunErrorHint: "계속되면 페이지를 새로고침해 보세요.",
    eliteSetupRoleLabel: "역할",
    eliteSetupPressureLabel: "압박 상황",
    eliteSetupTradeoffLabel: "딜레마",
    eliteInitialDecisionHint:
      "아래에서 옵션 하나를 고르면 실행이 이어집니다.",
    elitePrimaryPickHint: "옵션 하나를 눌러 첫 결정을 기록합니다.",
    eliteOpeningNarrativeLabel: "상황",
    eliteRunCompletePrepareHint:
      "계속하면 서버와 상태를 맞춥니다. 다음 단계에서 액션 확인이 필요할 수 있으며, 바로 다음 시나리오가 아닐 수 있습니다.",
    elitePostFlowRegionAria: "엘리트 아레나 — 이후 단계",
    elitePostConfirmTitle: "첫 선택이 기록되었습니다",
    elitePostConfirmLead: "이번 판의 첫 선택이 저장되었습니다. 준비되면 계속하세요.",
    elitePostConfirmNext: "계속",
    elitePostExplainTitle: "왜 이 선택인가요?",
    elitePostExplainPrompt: "지금 상황에서 이 선택이 맞다고 느껴지는 이유를 짧게 적어 보세요. (선택)",
    elitePostExplainOptionalLabel: "한두 문장이면 충분해요",
    elitePostExplainPlaceholder: "예: 팀 신뢰가 먼저라서, 솔직히 말하는 쪽이 맞다고 느꼈어요.",
    elitePostExplainSubmit: "다음 단계로",
    elitePostIntegrationTitle: "리더십으로 옮기기",
    elitePostIntegrationNext: "다음",
    elitePostBridgeTitle: "앞으로",
    elitePostBridgeDefault: "다음 대화·회의·결정 한 번에, 방금 정리한 한 가지를 의도적으로 시도해 보세요.",
    elitePostBridgeNext: "마무리로",
    elitePostCompleteTitle: "이번 판 완료",
    elitePostCompleteBody: "기록을 마쳤습니다. 다음 시나리오로 넘어가며 리듬을 이어 가세요.",
    elitePostXpLine: "이번 판 경험치: +{xp} XP",
    eliteMirrorTitle: "패턴 미러",
    eliteMirrorLead:
      "아래는 이번 실행의 패턴 exit 기록과 누적 패턴 창 상태입니다. 해석·평가 없이 기록만 표시합니다.",
    eliteMirrorEmpty: "표시할 이전 실행 데이터가 없습니다.",
    eliteMirrorContinue: "계속",
    eliteMirrorLoadError: "미러 데이터를 불러오지 못했습니다.",
    eliteSituationUpdateHeader: "상황 전개",
    eliteDecisionRequiredHeader: "결정 필요",
    eliteRunStepAdvanceError: "이 단계를 기록하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    eliteBindingIntegrityError:
      "시나리오 바인딩 정보가 맞지 않습니다. 페이지를 새로고침하거나 아레나를 다시 시작해 주세요.",
    eliteForcedTradeoffCostLabel: "비용",
    eliteTradeoffProtectsLabel: "이 선택이 끌고 가는 방향",
    eliteTradeoffRisksLabel: "이 선택이 감수하는 리스크",
    arenaFlowPhasePrimaryInstruction: "먼저, 압박 속에서 어떻게 반응할지 고릅니다.",
    arenaFlowPhaseTradeoffInstruction: "이제 그 반응의 대가를 직면합니다. 어느 쪽도 공짜가 아닙니다.",
    arenaFlowPhaseActionDecisionInstruction: "이제 관찰 가능한 행동을 고릅니다. 해석이 아닙니다.",
    eliteActionDecisionAdCommitmentBadge: "관찰 가능한 실행",
    eliteActionDecisionAdDeferBadge: "유예·비약정",
    eliteActionDecisionAd1TraitConcrete: "구체적인 행동(느낌이나 해석만이 아님)",
    eliteActionDecisionAd1TraitTimeBound: "시간이 정해진 실행(언젠가가 아님)",
    eliteActionDecisionAd1TraitObservable: "다른 사람이 볼 수 있는 변화",
    eliteActionDecisionAd1TraitContractGate: "완료해야 진행이 풀립니다 — 계약 단계로 이어집니다",
    eliteActionDecisionAd2TraitDelay: "오늘 움직임을 고정하지 않고 미룹니다",
    eliteActionDecisionAd2TraitNonCommitment: "해석을 더 열어 둡니다",
    eliteActionDecisionAd2TraitNextOrReexposure: "계약 없이 진행할 수 있으며, 다음 시나리오로 가거나 무변화 재노출 리스크가 생길 수 있습니다",
    eliteActionDecisionTitle: "실제 행동 결정",
    eliteActionDecisionLead: "해석이 아니라, 다음 업무 구간에서 실제로 할 일을 고릅니다.",
    eliteActionDecisionCommitmentLabel: "선택의 의미",
    eliteActionDecisionRegionAria: "실제 행동 결정",
    eliteV2ErrorNotConfiguredTitle: "에스컬레이션 데이터 없음",
    eliteV2ErrorNotConfiguredBody:
      "이 시나리오에 escalationBranches가 없습니다. 관리자에게 문의하거나 다른 시나리오를 선택하세요.",
    eliteV2ErrorMissingBranchTitle: "선택에 맞는 에스컬레이션 분기 없음",
    eliteV2ErrorMissingBranchBody:
      "escalationBranches에 현재 1차 선택 id에 해당하는 분기가 없습니다. 데이터를 수정해야 합니다.",
    eliteV2ErrorInvalidCostTitle: "2차 선택 비용 데이터 오류",
    eliteV2ErrorInvalidCostBody:
      "second_choices 항목에 비어 있지 않은 cost가 필요합니다. 시나리오 데이터를 확인하세요.",
    eliteInvalidPrimaryChoiceTitle: "1차 선택 상태가 시나리오와 맞지 않음",
    eliteInvalidPrimaryChoiceBody:
      "저장된 선택 ID가 이 시나리오의 선택 목록에 없습니다. 페이지를 새로고침하거나 아레나를 재시작해 주세요.",
    eliteRunCompleteLine: "이번 실행을 마쳤습니다.",
    eliteRunCompleteRegionAria: "실행 완료",
    eliteRunCompleteLoading: "불러오는 중…",
    eliteRunCompleteContinueCta: "계속",
    arenaPendingContractTitle: "진행 중인 액션 계약",
    arenaObservableActionConfirmationLead:
      "관찰 가능한 실행을 선택했습니다. 이제 진행은 이행 완료에 달려 있습니다.",
    arenaPendingContractLead:
      "마이페이지에서 진행 중인 액션 계약을 마무리하기 전에는 다음 시나리오로 넘어갈 수 없습니다. 아래 내용을 확인한 뒤 마이페이지에서 처리해 주세요.",
    arenaPendingContractActionLabel: "액션",
    arenaPendingContractDeadlineLabel: "기한",
    arenaPendingContractVerificationLabel: "검증 방식",
    arenaPendingContractRetry: "다시 확인",
    arenaPendingContractMyPageCta: "마이페이지에서 계속",
    arenaPendingContractRegionAria: "액션 계약 대기",
    arenaActionCompletedTitle: "행동 보고됨",
    arenaActionCompletedLead: "XP가 반영되었습니다.",
    arenaActionCompletedNextCta: "다음 시나리오로",
    arenaPulseQuestion: "이번 상황에서 나는 책임을 회피하지 않고 감당하려고 했는가?",
    arenaPulseLow: "전혀 아니다",
    arenaPulseHigh: "매우 그렇다",
    arenaPulseSkipCta: "나중에",
    arenaPulseThanks: "기록되었습니다.",
    arenaSnapshotForcedResetPlaceholder: "계정 상태를 동기화하는 중입니다. 잠시 후 다시 시도해 주세요.",
    arenaForcedResetGateTitle: "센터에서 리셋 단계가 필요합니다",
    arenaForcedResetGateLead:
      "아레나는 잠시 멈춥니다. 센터에서 안내에 따라 진행한 뒤 다시 돌아오세요.",
    arenaForcedResetGoCenterCta: "센터로 이동",
    arenaSnapshotReexposurePlaceholder: "다음 단계를 준비하는 중입니다. 잠시만 기다려 주세요.",
    arenaReexposurePanelTitle: "재노출 라운드",
    arenaReexposurePanelDescription:
      "이전 선택과 연결된 지연 결과가 도착했습니다. 시나리오로 들어가 계속 진행하세요.",
    arenaReexposurePanelBeginButton: "시나리오로 들어가기",
    arenaReexposureBlockedNextTitleV2: "다음 시나리오 전 패턴 검증이 필요합니다",
    arenaReexposureBlockedNextDescriptionV2:
      "재노출은 처벌 단계가 아니라, 같은 축에서 패턴 안정성을 확인하는 검증 단계입니다.",
    arenaReexposureBlockedNextButtonV2: "다음 시나리오 불러오기",
    arenaReexposureInternalStatusInterventionSensitivityUp:
      "패턴 검증 민감도가 조정되었습니다. 같은 축에서 다음 검증을 이어갑니다.",
    arenaReexposureValidationChanged: "패턴 검증 결과: changed",
    arenaReexposureValidationUnstable: "패턴 검증 결과: unstable",
    arenaReexposureValidationNoChange: "패턴 검증 결과: no_change",
    arenaReexposureStaleRecoveryMessage:
      "재노출 상태가 만료되었습니다. 다음 시나리오를 다시 불러오는 중입니다.",
    arenaSnapshotPlaySurfaceBlockedHint: "세션 상태를 서버와 맞추는 중입니다. 새로고침하거나 잠시 후 다시 시도해 주세요.",
    arenaPlaySurfaceBlockedTitle: "플레이 일시 중지",
    arenaAligningLoaderTitle: "서버와 세션을 맞추는 중",
    arenaAligningLoaderLead: "잠시만 기다려 주세요. 오류가 아니라 동기화 단계입니다.",
    arenaGateLabelContract: "액션 계약",
    arenaGateLabelReexposure: "재노출",
    arenaGateLabelReset: "리셋",
    arenaGateLabelNext: "다음 시나리오",
    arenaGateLabelPlay: "플레이",
    arenaGateLabelAligning: "동기화",
    arenaSnapshotNextScenarioReadyTitle: "다음 시나리오 준비됨",
    arenaSnapshotNextScenarioReadyHint: "세션을 불러오는 중입니다. 잠시만 기다려 주세요.",
    arenaPostContractNextScenarioTitle: "실행이 기록되었습니다. 다음 시나리오로 계속하세요.",
    arenaPostContractNextScenarioHint: "모바일과 동일하게, 제출된 계약은 더 이상 진행을 막지 않습니다.",
    arenaContinueToNextScenarioCta: "계속",
    eliteContractTitle: "액션 계약",
    eliteContractIntro:
      "네 가지는 실행 가능한 약속을 고정합니다. 가깝고 구체적일수록 좋습니다. 아래 안내는 형식만 돕고, 사람을 평가하지 않습니다.",
    eliteContractWhoLabel: "이 패턴의 영향을 받는 구체적인 한 사람은 누구인가요?",
    eliteContractWhoHint: "실제 이름을 적어 주세요.",
    eliteContractWhatLabel: "그 사람과 피해 오던 대화 한 가지는 무엇인가요?",
    eliteContractWhatHint: "사람을 붙이는 말보다 상황을 적어 주세요.",
    eliteContractHowLabel: "첫 문장은 무엇인가요?",
    eliteContractHowInstruction: "말할 내용을 그대로 적으세요. 설명은 넣지 마세요.",
    eliteContractHowResponsibilityLead: "솔직함이나 내 책임에서 시작하세요. 비난으로 시작하지 마세요.",
    eliteContractWhenLabel: "그 대화를 언제 시작할까요?",
    eliteContractWhenHint: "가깝고 실제적인 시간을 고르세요.",
    eliteContractWhoPlaceholder: "예: 박지민",
    eliteContractWhatPlaceholder: "예: 지난주에 미뤄 둔 일정 조정 안건",
    eliteContractHowPlaceholder: "예: \"지난주 일정 얘기, 오늘 잠깐 맞추고 싶어요.\"",
    eliteContractWhenPlaceholder: "예: 오늘 퇴근 후, 또는 목요일 오전",
    eliteContractSubmit: "계약 제출",
    eliteContractSubmitting: "제출 중…",
    eliteContractLoadError: "제출 처리에 실패했습니다.",
    eliteContractEnsureFailed: "계약 초기화를 완료하지 못했습니다. 잠시 후 다시 시도하세요.",
    eliteContractCreating: "실행 계약을 준비하는 중…",
    eliteContractInitFailedTitle: "실행 계약을 시작할 수 없습니다",
    eliteContractPatternThresholdTitle: "실행 계약은 아직 열리지 않았습니다",
    eliteContractPatternThresholdBody:
      "같은 패턴 패밀리에 대한 qualifying exit가 아직 충분히 쌓이지 않았습니다. 이건 오류가 아니라 기록 규칙입니다. 실행 게이트로 계속 진행할 수 있습니다.",
    eliteContractPatternThresholdContinue: "실행 게이트로 계속",
    eliteContractRevisionRequired: "수정이 필요합니다. 아래 필드를 갱신한 뒤 다시 제출하세요.",
    actionContractFollowupTitle: "실행 후속 확인",
    actionContractFollowupLead: "계약과 연결된 짧은 확인입니다. 판단이나 조언이 아닙니다.",
    actionContractFollowupQuestion: "대화를 시작했나요?",
    actionContractFollowupYes: "예",
    actionContractFollowupNo: "아니요",
    actionContractFollowupFirst30Label: "처음 30초에 무슨 일이 있었나요?",
    actionContractFollowupFirst30Placeholder: "있는 그대로 짧게 적어 주세요.",
    actionContractFollowupSubmit: "보내기",
    actionContractFollowupSubmitting: "보내는 중…",
    actionContractFollowupError: "보내기에 실패했습니다. 잠시 후 다시 시도하세요.",
    eliteExecutionGateTitle: "실행 게이트",
    eliteExecutionGateIntro:
      "검증은 절차에 따라 기록됩니다. 허용되는 증빙 유형과 제출 위치는 제품 정책을 따릅니다. 아래 문구는 제출한 그대로입니다.",
    eliteExecutionContractHeading: "제출한 계약 텍스트",
    eliteExecutionVerificationRecorded: "검증이 기록되었습니다.",
    eliteExecutionAwaitingVerification: "검증 결과 대기 중입니다.",
    eliteExecutionVerificationNotRecorded: "파일에 검증이 없습니다.",
    eliteExecutionUnderReview: "검토 중입니다.",
    eliteExecutionAdditionalConfirmation: "추가 확인이 필요합니다.",
    eliteExecutionAbandonedLead: "실행 검증이 완료되지 않았습니다.",
    eliteExecutionResumeHint:
      "이 실행을 마치려면 증빙 제출·확인 절차를 이어가세요. 계약 문구는 아래와 동일하게 유지됩니다.",
    eliteExecutionRawTextFallback: "(계약 텍스트 없음)",
    eliteExecutionNextBlocked: "다음 시나리오로 이동하려면 검증이 기록되어야 합니다.",
    eliteExecutionLoadError: "실행 상태를 불러오지 못했습니다.",
    eliteExecutionPatternThresholdDeferLine:
      "실행 계약 초안은 패턴 창 기준을 충족하면 열립니다. 지금은 제출된 계약 텍스트 없이 이 실행을 마무리할 수 있습니다.",
  },
  emotionalStats: {
    emptyMessage: "아직 기록이 없어요. Arena나 챗에서 대화를 진행해 보세요.",
    sectionTitle: "오늘의 성장",
    errorLoad: "표시를 불러오지 못했어요.",
  },
  profile: {
    title: "프로필",
    displayNameLabel: "표시 이름",
    displayNamePlaceholder: "닉네임 또는 표시할 이름 (선택)",
    save: "저장",
    saving: "저장 중…",
    backToDashboard: "대시보드로",
    errorLoad: "프로필을 불러오지 못했어요.",
    errorSave: "저장에 실패했어요.",
    errorTooLong: "표시 이름은 64자 이하여야 해요.",
    avatarSettingsLink: "아바타 설정",
    saveButtonAriaLabel: "표시 이름 저장",
    profileMainRegionAria: "프로필 · 표시 이름 및 아바타 설정",
  },
  uxPhase1Stub: {
    bottomNavArena: "btyARENA",
    bottomNavGrowth: "Growth",
    bottomNavMyPage: "My Page",
    growthTitle: "성장",
    growthSubtitle: "해석 · 회복",
    growthLead: "내면 상태를 다시 세웁니다.",
    growthReturnArena: "Arena 허브로",
    growthWeeklyRankingLink: "주간 랭킹",
    growthFoundryLink: "Foundry 대시보드",
    growthOptionsRegionAria: "성장 영역 바로가기 목록",
    wireframeLandmarkAria: "Arena 와이어프레임 — Phase 1 스텁 화면",
    wireframeCtaRegionAria: "시나리오 런 진입 — 이어하기 또는 새로 시작",
    wireframeBottomNavAria: "BTY 앱 하단 탭 — Arena·성장·마이",
    weeklySnapshotRegionAria: "주간 순위 요약 예시",
    wireframeScreenTitle: "bty arena",
    wireframeScreenSubtitle: "와이어 · Phase 1",
    wireframeSystemReady: "시스템 준비 완료.",
    wireframePlayGame: "플레이",
    wireframeStartScenario: "새 시나리오 시작",
    wireframeContinue: "이어하기",
    wireframeResumeLast: "마지막 시뮬 이어하기",
    wireframeOr: "또는",
    wireframeWeeklyRankLine: "주간 순위: #128",
    wireframeSeasonLine: "시즌 종료까지 12일",
    wireframeWireRuleNote:
      "예시 수치 · AIR·TII·LRI 등은 본 화면에 노출하지 않음 (와이어 규칙).",
    wireframeResultStubPrefix: "시뮬 결과 화면 스텁:",
    resultTitle: "시뮬레이션 완료",
    resultSubtitle: "Arena · 결과",
    resultCoreXpSample: "+25 코어 XP",
    resultWeeklyXpSample: "+15 주간 XP",
    resultSampleNote: "예시 보상 · 실데이터는 API 연동 후",
    resultSystemTitle: "시스템 메시지",
    resultSystemBody: "의사결정 일관성이 개선된 것으로 감지되었습니다.",
    resultContinueCta: "계속",
    resultReturnArenaCta: "Arena로 돌아가기",
    resultWireHubLink: "Arena 와이어 허브",
    growthArenaPlayLink: "Arena 플레이",
    growthArenaWireHubLink: "Arena 허브(와이어)",
    growthShortcutsNavAria: "랭킹·대시보드·Arena·Healing 바로가기",
    growthElitePageLink: "Elite (상위 5%)",
    wireframeFoundryEliteAria: "Foundry·Elite 바로가기",
    growthHealingLink: "Healing",
    growthAwakeningLink: "Awakening",
    resultRunCompleteHint:
      "런 완료 후 「다음 시나리오」를 눌러야 주간 XP·리더보드에 반영됩니다.",
    resultShareRegionAria: "완료 요약 공유",
    resultShareCta: "완료 한 줄 복사",
    resultShareCopied: "복사했어요.",
    resultShareFailed: "복사하지 못했어요.",
    resultShareClipboardLine: "bty arena에서 시나리오 런을 완료했습니다.",
    resultNextActionsRegionAria: "런 완료 후 다음 행동",
    resultNextScenarioSectionLabel: "권장 다음 행동",
    resultNextScenarioCta: "다음 시나리오",
    resultNextScenarioSub: "플레이를 이어가면 주간 XP·리더보드에 반영됩니다.",
    wireframeArenaPracticeNavAria: "Dojo·역지사지 연습 진입",
    wireframeDojoLink: "Dojo 50",
    wireframeIntegrityLink: "역지사지",
    wireframeRunsDashboardHint: "최근 런 목록은 Foundry 대시보드에서 확인할 수 있어요.",
    growthMyRankCardTitle: "이번 주 내 순위",
    growthMyRankRegionAria: "주간 리더보드 내 순위·주간 XP",
    growthMyRankLoading: "순위 불러오는 중…",
    growthMyRankYourRank: "내 순위: #{rank}",
    growthMyRankWeeklyXpLine: "주간 XP: {xp}",
    growthMyRankNoRank: "이번 주 리더보드에 아직 없어요.",
    growthMyRankWeekNotParticipated:
      "이번 주 주간 XP가 없어 순위에 올라가지 않았어요. Arena에서 플레이하면 반영돼요.",
    growthMyRankAnonymous: "로그인 후 내 순위가 표시됩니다.",
    growthMyRankSeeLeaderboard: "전체 리더보드",
    comebackTitle: "다시 만나서 반가워요",
    comebackBody:
      "잠시 쉬어간 것으로 보여요. 부담 없이, 지금 이어가던 회복 루프를 계속할 수 있어요.",
    comebackResumeJourneyCta: "훈련 이어가기",
    comebackNotNowCta: "나중에",
    growthNavDojoTitle: "Dojo",
    growthNavDojoLine: "지금 상태를 가늠하는 50문항",
    growthNavIntegrityTitle: "Integrity",
    growthNavIntegrityLine: "상대 시선에서 상황 보기",
    growthNavGuidanceTitle: "Guidance",
    growthNavGuidanceLine: "결정 패턴 되돌아보기",
    growthNavReflectionTitle: "Reflection",
    growthNavReflectionLine: "내면 목소리 안정화 (Dear Me)",
    growthBackToGrowth: "성장으로 돌아가기",
    growthJourneyLandmarkAria: "28일 Journey 보드",
    growthRouteSuspenseMainRegionAria: "오늘의 성장 — 불러오는 중",
    arenaHubTitle: "아레나를 이어갑니다.",
    arenaHubSubtitle: "판단·반복·압박 훈련은 이 공간에 있습니다.",
    arenaHubReadyTitle: "시스템 준비",
    arenaHubReadyBody: "마지막 시뮬을 이어가거나 새 시나리오를 시작할 수 있어요.",
    arenaHubCardTitleContinue: "아레나를 이어갑니다.",
    arenaHubCardBodyResume: "진행 중이던 시뮬을 이어갈 수 있어요.",
    arenaHubCardBodyNew: "새 시나리오를 시작할 수 있어요.",
    arenaHubCtaWeeklyRank: "주간 순위",
    arenaHubEntryLoading: "불러오는 중…",
    arenaHubContinueCta: "이어하기",
    arenaHubPlayCta: "플레이",
    arenaHubWeeklyRankLabel: "주간 순위",
    arenaHubSeasonEndsLabel: "시즌 종료까지",
    arenaHubSummaryRegionAria: "아레나 허브 — 주간 순위·시즌",
    arenaHubSummaryLoadError: "통계를 불러오지 못했습니다.",
    arenaHubMainRegionAria: "아레나 허브 — 진입 카드·주간 순위·시즌",
    arenaMissionLobbyMainRegionAria: "아레나 미션 로비 — 시나리오 진입·이어하기",
    arenaMissionLobbyLoadingMainRegionAria: "아레나 미션 로비 — 불러오는 중",
    lobbyLandmarkAria: "아레나 로비 — 판단 주기·런 진입",
    lobbyLoadingMainRegionAria: "아레나 로비 — 불러오는 중",
    lobbyCtaRegionAria: "런 진입 — 아레나 입장·시나리오 이어하기",
    lobbyTopBarAria: "아레나 미션 상단 — 단계·진행률",
    arenaBtyArenaRouteSegmentLoadingMainRegionAria: "BTY Arena — 페이지 불러오는 중",
    arenaMissionPlayLoadingMainRegionAria: "미션 플레이 — 불러오는 중",
    arenaMissionPlayMainRegionAria: "미션 플레이 — 실행·선택·성찰·이력",
    arenaMissionPlayTopBarAria: "미션 플레이 상단 — 단계·진행·코드네임",
    arenaMissionPlaySceneRegionAria: "미션 플레이 — 상황 장면",
    arenaMissionPlayDecisionsRegionAria: "미션 플레이 — 시나리오·선택·의도 확정·결과 확정",
    arenaMissionResultLoadingMainRegionAria: "미션 결과 — 불러오는 중",
    arenaMissionResultMainRegionAria: "미션 결과 — 결정 기록·해석·다음 행동",
    delayedOutcomeBannerRegionAria: "7일 지연 결과",
    delayedOutcomeIntro: "7일 전 당신의 선택이 결과를 만들었습니다.",
    delayedOutcomeDismiss: "닫기",
    delayedOutcomeFlagHeroTrap: "히어로 트랩",
    delayedOutcomeFlagIntegritySlip: "무결성 이탈",
    delayedOutcomeFlagClean: "클린",
    delayedOutcomeFlagRoleMirror: "역할 거울",
    arenaResolveSessionLoadingMainRegionAria: "세션 복원 — 불러오는 중",
    arenaResolveSessionMainRegionAria: "세션 복원 — 새 시나리오·선택·성찰",
    arenaLabMainRegionAria: "리더십 Lab — 일일 시도·남은 횟수",
    arenaLabUsageRegionAria: "리더십 Lab — 오늘 시도·남은 할당",
    arenaLabCtaRegionAria: "리더십 Lab — 다음 행동 — Arena 런으로",
    arenaLabBackNavAria: "리더십 Lab — Arena 런 선택으로 이동",
    arenaLabTitle: "Leadership Lab",
    arenaLabLead: "오늘 하루 3회까지 시나리오를 완료하면 Core XP를 얻을 수 있습니다.",
    arenaLabRemainingTodayLabel: "오늘 남은 횟수",
    arenaLabRemainingSuffix: "회 남음",
    arenaLabUsedPrefix: "사용한 횟수: ",
    arenaLabBackToArena: "← Arena로 돌아가기",
    arenaLabUsageLoadError: "불러오지 못했습니다.",
    arenaLabScoreCardRegionAria: "리더십 Lab — 난이도·시도별 점수",
    arenaLabScoreTitle: "Lab 점수",
    arenaLabScoreSlashMax: "{score} / {max}점",
    arenaLabScoreDifficultyPrefix: "난이도",
    arenaLabScoreAttemptPrefix: "시도",
    arenaLabScoreDifficultyEasy: "쉬움",
    arenaLabScoreDifficultyMid: "중간",
    arenaLabScoreDifficultyHard: "어려움",
    arenaLabScoreDifficultyExtreme: "극한",
    arenaLabEmptyRegionAria: "리더십 Lab — 활성 시나리오 없음",
    arenaLabEmptyTitle: "진행할 시나리오가 없어요",
    arenaLabEmptyMessage: "Arena에서 시나리오를 선택한 뒤 Lab에서 이어갈 수 있어요.",
    arenaLabEmptyHint: "Arena 런 화면으로 돌아가 시나리오를 시작해 보세요.",
    arenaBeginnerPathInitMainRegionAria: "입문 경로 — 시나리오 준비 중",
    arenaBeginnerPathMainRegionAria: "입문 경로 — 상황·감정·결정·성찰",
    arenaBeginnerPathCompleteMainRegionAria: "입문 경로 — 결과 요약",
    arenaBeginnerPathTrackLabel: "입문",
    arenaBeginnerPathSubmittingAria: "완료 처리 중",
    recordLandmarkAria: "아레나 기록 화면 — 시뮬 요약·XP·다음 행동",
    recordCtaRegionAria: "다음 행동 — 플레이 이어가기·허브로",
    recordBottomNavAria: "하단 앱 탭 — Arena·성장·마이",
    arenaResultEyebrow: "Arena 결과",
    arenaResultRecordedTitle: "결정이 기록되었습니다.",
    arenaResultRecordedSubtitle: "결과가 반영되었습니다.",
    arenaResultSystemNoteTitle: "시스템 메시지",
    arenaResultSystemNoteBody: "의사결정 일관성이 개선된 것으로 감지되었습니다.",
    arenaResultContinuePlayCta: "계속",
    arenaResultReturnHubCta: "Arena로 돌아가기",
    growthHubSectionLabel: "성장",
    growthHubHeadline: "내면 상태를 다시 세웁니다.",
    growthHubLead: "해석·회복·정렬은 이 공간에서 이어집니다.",
    growthHubCardsNavAria: "Dojo·Integrity·Guidance·Journey·성장 기록",
    growthHubMainRegionAria: "성장 허브 — 내면 정렬·Dojo·Integrity·Guidance·Journey",
    growthCardDojoTitle: "Dojo",
    growthCardDojoDesc: "지금 상태를 가늠합니다",
    growthCardIntegrityTitle: "Integrity Mirror",
    growthCardIntegrityDesc: "상대 시선에서 상황을 봅니다",
    growthCardGuidanceTitle: "Guidance",
    growthCardGuidanceDesc: "결정 패턴을 되돌아봅니다",
    growthReflectionRegionAria: "Arena에서 이어진 성찰 진입",
    growthReflectionEyebrow: "연결된 성찰",
    growthReflectionTitle: "직전 결정에서 이어지는 질문",
    growthReflectionSubtitle:
      "일반 일기가 아니라, 방금 밀봉한 선택과 연결된 해석형 질문입니다. 판단이 아니라 관찰에 머뭅니다.",
    growthReflectionFocusLabel: "초점",
    growthReflectionCueLabel: "개발 힌트",
    growthReflectionOpenCta: "저널에서 펼치기",
    growthReflectionSuggestedTrack: "추천 성장 트랙으로",
    growthReflectionBackHub: "성장 허브로",
    growthReflectionNoSeed:
      "저장된 Arena 성찰 시드가 없습니다. Arena를 한 사이클 완료하면 이 화면이 채워집니다.",
    growthRecoveryCardTitle: "회복·정렬 신호",
    growthRecoveryCardBody:
      "최근 결정에서 정서 조절 부담이 누적된 패턴이 보입니다. 잠시 속도를 낮추고 호흡·경계·신뢰 회복을 점검해 보세요. 탈락이 아니라 복귀를 위한 순환입니다.",
    growthReflectionAirlockEyebrow: "성장 성찰",
    growthReflectionAirlockTitle: "다음 주기 전에 패턴을 살펴보세요.",
    growthReflectionAirlockLead:
      "직전 Arena 결정이 해석된 성찰 질문으로 옮겨졌습니다. 과거를 되짚기보다, 다음 응답을 단단히 하는 데 쓰세요.",
    growthReflectionFocusChannel: "초점 채널",
    growthReflectionFocusClarity: "명료성 초점",
    growthReflectionFocusTrust: "신뢰 초점",
    growthReflectionFocusRegulation: "조절 초점",
    growthReflectionFocusAlignment: "정렬 초점",
    growthReflectionFocusReady: "성찰 준비됨",
    growthReflectionSectionPrompt: "성찰 질문",
    growthReflectionSectionCue: "개발 힌트",
    growthReflectionSectionRecovery: "회복 안내",
    growthReflectionOpenReflection: "성찰 열기",
    growthReflectionOpenRecovery: "회복 프롬프트 열기",
    growthReflectionReturnArena: "Arena로 돌아가기",
    growthReflectionCueEmpty: "아직 활성 힌트가 없습니다.",
    growthRecoveryPlaceholderTitle: "회복 공간",
    growthRecoveryPlaceholderLead:
      "압박 후 회복과 복귀를 위한 경로입니다. 여기서 속도를 낮추고 다음 주기를 준비하세요.",
    growthWritingGuidedLabel: "안내 성찰",
    growthWritingHeadline: "신호를 다음 방향으로 바꿉니다.",
    growthWritingSubhead:
      "감상문이 아니라 패턴 정리입니다. 짧고 선명하게 적고, 마지막은 실행 문장으로 닫습니다.",
    growthWritingPromptSection: "성찰 프롬프트",
    growthWritingCueInline: "개발 힌트",
    growthWritingQ1Label: "질문 1",
    growthWritingQ1: "압박 속에서 가장 먼저 떠오른 반응은 무엇이었나요?",
    growthWritingQ2Label: "질문 2",
    growthWritingQ2: "무엇을 지키려 했고, 무엇이 취약해졌나요?",
    growthWritingQ3Label: "질문 3",
    growthWritingQ3: "다음 응답에서 무엇이 더 정밀해져야 하나요?",
    growthWritingCommitmentLabel: "다음 약속",
    growthWritingCommitmentHelper: "다음 결정 주기에 실제로 지킬 한 문장으로 마무리하세요.",
    growthWritingPlaceholder: "짧고 구체적으로 적어 보세요.",
    growthWritingCommitmentPlaceholder: "나는 …",
    growthWritingSystemNote: "SYSTEM // 성찰은 신호를 방향으로 바꿉니다.",
    growthWritingSave: "성찰 저장",
    growthWritingSaved: "저장되었습니다.",
    growthWritingReturnArena: "Arena로 돌아가기",
    growthWritingViewMyPage: "리더십 상태 보기",
    growthWritingNoSeed:
      "성찰 시드가 없습니다. Arena를 한 사이클 완료한 뒤 다시 오세요.",
    growthHistoryRegionAria: "성장 기록 — 최근 성찰",
    growthHistoryEyebrow: "성장 기록",
    growthHistoryHeadline: "결정이 무엇을 가르치는지 추적합니다.",
    growthHistoryLead:
      "성찰 기록은 저널 피드가 아닙니다. Arena 판단이 더 선명한 다음 행동으로 번역되는 구조화된 흔적입니다.",
    growthHistoryEmpty: "저장된 성찰이 아직 없습니다. Arena 후 성찰을 한 번 완료하면 여기에 쌓입니다.",
    growthHistoryRecoveryStrip:
      "다음 압박 주기 전에 회복 지원을 열어 둘 수 있습니다. 조용한 복귀를 위한 순환입니다.",
    growthHistoryFocusLabel: "초점",
    growthHistoryCommitmentLabel: "다음 약속",
    growthHistoryRecoveryBadge: "회복 연계",
    growthHistoryBackGrowth: "성장 허브로",
    growthCardHistoryTitle: "성장 기록",
    growthCardHistoryDesc: "최근 성찰과 약속 문장을 한눈에 봅니다",
    growthHistoryReflectionCountLabel: "성찰 수",
    growthHistoryCapsuleClarity: "명료성",
    growthHistoryCapsuleTrust: "신뢰",
    growthHistoryCapsuleRegulation: "조절",
    growthHistoryCapsuleAlignment: "정렬",
    growthHistoryArenaReflectionBadge: "Arena 성찰",
    growthHistoryRecoveryTitle: "회복 신호",
    growthHistoryOpenRecoveryCta: "회복 열기",
    growthHistoryEmptyEyebrow: "성찰 기록 없음",
    growthHistoryEmptyTitle: "구조화된 성찰이 아직 기록되지 않았습니다.",
    growthHistoryEmptySubtext:
      "Arena 결정 주기를 한 번 완료하고 성찰을 저장하면 성장 기록이 쌓이기 시작합니다.",
    growthHistoryCtaOpenLatest: "최근 성찰 열기",
    growthHistoryCtaReturnArena: "Arena로 돌아가기",
    recoveryPromptLowRegulationTitle: "회복보다 빠르게 압력이 쌓이고 있습니다.",
    recoveryPromptLowRegulationBody:
      "최근 신호는 내부 조절이 완전히 리셋되기 전에 응답 압력이 올라가는 패턴을 보여 줍니다.",
    recoveryPromptLowRegulationCue: "다음 에스컬레이션 주기 전에 조절을 다시 세우세요.",
    recoveryPromptRepeatedFrictionTitle: "조절 초점이 연속적으로 모이고 있습니다.",
    recoveryPromptRepeatedFrictionBody:
      "최근 성찰에서 정서 조절이 반복적으로 드러납니다. 다음 응답 전에 패턴을 명명하는 것이 도움이 됩니다.",
    recoveryPromptRepeatedFrictionCue: "패턴을 인식한 뒤, 다음 에스컬레이션 전 속도를 재정렬하세요.",
    recoveryPromptPressureAccumulationTitle: "에스컬레이션 압력이 서서히 올라가고 있습니다.",
    recoveryPromptPressureAccumulationBody:
      "경보 수준은 아니지만 회복 여유가 얇아지고 있습니다. 구조화된 멈춤이 다음 수를 보호합니다.",
    recoveryPromptPressureAccumulationCue: "다음 에스컬레이션 전 명료성과 속도를 조여 주세요.",
    recoveryEntryEyebrow: "회복 진입",
    recoveryEntryHeadline: "다음 에스컬레이션 주기 전에 구조를 다시 세웁니다.",
    recoveryEntryLead:
      "회복은 후퇴가 아닙니다. 다음 결정이 불필요한 왜곡을 덜 겪도록 보호하는 정렬된 멈춤입니다.",
    recoveryEntryChannelLabel: "회복 채널",
    recoveryChannelLowRegulation: "조절 회복",
    recoveryChannelRepeatedFriction: "마찰 반복",
    recoveryChannelPressureAccumulation: "압력 누적",
    recoveryChannelReady: "회복 준비",
    recoveryEntrySignalTitle: "회복 신호",
    recoveryEntryCueLabel: "회복 힌트",
    recoveryEntryNoPrompt: "지금은 활성화된 회복 프롬프트가 없습니다. Growth 또는 Arena로 돌아가도 됩니다.",
    recoveryEntryQ1Label: "회복 질문 1",
    recoveryEntryQ1: "응답이 안정되기 전에 어떤 압력 패턴이 반복되나요?",
    recoveryEntryQ2Label: "회복 질문 2",
    recoveryEntryQ2: "다음 에스컬레이션 전에 무엇을 다시 세워야 하나요?",
    recoveryEntryQ3Label: "복귀 약속",
    recoveryEntryQ3: "다음 응답을 이끌 한 문장을 적으세요.",
    recoveryEntryPlaceholder: "짧고 정확하게 적어 보세요.",
    recoveryEntrySystemStrip: "SYSTEM // 회복은 다음 결정 주기를 보호합니다.",
    recoveryEntrySave: "회복 저장",
    recoveryEntryReturnGrowth: "Growth로",
    recoveryEntryReturnArena: "Arena로",
    recoveryEntryRegionAria: "회복 진입 — 재정렬",
  },
  journal: {
    title: "저널",
    subtitle: "오늘 마음을 내려놓는 연습을 함께 해보자.",
    prompt: "오늘 누구에게도 말 못했던 마음은?",
    placeholder: "여기에 적어줘.",
    save: "저장",
    saveAria: "저장",
    footerDrChi: "Dr. Chi와 이야기하려면 오른쪽 아래 아바타 버튼을 눌러 주세요.",
    chatToggle: "챗",
    chatToggleAria: "챗 열기",
    chatTitle: "코치 챗",
    chatClose: "닫기",
    chatCloseAria: "닫기",
    chatStub: "(저널용 챗 UI)",
    journalMainRegionAria: "저널 — 오늘의 마음 기록·코치 챗",
    journalLoadingMainRegionAria: "저널 — 불러오는 중",
    journalRedirectingMainRegionAria: "저널 — 로그인으로 이동 중",
    journalRedirectingMessage: "로그인 페이지로 이동 중…",
  },
  myPageStub: {
    subMyPage: "마이 페이지",
    progressTitle: "진행",
    coreXp: "코어 XP",
    weeklyXp: "주간 XP",
    streak: "연속",
    systemMsg: "시스템 메시지",
    progressStage: "3단계 → 4단계",
    progressRank: "현재 순위: #128",
    progressStreakVal: "일관성: 5일",
    progressSystemLine: "안정적인 실행 패턴이 감지되었습니다.",
    progressFootnote: "예시 수치 · 심층 분석·raw 지표는 노출하지 않음",
    teamTitle: "팀",
    teamTiiCard: "팀 무결성 지수 (TII)",
    teamTiiPlaceholder: "팀 스냅샷 연결 전",
    teamStatusCard: "팀 상태",
    teamInnerStatus: "상태",
    teamStable: "안정",
    teamInnerTrend: "추세",
    teamTrendVal: "개선 중",
    teamRankCard: "팀 순위",
    teamRankCaption: "순위",
    teamRankLine: "#8",
    teamFooter: "팀 점수는 집단 실행 품질을 반영합니다.",
    leaderTitle: "리더 트랙",
    leaderCardStatus: "상태",
    leaderBuilding: "구축 중",
    leaderCardReadiness: "리더 준비도",
    leaderReadinessVal: "임계치 근접",
    leaderCardCert: "인증",
    leaderCertVal: "아직 자격 미달",
    leaderSystem: "시스템 메시지",
    leaderSystemLine: "책임 패턴이 개선되고 있습니다.",
    leaderFootnote: "상태·톤 위주 · raw 수치·과도한 설명 없음",
    overviewHealingCardTitle: "Healing",
    overviewHealingCardLead: "회복·2차 각성 경로로 이동합니다.",
    overviewHealingNavAria: "Healing·Awakening 바로가기",
    recentRunsCardTitle: "최근 런",
    recentRunsRegionAria: "최근 Arena 런 목록",
    recentRunsLoading: "런 목록 불러오는 중…",
    recentRunsEmpty: "최근 런이 없어요.",
    recentRunsAnonymous: "로그인하면 최근 런 목록이 보여요.",
    recentRunsFootnote: "순서·필드는 API 응답 그대로입니다. 다음 페이지는 cursor로 불러옵니다.",
    recentRunsLoadMore: "다음 런 더 보기",
    recentRunsLoadingMore: "불러오는 중…",
    recentRunsScenarioPrefix: "시나리오",
    recentRunsStartedPrefix: "시작",
    recentRunsStatusPrefix: "상태",
    recentRunsOpenDetail: "상세",
    runDetailPageTitle: "런 상세",
    runDetailPageSubtitle: "Arena · 단일 런",
    runDetailRegionAria: "Arena 런 상세",
    runDetailBackMyPage: "마이 페이지",
    runDetailLoading: "런 정보 불러오는 중…",
    runDetailSkeletonLoading: "런 정보 불러오는 중…",
    runDetailSkeletonEmpty: "런을 찾을 수 없거나 권한이 없어요.",
    runDetailError: "런을 불러오지 못했어요.",
    runDetailNotFound: "런을 찾을 수 없거나 권한이 없어요.",
    runDetailSignIn: "로그인 후 내 런을 볼 수 있어요.",
    runDetailScenarioId: "시나리오",
    runDetailStatus: "상태",
    runDetailStartedAt: "시작",
    runDetailCompletedAt: "완료",
    runDetailLocaleLabel: "언어",
    runDetailDifficulty: "난이도",
    runStatusLabelDone: "완료",
    runStatusLabelInProgress: "진행 중",
    runStatusLabelStarted: "시작됨",
    myPageShellOverviewTitle: "지금 상태입니다.",
    myPageShellOverviewSubtitle: "Arena에 쌓인 결정이 리더십 정체성을 만듭니다.",
    myPageOverviewRegionAria: "마이 페이지 개요 — 아이덴티티·진행·팀·계정 링크",
    myPageShellProgressTitle: "현재 움직임입니다.",
    myPageShellProgressSubtitle: "성장은 누적됩니다. 경쟁 창은 주기적으로 초기화됩니다.",
    myPageShellTeamTitle: "집단 무결성 상태입니다.",
    myPageShellTeamSubtitle: "팀 점수는 집단 실행 품질을 반영합니다.",
    myPageShellLeaderTitle: "준비도와 자격입니다.",
    myPageShellLeaderSubtitle: "리더십은 상태로 표시됩니다. 과도한 수치는 보이지 않습니다.",
    myPageCardIdentity: "아이덴티티",
    myPageCardProgress: "진행",
    myPageCardTeam: "팀",
    myPageLinkView: "보기 →",
    myPageLinkLeader: "리더 →",
    myPageLabelCodeName: "코드네임",
    myPageLabelStage: "단계",
    myPageLabelCoreProgress: "코어 진행",
    myPageLabelWeeklyProgress: "주간 진행",
    myPageOverviewTeamStatus: "상태",
    myPageProgressMovement: "단계 이동",
    myPageTeamNoteHeading: "안내",
    myPageAccountLink: "계정",
    myPageTabsAria: "마이 페이지 섹션",
    myPageTabOverview: "개요",
    myPageTabProgress: "진행",
    myPageTabTeam: "팀",
    myPageTabLeader: "리더",
    myPageTabAccount: "계정",
    myPageRouteSuspenseMainRegionAria: "마이 페이지 — 불러오는 중",
    leadershipRegionAria: "리더십 상태 콘솔",
    leadershipCardInsight: "인사이트 패턴",
    leadershipCardInfluence: "팀 영향",
    leadershipCardRhythm: "리듬",
    leadershipSectionSignalPattern: "신호 패턴",
    leadershipSectionInfluence: "팀 · 영향",
    leadershipSectionNext: "다음 포커스",
    leadershipLabelRelationalBias: "관계 편향",
    leadershipLabelOperationalBias: "운영 편향",
    leadershipLabelEmotionalRegulation: "정서 조절",
    leadershipLabelCommunicationWeight: "커뮤니케이션 비중",
    leadershipLabelTeamSignal: "팀 신호",
    leadershipLabelInfluencePattern: "영향 패턴",
    leadershipLabelAlignmentTrend: "정렬 경향",
    leadershipLinkArena: "Arena로",
    leadershipLinkGrowth: "Growth 반성",
    leadershipCoreTrace: "코어 궤적",
    leadershipIdentityEyebrow: "리더십 아이덴티티",
    leadershipSystemNoteTitle: "시스템 노트",
    leadershipSystemNoteBodyDormant: "Arena 신호가 쌓이면 리더십 패턴이 형성됩니다.",
    leadershipSystemNoteBodyActive: "Arena 신호가 안정적인 리더십 패턴으로 누적되고 있습니다.",
    leadershipAbbrevAir: "AIR (실행 무결성)",
    leadershipAirExecutionFootnote:
      "요약 레이블은 Arena 결정 흔적에서 해석됩니다. 공식 AIR는 리더십 엔진 활성화 창을 사용합니다. 패턴 시프트와 별도입니다.",
    leadershipAbbrevTii: "TII",
    leadershipAbbrevRhythm: "리듬",
    leadershipNextFocusHeading: "다음 포커스",
    leadershipDevelopmentCueHeading: "개발 힌트",
    leadershipSuggestedModuleLine: "제안: Arena 시나리오 · Growth 반성",
    leadershipReflectionLayerEyebrow: "성찰 누적",
    leadershipReflectionDepthColumn: "성찰 깊이",
    leadershipReflectionIntegrationColumn: "패턴 통합",
    leadershipRecoveryAwarenessColumn: "회복 인지",
    leadershipReflectionDepthEarly: "초기",
    leadershipReflectionDepthActive: "활성",
    leadershipReflectionDepthDeepening: "심화",
    leadershipReflectionIntegrationForming: "형성 중",
    leadershipReflectionIntegrationImproving: "개선 중",
    leadershipRecoveryAwarenessStable: "안정",
    leadershipRecoveryAwarenessDetected: "감지됨",
    leadershipNextFocusWithReflectionRegulation:
      "관계 신호는 강하지만, 운영적 명료성을 다듬는 것이 다음 정밀 가장자리로 남아 있습니다.",
    leadershipFieldPatternTitle: "결정 패턴 형태",
    leadershipPatternShiftSectionTitle: "패턴 시프트 (행동 검증)",
    leadershipPatternShiftPlaceholder:
      "pattern_shift_results가 아직 없습니다. 실행 무결성(AIR)과 혼동하지 마세요. 데이터가 연결되면 이 블록이 채워집니다.",
    leadershipFieldInfluenceTitle: "영향",
    leadershipReflectionPanelTitle: "성찰 깊이",
    leadershipRowRecentFocus: "최근 초점",
    leadershipRowIntegration: "통합 신호",
    leadershipReflectionTraceEmpty: "구조화된 성찰이 쌓이면 이 영역이 열립니다.",
    leadershipRecoveryTraceEmpty: "회복 인지는 압력 패턴이 모일 때 조용히 표시됩니다.",
    leadershipIdentityCrestAria: "리더 정체성 마크",
    leadershipRecoverySupportNote: "압박 패턴이 겹칠 때는 회복 프레이밍으로 다음 결정을 보호할 수 있습니다.",
    leadershipRecoveryStableNote: "이 구간에서는 즉각적인 회복 개입이 필요하다는 신호는 없습니다.",
    leadershipSupportNoteLabel: "보조 안내",
    leadershipRecoveryStatusLabel: "회복 인지 상태",
    leadershipSuggestedRouteHeading: "제안 경로",
    leadershipSuggestedRouteDefault: "Arena · Growth · Reflection",
    leadershipSuggestedRouteRecovery: "Recovery · Reflection",
  },
  actionContract: {
    pendingTitle: "대기 중인 실행",
    pendingBody: "다음 실행이 준비되었습니다.",
    completedTitle: "완료된 약속",
    completedBody: "이 약속은 확인되었습니다.",
    missedTitle: "실행 기간이 종료되었습니다.",
    missedBody: "다음 시나리오로 계속 진행할 수 있습니다.",
    btnQr: "검증용 QR 보기",
    expiresIn: "만료까지",
    expiredShort: "만료됨",
    qrWitnessNotice: "이 QR은 본인이 완료하는 화면이 아닙니다.\n실제 행동을 확인할 수 있는 동료, 매니저 또는 어시스턴트에게 보여주세요.\n상대가 QR을 스캔하면 행동이 검증되고 완료됩니다.",
    dismiss: "닫기",
    awaitingListTitle: "QR 인증 대기 중인 실행",
    pendingListTitle: "내가 해야 할 행동",
    pendingEmptyMessage: "지금 처리할 행동이 없어요.",
    pendingErrorMessage: "행동 목록을 불러오지 못했어요.",
    badgeQrRequired: "QR 필요",
    badgeAwaitingVerification: "검증 대기",
    badgeInProgress: "진행 중",
    tierPersonal: "개인",
    tierManager: "매니저",
    scanVerified: "확인되었습니다. 이 행동은 오늘의 성장에 더해졌습니다.",
    scanAlready: "이미 인증된 실행입니다.",
    scanFailed: "인증에 실패했습니다. 다시 시도해 주세요.",
    scanValidationRequired: "아직 증인 확인을 받을 준비가 되지 않았습니다. 실행자가 먼저 행동 검증을 완료해야 합니다.",
    qrValidationRequired: "아직 행동 검증이 완료되지 않았습니다. 먼저 실행 내용을 제출한 뒤 QR을 보여주세요.",
    goToValidationCta: "행동 검증으로 이동",
    witnessPromiseTitle: "오늘의 약속",
    witnessConfirmQuestion: "오늘 정말 해냈나요?",
    witnessConfirmCta: "확인하기",
    witnessConfirming: "확인 중…",
    witnessConfirmSuccess: "확인되었습니다. 이 행동은 오늘의 성장에 더해졌습니다.",
    witnessLoadFailed: "행동을 불러오지 못했습니다. 링크를 다시 확인해 주세요.",
    witnessSelfBlocked: "이 행동은 다른 사람이 확인해야 합니다. 동료, 매니저, 또는 함께한 사람에게 QR을 보여주세요.",
    actorCompletedTitle: "오늘 하나는 실제로 해냈습니다.",
    actorReflectionPrompt: "실제로 해보니 어땠나요?",
    actorContinueCta: "내일 다시 이어가기",
  },
  legal: {
    accept: {
      title: "[KO_PENDING] Terms of Service & Privacy Policy",
      placeholder_notice: "[KO_PENDING] Legal text under review.",
      section_heading: "[KO_PENDING] Pending Legal Review",
      checkbox_label: "[KO_PENDING] I acknowledge that I am reviewing a placeholder consent form.",
      submit_button: "[KO_PENDING] Accept & Continue",
      submitting: "[KO_PENDING] Saving...",
      error_required: "[KO_PENDING] Please check the box to continue",
      error_network: "[KO_PENDING] Connection error. Please try again.",
      error_server: "[KO_PENDING] Something went wrong. Please try again.",
      error_rate_limit: "[KO_PENDING] Too many attempts. Please wait a few minutes.",
      success_message: "[KO_PENDING] Thank you. Redirecting...",
    },
  },
};

const en: Messages = {
  today: {
    dailyOs: {
      pulseHeading: "Your relationships, now",
      doorHeading: "Today, choose one",
      mirrorHasEvidence: "Yesterday, one thing crossed into real life.",
      mirrorNoEvidence: "It's quiet. You can choose one thing today.",
      openLoopTitle: "An open promise",
      openLoopBody: "You have a promise that isn't closed yet.",
      openLoopCta: "Return to your promise",
      centerFirstTitle: "First, come back to yourself",
      centerFirstBody: "Now is a time to return to yourself.",
      centerFirstCta: "Go to Center",
      reexposureTitle: "A moment to face again",
      reexposureBody: "A past choice is waiting for you.",
      reexposureCta: "Enter the Arena",
      firstDayTitle: "Today, the first",
      firstDayBody: "It's your first day at BTY. Try choosing just one.",
      doorCenterTitle: "With yourself",
      doorCenterSub: "Return to yourself",
      doorArenaTitle: "With others",
      doorArenaSub: "Step into connection",
      doorFoundryTitle: "With the world",
      doorFoundrySub: "Plant one thing in the world",
      exitLine: "Choose one, then return to your day.",
      loadingLabel: "Opening today…",
      pulse: {
        self: {
          quiet: "Quiet with yourself, for now.",
          living: "You've been coming back to yourself.",
          connected: "You meet yourself often.",
          deepening: "Going deeper with yourself.",
        },
        others: {
          quiet: "Quiet with others, for now.",
          living: "You've started meeting others.",
          connected: "You meet others often.",
          deepening: "Going deeper with others.",
        },
        ground: {
          quiet: "Quiet with the world, for now.",
          living: "You've started planting in the world.",
          connected: "You leave your mark often.",
          deepening: "Going deeper with the world.",
        },
      },
      intel: {
        focusLabel: "Today's focus",
        focus: {
          Self: {
            title: "With yourself",
            line: "Today is a day to set your relationship with yourself first.",
          },
          Others: {
            title: "With others",
            line: "Today is a day to close one action inside a relationship.",
          },
          World: {
            title: "With the world",
            line: "Today is a day to hold reality a little more clearly.",
          },
          CleanStart: {
            title: "Today, anew",
            line: "Today opens fresh. There's nothing to read into yet.",
          },
          ContinuePending: {
            title: "One thread, still open",
            line: "Today isn't a new task — it's closing an action that's still open.",
          },
        },
        ctaSelf: "Return to yourself",
        ctaOthers: "Step into connection",
        ctaWorld: "Plant one in the world",
      },
    },
    eyebrow: "BTY Daily",
    title: "What choice will you face today?",
    greeting: "Good morning.",
    nowReserved: "Reserved",
    todayLabel: "Day in focus",
    promise: "Carry one thing today. Let the rest wait.",
    journeyPlaceholder: "Your path, coming soon.",
    pendingPlaceholder: "Your open items will live here.",
    reflectionPlaceholder: "Tonight, a moment to look back.",
    beginButton: "Begin today",
    beginConfirmation: "Today is open. Come back when the moment comes.",
    profileTitle: "Today's me",
    coreXpLabel: "Core XP",
    levelStub: "Level and leadership status — coming soon",
    companionName: "Dr. Chi",
    companionLine: "One honest step today is enough.",
    situationTitle: "Today's choice",
    situationEmpty: "No open action right now.",
    beginCta: "Begin",
    pointsTitle: "Today's points",
    pointsLabel: "XP earned",
    airTitle: "Execution integrity",
    airLabel: "AIR",
    airBandLow: "Low",
    airBandMid: "Mid",
    airBandHigh: "High",
    airBandUnknown: "—",
    streakLabel: "{n}-day streak",
    stageLabel: "Stage {n} of 4 · {name}",
    greetingNamed: "Good morning, {name}.",
    greetingPlain: "Good morning.",
    promiseLine: "Today has one promise.",
    companionCardTitle: "Today with you",
    choiceWaiting: "One choice is waiting for you.",
    behaviorTitle: "Today's action",
    behaviorWaiting: "Waiting",
    growthTitle: "Growth",
    orbHint: "Hold to begin",
  },
  membership: {
    form: {
      title: "Team membership",
      intro:
        "Submit your team membership request. An admin reviews and approves it before you can enter the Arena.",
      fullName: { label: "Full name" },
      jobFunction: { label: "Role", staff: "Staff", leader: "Leader" },
      joinedAt: { label: "Join date" },
      leaderStartedAt: {
        label: "Leadership start date",
        hint: "Required when your role is Leader.",
      },
      submit: "Submit request",
      submitting: "Submitting…",
      errorGeneric: "Something went wrong. Please try again.",
      success: "Submitted. Awaiting admin approval.",
    },
    status: {
      pending: "Your membership request is pending admin approval.",
      approved: "Your team membership is approved.",
      approvedOn: "Approved on",
    },
    gate: {
      redirectCopy: "Membership required. Submit your team membership request to continue.",
    },
  },
  loading: {
    message: "Please wait…",
    hint: "First load may take 1–2 minutes.",
    localeRouteSuspenseMainRegionAria: "App — loading",
  },
  nav: { center: "Center", bty: "Foundry", arena: "Arena", en: "English", ko: "한국어", skipToMainContent: "Skip to main content" },
  login: {
    title: "bty Sign in",
    afterLoginGoTo: "After sign in you will go to",
    email: "Email",
    password: "Password",
    submit: "Sign in",
    submitting: "Signing in…",
    cookieNotice: "Session uses cookies. The page may refresh or redirect after sign in.",
    errorDefault: "Sign in failed.",
    forgotPassword: "Forgot password?",
    forgotPasswordSent: "We sent a reset link to your email. Check your inbox (and spam folder).",
    forgotPasswordError: "Request failed. Check the email address or wait a moment and resubmit.",
    forgotPasswordMainRegionAria: "Forgot password",
    btyPublicRouteSuspenseMainRegionAria: "Foundry sign-in — loading",
  },
  auth: { backToLogin: "Back to sign in", loading: "Verifying…", callbackError: "Verification failed. Please resubmit." },
  logout: "Log out",
  arena: {
    weeklyTierBronze: "Bronze (weekly)",
    weeklyTierSilver: "Silver (weekly)",
    weeklyTierGold: "Gold (weekly)",
    weeklyTierPlatinum: "Platinum (weekly)",
    runStateInProgress: "In progress",
    runStateCompleted: "Completed",
    runStateAborted: "Aborted",
    tieRankSuffix: "Tied",
    sessionChoiceToastExecutionIntegrity: "Execution integrity signal updated.",
    sessionSummaryExecutionIntegrityHeading: "Execution integrity (AIR)",
    sessionSummaryAirBandBefore: "Previous band",
    sessionSummaryAirBandAfter: "Current band",
    sessionSummaryContextNoteHeading: "Session note",
    sessionSummaryPatternShiftHeading: "Pattern Shift (behavior validation)",
    sessionSummaryPatternShiftPlaceholder:
      "Pattern Shift results are not connected yet. This is separate from AIR (execution integrity) and will appear here when pattern_shift_results is available.",
  },
  reflectHints: {
    reflect_hint_empty: "Even one sentence helps you move forward.",
    reflect_hint_short: "A bit more detail would strengthen your reflection.",
    reflect_hint_developing: "Nice — your perspective is coming through.",
    reflect_hint_substantial: "You’ve captured plenty. You can still add if you like.",
    reflect_hint_near_limit: "You’re close to the length limit — wrap up when ready.",
    reflect_hint_at_limit: "You can submit at this length.",
  },
  integrity: {
    title: "Integrity Mirror",
    subtitle: "Reframe the situation with Dr. Chi.",
    stepLabelGuide: "Step 2 — Guide",
    guideMessage: "A short practice to see the other side. Describe a conflict in one line and Dr. Chi will reflect with an integrity question.",
    startPractice: "Start practice",
    intro: "Describe a conflict in one line. Dr. Chi will reflect with an integrity question.",
    placeholder: "e.g. I said something and the other person got upset",
    send: "Send",
    thinking: "Dr. Chi is thinking…",
    emptyHint: "Enter a conflict and send.",
    emptyStateHint: "Send your first message.",
    backToFoundry: "Back to Foundry",
    reply: "What if the roles were reversed?",
    doneTitle: "Today’s practice complete",
    doneSub: "You’re building the habit of seeing the other side, one step at a time.",
    doneCtaComplete: "Complete practice",
    doneCtaMentor: "Talk to mentor",
    doneCtaFoundry: "Back to Foundry",
    doneCtaAssessment: "50-item assessment",
    apiError: "Connection issue. Please resubmit.",
    errorTitle: "Error",
    replyFallback: "Could you say a bit more?",
    ariaStartPractice: "Start practice",
    ariaCompletePractice: "Complete practice",
    ariaInputLabel: "Describe a conflict situation",
    ariaWaitingChi: "Waiting for Dr. Chi…",
    mentorName: "Dr. Chi",
    conversationSimulatorAria: "Integrity conversation",
    messageComposerAria: "Message input and send",
    integrityMainRegionAria: "Integrity simulator — guide, conversation, completion",
  },
  center: {
    loading: "Please wait…",
    mainAriaLabel: "Letter to yourself",
    headerLabel: "Dear Me title and intro",
    contentLabel: "Dear Me content",
    title: "Center",
    heroTitleMain: "Center,",
    heroTitleAccent: "I'm listening.",
    tagline: "A cozy room to rest and heal. You're safe here.",
    ctaToFoundry: "Go to Foundry (practice)",
    linkToBty: "Go to bty (practice)",
    assessmentCta: "Self-Esteem Assessment (50 items)",
    assessmentCtaSub: "A short check on how you feel about yourself.",
    assessmentDetailHint: "Click here for a more detailed test.",
    entryIntro: "A safe space that reflects your feelings. No advice—just reflection. A place to rest and heal.",
    startCta: "Start",
    todayStepTitle: "Today's you",
    todayMoodLabel: "How do you feel right now?",
    todayEnergyLabel: "Energy level (1=very low, 5=okay)",
    todayOneWordLabel: "One word for today?",
    todayMoodPlaceholder: "e.g. calm, a bit anxious",
    todayOneWordPlaceholder: "e.g. grateful, tired",
    todayNext: "Next",
    todaySkip: "Skip",
    bridgeHeading: "Ready to step out?",
    bridgeSub: "(Go to practice)",
    letterStepTitle: "A letter to yourself",
    letterFormLabel: "Letter input and send",
    letterPrompt: "Write what's on your mind. Here we reflect, not advise.",
    letterPlaceholder: "Was there something you couldn't tell anyone today?",
    submitLetter: "Send",
    sendingLetter: "Reading your words…",
    replyStepTitle: "Reply",
    replySummaryLabel: "Reply summary",
    completedTitle: "Today's letter complete",
    completedSub: "Thanks for sharing. You can continue in Chat if you'd like.",
    continueToChat: "Continue in Chat",
    letterHistoryTitle: "Letter history",
    letterHistoryListAria: "List of sent letters by date",
    letterHistoryEmpty: "No letters yet.",
    letterHistoryError: "Failed to load letter history.",
    letterHistoryLoading: "Loading letter history…",
    letterHistoryReplied: "Replied",
    letterHistoryNoReply: "No reply",
    calendarTitle: "Letter calendar",
    calendarPrevMonth: "Previous month",
    calendarNextMonth: "Next month",
    calendarWeekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    calendarMonths: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    calendarSelectedEmpty: "No letter on this day.",
    dearMeComposerRegionAria: "Dear Me letter composer",
    dearMeComposerCharCount: "{n} / min 100 · max 1000",
    dearMeComposerSubmit: "Send letter",
    dearMeComposerSubmitting: "Saving…",
    dearMeComposerSaved: "Your letter was saved",
    dearMeComposerLoadError: "Could not load. Please resubmit.",
    dearMeComposerSubmitError: "Could not save. Please resubmit.",
    dearMeComposerPreviousTitle: "Previous letters",
    replyActionsLabel: "Actions after reply",
    footerLabel: "Back to Center",
    dearMeSuspenseMainRegionAria: "Dear Me — loading",
    dearMeErrorMainRegionAria: "Dear Me — error",
    centerSuspenseMainRegionAria: "Center — loading",
    centerErrorMainRegionAria: "Center — error",
    currentStateTitle: "Current State",
    forcedResetRegionAria: "Integrity reset — lockout, diagnostics, return",
    forcedResetBannerTitle: "Arena is locked during the integrity reset window.",
    forcedResetCountdownLive: "Time remaining until unlock",
    forcedResetArenaNavLabel: "Go to Arena",
    forcedResetArenaBlockedHint: "Arena navigation is disabled while locked.",
    forcedResetChecklistTitle: "Center diagnostic checklist",
    forcedResetItemStabilize: "Breathing & nervous-system stabilization check done",
    forcedResetItemBoundary: "Boundaries & recovery plan confirmed",
    forcedResetItemAccountability: "Accountability & return commitment captured",
    forcedResetProgress: "{done} / {total} complete",
    forcedResetReturnCta: "Request return",
    forcedResetSubmitting: "Submitting…",
    forcedResetError: "Could not complete return request. Please resubmit.",
    forcedResetWindowElapsed: "The 48-hour window has passed. Finish the checklist below, then request return.",
  },
  bty: {
    title: "bty",
    tagline: "Gratitude. Integrity & Better Than Yesterday.",
    linkToCenter: "Go to Center",
    entryIntro: "Dashboard, mentor, integrity practice. Choose what to work on.",
    startCta: "Start",
    arenaCta: "Play Arena",
    dashboardLabel: "Dashboard",
    leaderboardLabel: "Leaderboard",
    leStageLabel: "LE Stage",
    airLabel: "AIR",
    recommendationLabel: "Recommendation",
    recommendationEmptyPlaceholder:
      "No personalized recommendation right now. Activity in Arena, Foundry, or Center will surface next steps here.",
    airUnavailable: "Unable to load AIR.",
    weeklyReflectionRegion: "Weekly reflection progress",
    personalRecordRegion: "Personal record this week",
    streakLabel: "Streak",
    pointsTodayLabel: "Points Today",
    lifetimeProgressLabel: "Lifetime Progress (Core XP)",
    dojoPracticeLabel: "Dojo practice",
    dojoPracticeLinksRegion: "Dojo practice links",
    leaderboardWeekResetRegion: "Weekly reset time",
    dashboardTierCardLabel: "Arena tier",
    dashboardTierCardSubline:
      "Tier from the server. Do not derive tier from Core XP in the UI.",
    dashboardTierCardRegionAria: "Arena tier (API)",
    leAirSummarySectionAria: "Leadership Engine and AIR summary widgets",
    dashboardShortcutGoArena: "Play Arena",
    dashboardShortcutWeeklyRanking: "View weekly ranking",
    leaderboardMainRegionAria: "Leaderboard cards and ranking list",
    leaderboardRouteSuspenseMainRegionAria: "Leaderboard — loading",
    leaderboardWidgetRegionAria: "Top 20 weekly XP",
    leaderboardWidgetTitle: "Weekly leaderboard (top 20)",
    leaderboardWidgetResetCountdown: "Weekly reset in",
    leaderboardWidgetLoading: "Loading rankings…",
    leaderboardWidgetError: "Could not load leaderboard.",
    leaderboardWidgetEmpty: "No rankings yet.",
    leaderboardWidgetTop20Aria: "Top 20 weekly XP list",
    avatarRendererRegionAria: "Avatar display tier",
    avatarRendererLoading: "Loading avatar…",
    avatarRendererError: "Could not load avatar state.",
    avatarRendererNextTierProgress: "Progress to next tier (Core XP)",
    avatarRendererMaxTier: "Max tier",
    avatarRendererUnlockedBadges: "Unlocked assets",
    avatarRendererCoreXpLabel: "Core XP",
    avatarRendererCoreXpSources: "Core XP by source",
    avatarRendererCoreXpSegmentsAria: "Core XP earned by source (share)",
    avatarRendererXpArena: "ARENA",
    avatarRendererXpLab: "LAB",
    avatarRendererXpFoundry: "FOUNDRY",
    avatarRendererXpMentor: "MENTOR",
    avatarRendererXpOther: "Other",
    profileRouteSuspenseMainRegionAria: "Profile — loading",
    dashboardHeroSubtitle: "Your arena progress at a glance.",
    dashboardWeeklyRankWidgetTitle: "Weekly XP & ranking",
    dashboardWeeklyWidgetLoading: "Loading weekly info",
    dashboardWeeklyWidgetAria: "This week’s weekly XP and ranking info",
    dashboardWeeklyXpCaption: "Weekly XP earned this week (UTC window)",
    dashboardWeeklyTierCaption: "This week's competition tier (from weekly XP)",
    dashboardWeeklyCompetitionStageCaption: "Weekly competition stage (band)",
    weeklyCompetitionStageBandBronze: "Bronze band — first weekly XP climb.",
    weeklyCompetitionStageBandSilver: "Silver band — steady progress.",
    weeklyCompetitionStageBandGold: "Gold band — upper competition tier.",
    weeklyCompetitionStageBandPlatinum: "Platinum band — top weekly tier.",
    leStageLeProgressBarAria: "Leadership Engine stage progress",
    dashboardWeeklyRankSeeLeaderboard: "View rank on leaderboard",
    dashboardWeeklyRankHint: "Weekly rank is shown only on the leaderboard.",
    dashboardOpenLiveLeaderboardCta: "Preview live rankings",
    dashboardLiveLeaderboardModalTitle: "This week’s live rankings",
    dashboardLiveLeaderboardModalAria: "Weekly XP leaderboard preview",
    dashboardLiveLeaderboardLoading: "Loading rankings…",
    dashboardLiveLeaderboardEmpty: "No rows to show yet.",
    dashboardLiveLeaderboardClose: "Close",
    dashboardLiveLeaderboardFullPage: "Open full leaderboard",
    dashboardLiveLeaderboardFailed: "Could not load.",
    dashboardLiveLeaderboardModalDesc:
      "Weekly XP rankings for this week. You may see players near your rank or the top of the board.",
    dashboardLiveLeaderboardListAria: "Weekly XP ranking list",
    dashboardLiveLeaderboardNotRankedBanner:
      "You’re not on the board for this week yet. Below is a preview of active players.",
    dashboardLiveLeaderboardNearMeFallbackBanner:
      "No players in your nearby rank slice; showing top rankings instead.",
    dashboardLiveLeaderboardCloseAria: "Close live rankings preview",
    leStageSummaryWidgetDesc:
      "Stage, reset due date, Arena summary, and behavior pattern are shown as returned by the server.",
    profileCodeNameCardLabel: "Code · Sub name",
    profileIdentitySubline:
      "Your identity on the dashboard. Complete an Arena scenario to appear on the leaderboard.",
    profileRenamePlaceholder: "Rename Sub Name (7 chars, once per code)",
    profileSaveSubName: "Save",
    profileSaving: "Saving…",
    eliteMeContentUnlockedLabel: "Elite-only content (me/elite)",
    eliteMeContentUnlockedYes: "Yes",
    eliteMeContentUnlockedNo: "No",
    eliteSpecWidgetRegionAria: "Elite — certification and mentor request queue",
    eliteSpecWidgetCertifiedBadge: "Certified Leader",
    eliteSpecWidgetCertifiedYes: "Certified (leadership engine rules)",
    eliteSpecWidgetCertifiedNo: "Not certified",
    eliteSpecWidgetLriHint: "LRI and Certified status follow server/domain rules.",
    eliteSpecWidgetQueueTitle: "Dr. Chi 1:1 request queue",
    eliteSpecWidgetQueueForbidden: "Only mentors/admins can view the queue.",
    eliteSpecWidgetStatusPending: "Pending",
    eliteSpecWidgetStatusApproved: "Approved",
    eliteSpecWidgetStatusRejected: "Rejected",
    eliteSpecWidgetApprove: "Approve",
    eliteSpecWidgetReject: "Reject",
    eliteSpecWidgetLoading: "Loading…",
    eliteSpecWidgetError: "Could not load.",
    leEngineTiiCertifiedBandAria: "Team TII, AIR summary, and certification",
    weeklySeasonActivityAria: "Season and weekly XP, event count",
    dashboardPageTitle: "Dashboard",
    dashboardMainRegionAria: "Dashboard — Arena progress, weekly ranking, recommendations",
    dashboardSuspenseMainRegionAria: "Dashboard — loading",
    dojoSuspenseMainRegionAria: "Dojo — loading",
    eliteSuspenseMainRegionAria: "Elite — loading",
    healingSuspenseMainRegionAria: "Healing — loading",
    mentorSuspenseMainRegionAria: "Mentor — loading",
    integrityPracticeSuspenseMainRegionAria: "Integrity practice — loading",
    foundryHubMainLandmarkAria: "Foundry practice hub",
    foundryHubSuspenseMainRegionAria: "Foundry practice hub — loading",
    btyIndexMainRegionAria: "bty home — Arena, Center, Foundry hubs",
    foundryFeatureCardsRegionAria: "Links to Dojo, Integrity, mentor, dashboard, Elite",
    foundryBackToBtyHome: "← bty home",
    programRecommenderWidgetRegionAria: "Recommended learning programs",
    programRecommenderWidgetTitle: "Recommended programs",
    programRecommenderWidgetLoading: "Loading…",
    programRecommenderWidgetError: "Could not load recommendations.",
    programRecommenderWidgetEmpty: "No recommendations yet. Play Arena or progress healing, then check back.",
    programRecommenderWidgetLocked: "Locked — unlock in Arena",
    foundryProgramDetailMainAria: "Foundry program detail",
    indexThreeHubsExplainer: "Arena: scenarios & ranking. Center: rest & letters. Foundry: dashboard, mentor & practice.",
    indexHubEntriesRegionAria: "Arena, Center, and Foundry hub links",
    pointsTodaySubline: "XP earned today (UTC date).",
    airIntegritySlip: "Integrity slip",
    leStageSummaryUnavailable: "Unable to load stage summary.",
    leAirAria7d: "AIR 7d",
    leAirAria14d: "AIR 14d",
    leAirAria90d: "AIR 90d",
    leAirAriaGrid: "AIR: 7d, 14d, 90d",
    leAirLoading: "Loading AIR metrics…",
    leStageSummarySectionAria: "Leadership Engine stage summary",
    leStageStagePrefix: "Stage",
    leStageResetDueLabel: "Reset due",
    leStageArenaSummaryHeading: "Arena summary",
    leStageBehaviorHeading: "Behavior pattern",
    leadershipEngineWidgetRegionAria: "Leadership Engine — AIR, TII, LRI",
    leadershipEngineWidgetTitle: "Leadership Engine",
    leadershipEngineAir14Aria: "AIR (14 days)",
    leadershipEngineTiiAria: "Team Integrity Index (TII)",
    leadershipEngineLriAria: "LRI readiness",
    leadershipEngineReadinessReady: "Ready",
    leadershipEngineReadinessNotReady: "Building",
    leadershipEngineCertifiedBadge: "Certified Leader — AIR 14d at or above 80%",
    leadershipEngineWidgetLoading: "Loading leadership metrics…",
    leadershipEngineWidgetError: "Could not load leadership metrics.",
    leadershipEngineAirTrendSparklineAria: "AIR 30-day trend",
    leadershipEnginePromotionBlockedBadge: "Promotion blocked",
    leadershipEngineCertifiedExpiresIn: "Certified expires in",
    leadershipEngineRenewalHistoryAria: "Certified Leader renewal attempts",
    leadershipEngineRenewalEmpty: "No renewal attempts yet.",
    leadershipEngineRenewalRowRenewed: "Renewed",
    leadershipEngineRenewalRowNotRenewed: "Not renewed",
    leadershipEngineElitePendingBadge: "Elite Spec pending review",
    leadershipEngineEliteApprovedBadge: "Elite Spec approved",
    leadershipEngineWeeklyStreak: "Weekly streak",
    leadershipEngineWeeklyCompletion: "Weekly completion",
    leadershipEngineIntegrityRegionAria: "Overall integrity grade",
    leadershipEngineIntegrityLabel: "Integrity composite",
    leadershipEngineEliteCta: "Register as Elite candidate",
    leadershipEngineEliteCtaBusy: "Submitting…",
    tiiWidgetRegionAria: "Team Integrity Index — team health",
    tiiWidgetTitle: "Team Integrity Index (TII)",
    tiiWidgetHealthBarAria: "TII health bar: red 0–59, yellow 60–79, green 80–100.",
    tiiWidgetBandRed: "At risk (0–59)",
    tiiWidgetBandYellow: "Fair (60–79)",
    tiiWidgetBandGreen: "Healthy (80–100)",
    tiiWidgetMembersLabel: "Members included in team average",
    tiiWidgetLastCalculated: "Last calculated",
    tiiWidgetNoSnapshot: "No TII snapshot yet.",
    tiiWidgetLoading: "Loading TII…",
    tiiWidgetError: "Could not load TII.",
    tiiWidgetScorePct: "{pct}%",
    leStageLoading: "Loading stage summary…",
    airTrendWidgetRegionAria: "AIR 30-day trend",
    airTrendWidgetTitle: "AIR trend (30 days)",
    airTrendWidgetSevenDayLabel: "Last 7-day execution integrity band",
    airTrendWidgetSevenDayAria: "Last 7-day AIR execution integrity band (low, mid, high)",
    airTrendWidgetCertifiedExpiryPrefix: "Certified Leader expires in",
    airTrendWidgetWarningBanner:
      "Your 7-day rolling AIR has declined for several days in a row. Consider reviewing your activation pattern.",
    airTrendWidgetLoading: "Loading AIR trend…",
    airTrendWidgetError: "Could not load AIR trend.",
    airBandLow: "Low band",
    airBandMid: "Mid band",
    airBandHigh: "High band",
    airExecutionIntegrityFootnote:
      "AIR reflects execution integrity (selection, completion, verification) only — not Pattern Shift.",
    weeklyReportCardRegionAria: "Weekly AIR report",
    weeklyReportCardAirTrendCaption: "Execution integrity trend (30d)",
    weeklyReportCardTitle: "Weekly report",
    weeklyReportCardWeekLabel: "Week (UTC Monday)",
    weeklyReportCardCompletionLabel: "Scenario completion",
    weeklyReportCardStreakLabel: "Play streak",
    weeklyReportCardStreakDays: "{n} days",
    weeklyReportCardFlagHero: "Hero trap",
    weeklyReportCardFlagIntegrity: "Integrity slip",
    weeklyReportCardFlagClean: "Clean / other",
    weeklyReportCardFlagBarAria: "Flag type mix",
    weeklyReportCardPhaseLabel: "Healing phase progress",
    weeklyReportCardResilienceLabel: "Resilience score",
    weeklyReportCardIntegrityRegionAria: "Overall integrity grade",
    weeklyReportCardIntegrityLabel: "Overall integrity",
    weeklyReportCardIntegritySubline: "Grade {grade} · composite {score} (weighted AIR, LRI, resilience)",
    weeklyReportCardLoading: "Loading weekly report…",
    weeklyReportCardError: "Could not load weekly report.",
    weeklyReportCardNoReport: "No report for this week yet. Check back soon.",
    weeklyReportCardCtaStart: "Start Center assessment",
    mentorChatShellRegionAria: "Dr. Chi mentor chat",
    mentorChatShellTitle: "Dr. Chi",
    mentorChatShellPhaseBadge: "Healing phase",
    mentorChatShellFlagLabel: "Flag",
    mentorChatShellRecsCta: "View Foundry program recommendations",
    mentorChatShellPlaceholder: "Type a message…",
    mentorChatShellSend: "Send",
    mentorChatShellLoading: "Preparing session…",
    mentorChatShellError: "Could not load chat.",
    dojoAssessmentShellRegionAria: "Foundry Dojo micro skill check",
    dojoAssessmentShellLoading: "Preparing assessment…",
    dojoAssessmentShellError: "Could not load assessment.",
    dojoAssessmentShellProgress: "Question {current} of {total}",
    dojoAssessmentShellNext: "Next",
    dojoAssessmentShellSubmit: "Submit",
    dojoAssessmentShellPass: "Passed",
    dojoAssessmentShellFail: "Not passed",
    dojoAssessmentShellScoreLabel: "Area score",
    dojoAssessmentShellHintLabel: "Improvement hint",
    dojoAssessmentShellReturnToFoundry: "Back to Foundry programs",
    dojoAssessmentShellLikert1: "Strongly disagree",
    dojoAssessmentShellLikert2: "Disagree",
    dojoAssessmentShellLikert3: "Agree",
    dojoAssessmentShellLikert4: "Strongly agree",
    programProgressShellRegionAria: "Foundry program progress",
    programProgressShellLoading: "Loading progress…",
    programProgressShellError: "Could not load.",
    programProgressShellPhaseLabel: "Phase",
    programProgressShellCompletionLabel: "Completion",
    programProgressShellModulesHeading: "Modules",
    programProgressShellModuleTap: "Tap to complete",
    programProgressShellCompleteOverlay: "Program complete",
    programProgressShellArenaCta: "Back to Arena",
    programProgressShellDojoCta: "Start Dojo assessment",
    awakeningMilestoneTrackerRegionAria: "Awakening milestones",
    awakeningMilestoneTrackerTitle: "RENEWAL awakening milestones",
    awakeningMilestoneTrackerNextLabel: "Next focus",
    awakeningMilestoneTrackerRenewalUnlocked: "RENEWAL badge unlocked",
    awakeningMilestoneTrackerRenewalSub: "You completed all four milestones.",
    awakeningMilestoneTrackerDismiss: "Dismiss",
    awakeningMilestoneTrackerChatCta: "Chat with Dr. Chi",
    awakeningMilestoneTrackerCollapseChat: "Collapse chat",
    awakeningMilestoneTrackerLoading: "Loading milestones…",
    awakeningMilestoneTrackerError: "Could not load milestones.",
  },
  assessmentResult: {
    mainContentRegionAria: "Assessment result: scores and recommended track",
    nextStepsCtaGroupAria: "Next steps after assessment",
    start28ProgramCta: "Start 28-day program",
    retakeCta: "Retake assessment",
    ctaSrOnlyHeading: "Next steps after assessment",
  },
  landing: {
    heroTitle: "bty",
    heroSubtitle: "Where would you like to go today?",
    recommended: "Recommended",
    arenaTitle: "Arena",
    arenaDesc: "Play scenarios, make choices, grow. XP, weekly quests, leaderboard.",
    arenaCta: "Play",
    foundryTitle: "Foundry",
    foundryDesc: "Dashboard, mentor, integrity practice. Choose what to work on.",
    foundryCta: "Go to Foundry",
    centerTitle: "Center",
    centerDesc: "A safe space that reflects your feelings. No advice—just reflection.",
    centerCta: "Rest here",
    todayGrowthLink: "Today's growth",
    footerHint: "Choose a path above.",
    assessmentMainRegionAria: "Self-esteem 50-item assessment",
    assessmentErrorMainRegionAria: "Self-esteem assessment — error",
    assessmentSuspenseMainRegionAria: "Self-esteem assessment — loading",
    landingHubMainRegionAria: "BTY landing — Arena, Foundry, and Center entry",
  },
  safeMirror: {
    title: "Safe Mirror",
    subtitle: "Write how you feel. Here we don’t give advice—we reflect your feelings back.",
    placeholder: "Was there something you couldn't tell anyone today?",
    submit: "Write",
    submitting: "Reading your words…",
    fallbackReply: "I can't write back right now. It's enough that you wrote it down.",
  },
  smallWins: {
    title: "Small Wins",
    subtitle: "Record one small thing you did today. No need for big goals.",
    add: "Add",
    customPlaceholder: "Custom (e.g. drank a glass of water)",
    count: "Today’s small wins",
    suggested: ["Made the bed", "Drank water", "Opened a window", "Washed hands", "1 min stretch", "Ate a bite", "Washed face", "Brushed teeth"],
  },
  selfEsteem: {
    title: "Self-esteem check",
    subtitle: "A short conversation with yourself. Pick what feels closest. There are no wrong answers.",
    again: "Start over",
    choices: [
      { value: 1, label: "Almost never" },
      { value: 2, label: "Rarely" },
      { value: 3, label: "Sometimes" },
      { value: 4, label: "Often" },
      { value: 5, label: "Almost always" },
    ],
    questions: [
      "I am satisfied with what I’ve done.",
      "I generally feel okay about myself.",
      "I try to accept my flaws.",
      "I can respect myself even when I fail.",
      "I try to speak kindly to myself.",
    ],
    storyLabels: ["First story", "Second story", "Third story", "Fourth story", "Fifth story"],
    results: {
      high: "You’re already taking steps to be okay with yourself. That intention matters.",
      mid: "Some days we feel anxious or not enough. Here, it’s okay to just be as you are.",
      low: "It’s okay to be hard on yourself sometimes. That often means you care. You’re allowed to be here as you are.",
      strengthHigh: "self-caring and self-acknowledging",
      strengthMid: "gradually getting to know yourself",
      strengthLow: "still trying to hold yourself dear",
      scoreSuffix: " pts",
    },
  },
  chat: {
    title: "Chat",
    placeholder: "Type something…",
    send: "Send",
    thinking: "Thinking…",
    connectionError: "Connection failed or timed out. Please resubmit.",
    retry: "Retry",
    introFoundry: "I'm Dr. Chi. I'm here to help you practice seeing things from the other person's side. Start with a line or two whenever you're ready.",
    introCenter: "I'm Dr. Chi. However you feel right now is okay. This is a safe space. Share whatever you'd like.",
    spaceHintFoundry: "You're in Foundry (practice space). We'll focus on scenarios, choices, and role-play.",
    spaceHintCenter: "You're in Center (rest space). We'll focus on support and empathy. Share at your own pace.",
    introArena: "I'm Dr. Chi. We can work through scenario choices, trade-offs, and priorities. Share whenever you're ready.",
    spaceHintArena: "You're in Arena. We'll focus on simulation, choices, and reflection.",
    exampleLabel: "Try an example",
    examplePhrasesFoundry: [
      "What might the other person be thinking in this situation?",
      "What's one thing I could change in how I respond?",
      "One thing I could try saying next time?",
      "When values clash, what do I prioritize?",
    ],
    examplePhrasesCenter: [
      "I want to talk about what's been hardest lately.",
      "In one word, how I feel right now.",
      "One small moment that felt okay today.",
      "Just listening is enough.",
    ],
    examplePhrasesArena: [
      "If I could take one thing from this run, what would it be?",
      "I want to try using this choice in the next scenario.",
    ],
    rememberConversation: "Remember conversation",
    deleteHistory: "Delete history",
  },
  resilience: {
    title: "Recovery resilience",
    subtitle: "A wave that dips then rises again.",
    dailyTrajectorySubtitle: "Daily trajectory from your 5-item check-ins. Your recovery over time.",
    emptyMessage: "No trajectory yet. Complete today's steps or letter in Center to build it.",
    past: "Past",
    now: "Now",
    levelLow: "Low (energy 1–2)",
    levelMid: "Mid (energy 3)",
    levelHigh: "High (energy 4–5)",
    levelLegendAria: "Daily recovery level legend",
  },
  arenaLevels: {
    membershipPending: "Membership approval pending. Levels will show after approval.",
    noLevelsYet: "No records yet. Start your first scenario.",
    emptyCta: "Start a scenario in Arena",
    loginRequired: "Sign in required.",
    track: "Track",
    unlockedUpTo: "Unlocked up to",
    levelCardHint: "Unlocked up to here.",
    l4AdminGranted: "admin-granted",
    staff: "Staff",
    leader: "Leader",
    l4Partner: "L4 (Partner)",
  },
  avatarOutfit: {
    label: "Outfit",
    professional: "Professional",
    fantasy: "Fantasy",
    hint: "Choose from the outfit catalog. Unlock rules may follow Code or Tier progression later.",
    pageTitle: "Avatar",
    backToFoundry: "Back to Foundry",
    backToFoundryAria: "Back to Foundry",
    goToDashboard: "Go to Dashboard",
    goToDashboardAria: "Go to Dashboard",
    preview: "Preview",
    previewAria: "Avatar preview",
    character: "Character",
    characterLocked: "Character is locked until the next Code evolution.",
    characterChangeHint: "You can change your character on the dashboard.",
    outfit: "Outfit",
    noOutfits: "No outfits available.",
    save: "Save",
    saving: "Saving",
    saveAria: "Save",
    errorLoad: "Failed to load avatar settings.",
    outfitLabels: {
      outfit_1_basic_clinic_scrubs: "Basic Clinic Scrubs",
      outfit_2_assistant_utility_uniform: "Assistant Utility Uniform",
      outfit_3_hygiene_scrubs: "Hygiene Scrubs",
      outfit_4_standard_dental_coat: "Standard Dental Coat",
      outfit_5_clinic_utility_uniform: "Clinic Utility Uniform",
      outfit_6_digital_dentistry_coat: "Digital Dentistry Coat",
      outfit_7_imaging_technician_suit: "Imaging Technician Suit",
      outfit_8_surgical_scrubs: "Surgical Scrubs",
      outfit_9_orthodontic_specialist_coat: "Orthodontic Specialist Coat",
      outfit_10_pediatric_dentistry_uniform: "Pediatric Dentistry Uniform",
      outfit_11_implant_surgery_armor_scrubs: "Implant Surgery Armor Scrubs",
      outfit_12_ai_diagnostic_suit: "AI Diagnostic Suit",
      outfit_13_research_lab_coat: "Research Lab Coat",
      outfit_14_master_hygienist_uniform: "Master Hygienist Uniform",
      outfit_15_prosthetic_design_coat: "Prosthetic Design Coat",
      outfit_16_neurosight_clinical_suit: "Neurosight Clinical Suit",
      outfit_17_bty_arena_champion_uniform: "BTY Arena Champion Uniform",
      outfit_18_foundry_engineer_outfit: "Foundry Engineer Outfit",
      outfit_19_master_surgeon_robe: "Master Surgeon Robe",
      outfit_20_digital_dentistry_elite_coat: "Digital Dentistry Elite Coat",
      professional_outfit_scrub_general: "General scrub",
      professional_outfit_figs_scrub: "Figs scrub",
      professional_outfit_doctor_gown: "Doctor gown",
      professional_outfit_surgery_coat_suit: "Surgery coat + suit",
      professional_outfit_brand_suit: "Brand suit",
      professional_outfit_figs_scrub_short: "Short-sleeve Figs scrub",
      professional_outfit_shorts_tee: "Shorts & tee",
      fantasy_outfit_apprentice: "Apprentice",
      fantasy_outfit_adventurer: "Adventurer",
      fantasy_outfit_journeyer: "Journeyer",
      fantasy_outfit_warrior_mage_mid: "Warrior/Mage/Merchant mid",
      fantasy_outfit_senior: "Senior",
      fantasy_outfit_senior_plus: "Senior+",
      fantasy_outfit_master: "Master",
    },
    currentLevelAccessories: "Current level accessories",
    currentOutfitPrefix: "Current:",
    pickCharacterTitle: "Choose a character",
    dashboardCharacterLockedGridHint:
      "Your character is saved and locked. Below is the full character roster (selection disabled).",
    avatarSettingsMainRegionAria: "Avatar settings — character, outfit, accessories",
  },
  arenaMembership: {
    label: "Arena membership",
    approved: "Approved",
    pending: "Pending approval. Levels will show after admin approval.",
    hint: "Enter job function and join date. Admin approval unlocks Arena levels.",
    jobFunction: "Job function",
    joinedAt: "Join date",
    leaderStartedAt: "Leader start date (optional, for leader roles)",
    submit: "Submit",
    submitting: "Submitting…",
    submitError: "Submission failed.",
    submitSuccess: "Pending approval. Levels will show after admin approval.",
    seniorDoctorHint: "Senior Doctor is typically 3+ years of experience.",
    validationRequired: "Please enter job function and join date.",
  },
  mentorRequest: {
    cardTitle: "Request 1:1 session with Dr. Chi",
    cardDesc: "Available for Elite (top 5%) only. After approval, continue the conversation on the Mentor page.",
    requestCta: "Request session",
    submitting: "Submitting…",
    statusPending: "Pending approval.",
    statusApproved: "Approved. Continue the conversation on the Mentor page.",
    statusRejected: "Not approved this time. You can request again later.",
    messageLabel: "Message (optional)",
    messagePlaceholder: "e.g. I'd like feedback on leadership",
    errorEliteOnly: "Only available when you're in the top 5%.",
    errorPendingExists: "You already have a pending request.",
    errorSubmit: "Request failed. Please resubmit.",
    approvedCta: "Go to Mentor page",
    listTitle: "My requests",
    listEmpty: "No requests yet.",
    colDate: "Requested at",
    colStatus: "Status",
    slaImminentBadge: "Mentor response window closing soon — you may hear back shortly.",
    eliteDomainTerminalApproved: "Mentor request · Approved",
    eliteDomainTerminalRejected: "Mentor request · Declined",
    eliteDomainPendingStale: "It’s been over two weeks since you applied. We’re still waiting on a response.",
    eliteStatusBadgePending: "Mentor request · Pending",
    eliteStatusBadgeApproved: "Mentor request · Approved",
    eliteStatusBadgeRejected: "Mentor request · Declined",
  },
  elitePage: {
    circleCardTitle: "Circle meetup",
    circleCardDesc: "Elite-only small-group meetups (e.g. weekly or monthly). Check below for schedule and how to join.",
    circleCardPlaceholder: "Coming soon",
    badgeLabels: { weekly_elite: "Weekly Top 5%" },
    unlockConditionTitle: "Unlock condition",
    unlockConditionMet: "Top 5% on the weekly leaderboard (met).",
    unlockConditionLocked: "Available when you reach the top 5% on the weekly leaderboard.",
    unlockExposureTitle: "What's available",
    unlockExposureMet: "You can access Elite-only content (mentor session request, circle meetup, badges, etc.).",
    unlockExposureLocked: "Elite page and content available when you reach top 5%.",
    mentorDeepLinksListAria: "Elite mentor and deep conversation links",
    loadingElitePageAria: "Loading Elite page",
    elitePageMainRegionAria: "Elite only — unlock condition, badges, mentor request",
    pageTitle: "Elite only",
    pageIntroElite:
      "You're in the top 5% on the weekly leaderboard. Here you can access Elite-only content.",
    badgesSectionTitle: "Badges",
    tableColActions: "Actions",
  },
  leadershipMetricsAdmin: {
    networkError: "Network error",
    title: "Leadership Metrics",
    computedPrefix: "Computed:",
    refresh: "Refresh",
    loading: "Loading data…",
    emptyAir: "No Action Contract data.",
    emptyStage: "No Leadership Engine State data.",
    emptyMwd: "No Micro Win activation data.",
    emptyTii: "No TII snapshot data.",
    emptyTeamWeekly: "No team_weekly_metrics snapshot data.",
    colEmail: "Email",
    colStatus: "Status",
    colDoneSelected: "Done / Selected",
    colIncomplete: "Incomplete",
    colLastActivity: "Last activity",
    colStageEntry: "Stage entry",
    colDaysElapsed: "Days elapsed",
    colMwd7: "MWD 7d",
    colMwd14: "MWD 14d",
    colDoneTotal: "Done / Total",
    colCompletionRate: "Completion rate",
    colLeagueTeam: "League / Team",
    colMembers: "Members",
    colBaseWeek: "Base week",
    resetBadge: "Reset",
    daysSuffix: "d",
    airBandHigh: "High",
    airBandMid: "Mid",
    airBandLow: "Low",
    glAir: "= completed actions ÷ selected actions (excluding draft)",
    glDoneCriteriaLabel: "Done criteria",
    glDoneCriteria: ": approved / completed / DONE, or verified_at present",
    glIntegritySlip: ": 3 consecutive missed windows",
    glTsp: ": TSP (weekly survey score)",
    glMwd: ": count of unique days with at least one completed Micro Win",
    glMwdWindow: ": last 7 days / 14 days",
    glStageCertifiedLabel: "Certified Leader",
    colCertified: "Certified",
    colCertifiedYes: "Certified",
    colCertifiedNo: "Not yet",
    glStageCertifiedBody:
      ": certified when all 4 gates pass — 14-day AIR ≥ 80%, MWD at/above threshold, reset compliance met, no integrity_slip in 14 days. Re-evaluated quarterly and weekly; auto-cleared when any gate fails (recomputed — no separate revocation step). Distinct from Arena Elite (weekly top 5%).",
    glStageLriLabel: "LRI",
    glStageLriBody:
      ": non-leader readiness. (14-day AIR × 50%) + (MWD × 30%) + (pulse × 20%). LRI ≥ 0.80 and no integrity_slip → readiness. (Differs from the team metric TII at 60/25/15.)",
    glStageResetLabel: "Forced Reset",
    glStageResetBody:
      ": Stage 4 (Integrity Reset) forced trigger — fires when any two hold: Stage 3 selected twice in 14 days · AIR_7d below 80% for 2 consecutive weeks · no QR verification for 7 days · TSP declining for 2 consecutive weeks. May be delayed up to 48h (not permanently dismissable); returns to Stage 1 on completion.",
  },
  mentorRequestAdmin: {
    mainRegionAria: "Mentor request approval — admin",
    title: "Mentor session request approval",
    description: "Elite mentor (1:1) request queue. Approve or reject.",
    empty: "No pending requests.",
    loading: "Loading…",
    actions: "Actions",
    userId: "User ID",
    createdAt: "Requested at",
    message: "Message",
    colStatus: "Status",
    tableCaption: "Pending Elite mentor session requests",
    queueTableAria: "Mentor request queue table",
    approveRejectGroupAria: "Approve or reject this request",
    statusPendingLabel: "Pending",
    approve: "Approve",
    reject: "Reject",
    approving: "Approving…",
    rejecting: "Rejecting…",
    error: "Action failed",
    errorLoadQueue: "Could not load the queue.",
    errorPatch: "Approve or reject failed.",
    debugLink: "Debug",
    usersLink: "Users",
    arenaMembershipLink: "Arena membership",
  },
  adminUsers: {
    mainRegionAria: "User accounts — admin",
    networkError: "Network error",
    errLoadList: "Could not load the user list.",
    errCreateFailed: "Failed to create user.",
    errDeleteFailed: "Failed to delete user.",
    errPasswordFailed: "Failed to change password.",
    validateCreate: "Enter an email and a password (6+ characters).",
    validatePassword: "Password must be at least 6 characters.",
    deleteConfirm: "Delete user {email}? Their account and all related data will be removed and this cannot be undone.",
    passwordChanged: "Password changed.",
    title: "User management",
    description: "Create, edit, and delete regular user accounts.",
    cancel: "Cancel",
    createUser: "Create user",
    createFormTitle: "Create new user",
    labelEmail: "Email",
    labelPassword: "Password",
    passwordPlaceholder: "6+ characters",
    creating: "Creating…",
    create: "Create",
    emptyUsers: "No users registered.",
    colCreatedAt: "Created",
    colActions: "Actions",
    newPasswordPlaceholder: "New password",
    save: "Save",
    changePassword: "Change password",
    delete: "Delete",
    noteTitle: "⚠️ Notes",
    note1: "User management uses Supabase Auth.",
    note2: "List, create, delete, and password change are also available in the Supabase dashboard.",
  },
  adminQuality: {
    mainRegionAria: "Quality events — admin",
    networkError: "Network error",
    subtitle: "Quality Events dashboard",
    refresh: "Refresh",
    loading: "Loading quality data…",
    dbOk: "Connected",
    dbDown: "Disconnected",
    lastPrefix: "Last:",
    events30dPrefix: "30-day events:",
    summaryTitle: "Event summary",
    windowPrefix: "Window:",
    totalEventsPrefix: "Total events:",
    avgCssPrefix: "Avg CSS:",
    emptyEvents: "No event data. Shown once the bty-ai-core backend is connected.",
    topSignaturesTitle: "Top issue signatures",
    breakdownRoute: "Route distribution",
    breakdownRole: "Role distribution",
    breakdownIntent: "Intent distribution",
  },
  adminOrganizations: {
    mainRegionAria: "Organizations — admin",
  },
  adminArenaMembership: {
    mainRegionAria: "Arena membership approval — admin",
    title: "Arena membership approval",
    description:
      "Review pending requests, then approve. On approval, the user's tenure (join date / leadership start date) is applied.",
    emptyNoPending: "No pending requests.",
    colJobFunction: "Job function",
    colJoinedAt: "Joined",
    colLeaderStartedAt: "Leadership start",
    colRequestedAt: "Requested",
    colActions: "Actions",
    approving: "Processing…",
    approve: "Approve",
    approveAria: "Approve membership",
  },
  adminLogin: {
    mainRegionAria: "Admin sign-in",
    heading: "Admin sign-in",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    loginSubmit: "Sign in",
    signingIn: "Signing in…",
    loginSubmitAriaBusy: "Signing in",
    loginSubmitAriaIdle: "Submit admin sign-in",
    loginFailed: "Sign-in failed.",
  },
  adminHub: {
    mainRegionAria: "Admin area — redirecting",
    redirecting: "Taking you to admin…",
  },
  mentorPage: {
    deleteAllHistoryConfirm: "Delete all saved mentor conversation?",
    pageMainLandmarkAria: "Dr. Chi mentor conversation",
    topicChoiceGroupAria: "Choose a conversation topic",
  },
  dentalRpg: {
    empty: "No equipment.",
    listAria: "Equipment list",
    rarity1: "Common",
    rarity2: "Uncommon",
    rarity3: "Rare",
    rarity4: "Epic",
    rarity5: "Legendary",
  },
  healing: {
    title: "Healing",
    intro: "Entry point for Healing and Awakening. Continue to Awakening for the Second Awakening ritual.",
    awakeningCta: "Awakening →",
    dashboardCta: "← Dashboard",
    navLabel: "Healing menu",
    ariaAwakening: "Go to Awakening",
    ariaDashboard: "Back to dashboard",
    loading: "Loading…",
    emptyPhase: "Phase information is unavailable. Try going to Awakening.",
    loadError: "Could not load progress. Please wait and resubmit.",
    awakeningLoadingLabel: "Loading Second Awakening",
    awakeningMainRegionAriaError: "Second Awakening error",
    awakeningMainRegionAriaCompleted: "Second Awakening completed",
    awakeningMainRegionAriaEligibility: "Second Awakening eligibility",
    awakeningMainRegionAriaRitual: "Second Awakening ritual",
    phaseProgressRegionAria: "Healing phase and progress",
    awakeningRitualActsRegionAria: "Second Awakening ritual acts 1–3",
    healingBottomNavSectionAria: "Awakening and dashboard links",
    mainLandmarkAria: "Healing hub",
    stubApiSectionTitle: "Server progress (display only)",
    phaseFieldLabel: "Phase",
    ringTypeFieldLabel: "Ring type",
    stubRenderOnlyNote: "Values come from the API; the UI does not compute phase.",
    awakeningActsTrackTitle: "Awakening acts (server record)",
    awakeningActsTrackRegionAria: "Awakening act list and completion record",
    awakeningActsTriggerLine: "The ritual unlocks as you progress.",
    awakeningActDone: "Recorded",
    awakeningActOpen: "Not yet recorded",
    awakeningRecordNextCta: "Record next act complete",
    awakeningAllActsRecorded: "All three acts are recorded.",
    awakeningProgressToastOk: "Progress saved.",
    awakeningProgressToast409: "This act was already recorded.",
    awakeningProgressToast400: "Wrong order or invalid request.",
    awakeningProgressToastNetwork: "Could not save. Resubmit.",
    awakeningActsLoadError: "Could not load act info.",
    healingAwakeningProgressHeading: "Awakening progress (server)",
    healingAwakeningProgressPct: "Progress {n}%",
    healingProgressbarValuetext: "{pct} percent. {detail}",
    healingProgressbarDetailAllDone: "All acts are complete.",
    healingProgressbarDetailNext: "Next act: {name}.",
    healingProgressbarDetailSync: "Next act appears when synced with the server.",
    healingAwakeningAllActsDone: "All three acts are recorded.",
    healingNextActFromApi: "Next act (server): {name}",
    healingNextActSyncHint:
      "If records are out of order, complete acts in sequence below.",
    healingProgressRefreshHint:
      "If the list doesn’t update after recording, refresh server state below.",
    healingProgressRefreshCta: "Refresh progress",
    awakeningActsGridAria: "Awakening acts 1–3 grid",
    awakeningActNumberLabel: "Act {n}",
    healingAwakeningBlockedOrderRequired: "Complete the previous act in order first.",
    healingAwakeningBlockedAlreadyComplete: "This act is already recorded.",
    healingActLockPrerequisite: "Complete the previous act in order first.",
    healingActLockAlreadyComplete: "This act is already recorded.",
    pathProgressBlockedCooldown: "Please wait a moment, then resubmit. (Cooldown)",
    pathProgressBlockedPhase: "Complete the previous step first to continue.",
    healingActsOverallProgressAria: "Awakening acts completion progress",
    healingActsOverallProgressCaption: "Acts recorded",
    healingActsOverallProgressValuetext: "{done} of {total} acts recorded, {pct} percent",
    healingPhaseTrackerRegionAria: "Healing journey — four steps",
    healingPhaseTrackerTitle: "Healing journey phases",
    healingPhaseStep1: "Acknowledgment",
    healingPhaseStep2: "Reflection",
    healingPhaseStep3: "Re-integration",
    healingPhaseStep4: "Renewal",
    healingPhasePrompt1:
      "Complete the self-esteem assessment to acknowledge where you are. (At least one Center 50-item submission.)",
    healingPhasePrompt2:
      "Write a Dear Me letter to reflect in words. (Assessment + at least one letter.)",
    healingPhasePrompt3:
      "Go to the Healing hub and tap 'Record Next Act' to complete the first ritual — Reflection Chamber.",
    healingPhasePrompt4:
      "Go to the Healing hub and record the remaining two acts — Transition, then Awakening — in order.",
    healingPhaseAdvanceCta: "Continue to next phase",
    healingPhaseAdvancing: "Continuing…",
    healingPhaseTrackerLoadError: "Could not load phase information.",
    healingPhaseAdvanceError: "Could not advance. Check that requirements are met.",
    healingPhaseInputsHint:
      "Summary: {a} assessment(s) · {l} letter(s) · {aw} act(s) recorded (no individual scores shown)",
  },
  train: {
    title: "28-Day Training",
    completeGroupLabel: "Complete and coach or summary toggle",
    completionSummaryLabel: "Completion summary",
    coachChatLabel: "Coach chat",
    reinforcementLabel: "Reinforcement questions",
    dayListLabel: "Day list",
    lessonLabel: "Today's lesson",
    lockedLabel: "Locked lesson notice",
    sidebarPanelLabel: "Completion summary and coach chat",
    journeyStartTitle: "Start 28-day training",
    journeyStartIntro: "Pick a day on the left for the lesson; chat with the coach on the right.",
    journeyStartDay1Link: "Start from Day 1",
    journeyStartDay1Aria: "Start from Day 1",
    journeyStartMainRegionAria: "28-day training start",
    track28HubMainRegionAria: "28-day training hub",
    track28DayMainRegionAria: "28-day training lesson",
    trainRouteSuspenseMainRegionAria: "28-day training — loading",
  },
  dojoResult: {
    title: "Today-Me 50-Item Result",
    areaScoresTitle: "Scores by area",
    drChiCommentTitle: "Dr. Chi comment",
    resultCommentHigh: "You're doing well in perspective-taking, communication, and leadership. Keep building on these strengths.",
    resultCommentMid: "Some areas are already strong; others have room to grow. Pick one area at a time to practice.",
    resultCommentLow: "This is your starting point. Repeating the Foundry integrity practice and this assessment will help you grow step by step.",
    loading: "Loading result…",
    noAnswers: "Complete the 50 questions to see your result.",
    backToAssessment: "Go to assessment",
    apiError: "Could not load result. Please wait and resubmit.",
    dimensionLabels: {
      core: "Self-worth",
      compassion: "Self-compassion",
      stability: "Stability",
      growth: "Growth",
      social: "Relations",
      perspective_taking: "Perspective-taking",
      communication: "Communication & listening",
      leadership: "Leadership & responsibility",
      conflict: "Conflict & negotiation",
      teamwork: "Team & collaboration",
    },
    nextActionsLabel: "Next actions",
    resultActionsLabel: "Result actions",
    questionStepSectionAria: "Current question and response options",
    resultScoresInsightRegionAria: "Scores by area and comment",
    dojoPageMainAria: "Dojo 50-item assessment",
    dojoHistoryMainRegionAria: "Dojo past assessment history — submission date and summary list",
    dojoResultMainRegionAria: "Dojo 50-item result — summary, area scores, next actions",
  },
  arenaRun: {
    reflectionPrompt: "In one sentence: what will you take from this scenario?",
    errorStartRun: "Could not start run.",
    errorSignIn: "Please sign in.",
    errorAlreadySubmitted: "Already submitted or session expired.",
    errorSubmitFailed: "Submission failed. Please resubmit.",
    heroTitle: "One step today, in the Arena.",
    otherLabel: "Other (Write your own)",
    otherRecorded: "Other recorded.",
    nextScenario: "Next scenario",
    otherPlaceholder: "Write your own...",
    cancel: "Cancel",
    submit: "Submit",
    liveRanking: "Live ranking",
    completeErrorPrefix: "Save failed: ",
    completeErrorSuffix: ". You may not appear on the leaderboard.",
    headerTitle: "Simulation",
    headerSubtitle: "One round. Resumes when you return. (MVP 1)",
    pauseLabel: "Pause",
    resetLabel: "Reset",
    mainLabel: "Main",
    headerIdentityLinkAria: "Open profile and avatar",
    startSimulation: "Start Simulation",
    confirm: "Confirm",
    reflectionTitle: "Reflection",
    reflectionNext: "Next",
    reflectionOptional: "Optional",
    reflectionPlaceholder: "e.g. I'll pause before reacting next time.",
    systemOutput: "SYSTEM OUTPUT",
    skipFollowUp: "Skip follow-up · Next scenario",
    followUpSelected: "FOLLOW-UP SELECTED",
    step6Title: "Step 6 · Consolidation",
    arenaRunPageMainRegionAria: "Arena mission run — scenario hub, choices, reflection, history",
    preparingNewScenarioAria: "Preparing new scenario",
    scenarioProgressPanelAria: "Scenario progress and result",
    reflectionBusyAria: "Submitting reflection",
    reflectionSentenceBlockAria: "One-sentence reflection",
    reflectionChoiceOptionsAria: "Reflection choices",
    reflectionLengthHintAria: "Reflection length hint",
    reflectionCharCount: "{n} / {max} characters",
    youChose: "You chose",
    keyInsight: "Key insight:",
    principle: "Principle: \"Stabilize people first, then fix the system.\"",
    completeBtn: "Complete",
    reflectionBonusLabel: "Reflection bonus",
    deepeningTitle: "Reflection deepening",
    nextActionLabel: "Next step",
    step7Title: "Step 7 · Complete",
    step7Body: "Saved. Use the button below to go to the next scenario.",
    choiceLabel: "Choice",
    scenarioCompletedToast: "Selection recorded.",
    scenarioNotFound: "Scenario not found.",
    scenarioNotFoundHint: "Try another scenario or refresh.",
    pastScenariosHeading: "Past scenarios",
    pastScenariosCollapse: "Collapse",
    pastScenariosExpand: "Expand",
    loadingHistory: "Loading history…",
    couldNotLoadHistory: "Could not load history.",
    pastScenariosSessionHint:
      "Your session may not have reached this request. Try signing in again from bty home or refresh the page to see past runs.",
    retryLoadHistory: "Retry load history",
    retry: "Retry",
    noCompletedScenarios: "No completed scenarios yet.",
    startFirstScenario: "Start your first scenario!",
    completed: "Completed",
    inProgress: "In progress",
    reflectDeepeningUnavailable:
      "We couldn’t load the deeper reflection right now. Your reflection was saved.",
    runPageLevelCheckMainRegionAria: "Mission run — checking level and session",
    runPageBeginnerPathMainRegionAria: "Redirecting to beginner path",
    runPageNoScenarioMainRegionAria: "Mission run — no scenario",
    arenaRunErrorMainRegionAria: "Arena run — error",
    arenaRunErrorTitle: "Something went wrong",
    arenaRunErrorDescription:
      "We couldn’t load the Arena run. Check your connection and resubmit.",
    arenaRunErrorHint: "If this keeps happening, refresh the page.",
    eliteSetupRoleLabel: "Your role",
    eliteSetupPressureLabel: "Pressure",
    eliteSetupTradeoffLabel: "The dilemma",
    eliteInitialDecisionHint:
      "Pick one option below. Your selection advances the run.",
    elitePrimaryPickHint: "Tap one option to record your primary decision.",
    eliteOpeningNarrativeLabel: "Situation",
    eliteRunCompletePrepareHint:
      "Continue to sync with the server. You may need to complete an action next—not necessarily the next scenario immediately.",
    elitePostFlowRegionAria: "Elite arena — follow-on steps",
    elitePostConfirmTitle: "Primary choice recorded",
    elitePostConfirmLead: "This round’s primary choice is saved. Continue when ready.",
    elitePostConfirmNext: "Continue",
    elitePostExplainTitle: "Why this choice?",
    elitePostExplainPrompt: "In a sentence or two, why does this choice fit here? (Optional)",
    elitePostExplainOptionalLabel: "A short note is enough",
    elitePostExplainPlaceholder: "e.g. Credibility matters more than speed right now.",
    elitePostExplainSubmit: "Continue",
    elitePostIntegrationTitle: "Leadership integration",
    elitePostIntegrationNext: "Continue",
    elitePostBridgeTitle: "Forward",
    elitePostBridgeDefault:
      "In your next conversation, meeting, or decision, try one deliberate move from what you just named.",
    elitePostBridgeNext: "Finish round",
    elitePostCompleteTitle: "Round complete",
    elitePostCompleteBody: "You are set. Continue to the next scenario when you are ready.",
    elitePostXpLine: "This round: +{xp} XP recorded",
    eliteMirrorTitle: "Pattern Mirror",
    eliteMirrorLead:
      "Below is pattern exit data for this run and accumulated pattern-window state as recorded. No interpretation or evaluation.",
    eliteMirrorEmpty: "No prior-run data is available to display.",
    eliteMirrorContinue: "Continue",
    eliteMirrorLoadError: "Could not load mirror data.",
    eliteSituationUpdateHeader: "SITUATION UPDATE",
    eliteDecisionRequiredHeader: "DECISION REQUIRED",
    eliteRunStepAdvanceError: "Could not record this step. Please retry in a moment.",
    eliteBindingIntegrityError:
      "Scenario binding data is missing or invalid. Refresh the page or restart the arena.",
    eliteForcedTradeoffCostLabel: "COST",
    eliteTradeoffProtectsLabel: "What this leans toward",
    eliteTradeoffRisksLabel: "What this risks",
    arenaFlowPhasePrimaryInstruction: "First, choose how you respond under pressure.",
    arenaFlowPhaseTradeoffInstruction: "Now face the cost of that response. Neither option is free.",
    arenaFlowPhaseActionDecisionInstruction: "Now choose the observable action. This is not interpretation.",
    eliteActionDecisionAdCommitmentBadge: "Observable action",
    eliteActionDecisionAdDeferBadge: "Delay / non-commitment",
    eliteActionDecisionAd1TraitConcrete: "A concrete, real-world move (not vibes alone)",
    eliteActionDecisionAd1TraitTimeBound: "Time-bound — happens in a defined window",
    eliteActionDecisionAd1TraitObservable: "Observable by others",
    eliteActionDecisionAd1TraitContractGate: "Progress depends on completing it — opens the action-contract gate",
    eliteActionDecisionAd2TraitDelay: "Defers locking a move today",
    eliteActionDecisionAd2TraitNonCommitment: "Keeps interpretation open longer",
    eliteActionDecisionAd2TraitNextOrReexposure:
      "May proceed without a contract — next scenario or no-change re-exposure risk",
    eliteActionDecisionTitle: "Action decision",
    eliteActionDecisionLead: "Not interpretation — what you will actually do next.",
    eliteActionDecisionCommitmentLabel: "What this means",
    eliteActionDecisionRegionAria: "Action decision",
    eliteV2ErrorNotConfiguredTitle: "Escalation data missing",
    eliteV2ErrorNotConfiguredBody:
      "This scenario has no escalationBranches configured. Contact support or pick another scenario.",
    eliteV2ErrorMissingBranchTitle: "No escalation branch for this choice",
    eliteV2ErrorMissingBranchBody:
      "escalationBranches has no entry for your primary choice id. The scenario data must be fixed.",
    eliteV2ErrorInvalidCostTitle: "Invalid second-choice cost",
    eliteV2ErrorInvalidCostBody:
      "Each second_choices item must include a non-empty cost. Check the scenario dataset.",
    eliteInvalidPrimaryChoiceTitle: "Primary choice out of sync",
    eliteInvalidPrimaryChoiceBody:
      "The saved choice id is not in this scenario’s choice list. Refresh the page or restart the arena.",
    eliteRunCompleteLine: "This run is complete.",
    eliteRunCompleteRegionAria: "Run complete",
    eliteRunCompleteLoading: "Loading…",
    eliteRunCompleteContinueCta: "Continue",
    arenaPendingContractTitle: "Action contract in progress",
    arenaObservableActionConfirmationLead:
      "You chose an observable action. Progress now depends on completing it.",
    arenaPendingContractLead:
      "You cannot start the next Arena scenario until you finish the open action contract on My Page. Review the details below, complete it there, then return.",
    arenaPendingContractActionLabel: "Action",
    arenaPendingContractDeadlineLabel: "Deadline",
    arenaPendingContractVerificationLabel: "Verification",
    arenaPendingContractRetry: "Check again",
    arenaPendingContractMyPageCta: "Continue on My Page",
    arenaPendingContractRegionAria: "Action contract pending",
    arenaActionCompletedTitle: "Action reported",
    arenaActionCompletedLead: "Your XP has been applied.",
    arenaActionCompletedNextCta: "Next scenario",
    arenaPulseQuestion: "In this situation, did I try to take responsibility without avoiding it?",
    arenaPulseLow: "Not at all",
    arenaPulseHigh: "Fully",
    arenaPulseSkipCta: "Skip",
    arenaPulseThanks: "Recorded.",
    arenaSnapshotForcedResetPlaceholder: "Syncing account state. Please refresh shortly.",
    arenaForcedResetGateTitle: "Center step required",
    arenaForcedResetGateLead: "Arena is paused. Complete the guided step in Center, then return.",
    arenaForcedResetGoCenterCta: "Open Center",
    arenaSnapshotReexposurePlaceholder: "Preparing the next step. Please wait.",
    arenaReexposurePanelTitle: "Re-exposure round",
    arenaReexposurePanelDescription:
      "A delayed outcome linked to an earlier choice is ready. Enter the scenario to continue.",
    arenaReexposurePanelBeginButton: "Enter scenario",
    arenaReexposureBlockedNextTitleV2: "Pattern validation is required before the next scenario",
    arenaReexposureBlockedNextDescriptionV2:
      "Re-exposure is a validation stage to confirm pattern stability on the same axis, not a punishment step.",
    arenaReexposureBlockedNextButtonV2: "Load Next Scenario",
    arenaReexposureInternalStatusInterventionSensitivityUp:
      "Pattern validation sensitivity is adjusted. The next validation stays on the same axis.",
    arenaReexposureValidationChanged: "Pattern validation update: changed",
    arenaReexposureValidationUnstable: "Pattern validation update: unstable",
    arenaReexposureValidationNoChange: "Pattern validation update: no_change",
    arenaReexposureStaleRecoveryMessage: "Re-exposure state expired. Refreshing your next scenario.",
    arenaSnapshotPlaySurfaceBlockedHint: "Aligning your session with the server. Refresh shortly.",
    arenaPlaySurfaceBlockedTitle: "Play paused",
    arenaAligningLoaderTitle: "Aligning with the server",
    arenaAligningLoaderLead: "Please wait — this is synchronization, not an error.",
    arenaGateLabelContract: "Action contract",
    arenaGateLabelReexposure: "Re-exposure",
    arenaGateLabelReset: "Reset",
    arenaGateLabelNext: "Next scenario",
    arenaGateLabelPlay: "Play",
    arenaGateLabelAligning: "Aligning",
    arenaSnapshotNextScenarioReadyTitle: "Next scenario ready",
    arenaSnapshotNextScenarioReadyHint: "Loading your next session…",
    arenaPostContractNextScenarioTitle: "Execution recorded. Continue to the next scenario.",
    arenaPostContractNextScenarioHint: "Your submitted contract no longer blocks progression—same as mobile.",
    arenaContinueToNextScenarioCta: "Continue",
    eliteContractTitle: "Action Contract",
    eliteContractIntro:
      "These four fields lock a commitment someone could verify. Keep it close, specific, and fair—about the move, not a verdict on anyone’s character.",
    eliteContractWhoLabel: "Who is one specific person affected by this pattern?",
    eliteContractWhoHint: "Use a real name.",
    eliteContractWhatLabel: "What is one conversation you have been avoiding with this person?",
    eliteContractWhatHint: "Describe the situation, not the person.",
    eliteContractHowLabel: "What is your first sentence?",
    eliteContractHowInstruction: "Write exactly what you will say. No explanation.",
    eliteContractHowResponsibilityLead: "Start with your honesty or responsibility, not accusation.",
    eliteContractWhenLabel: "When will you start this conversation?",
    eliteContractWhenHint: "Choose a near, real time.",
    eliteContractWhoPlaceholder: "e.g., Jordan Park",
    eliteContractWhatPlaceholder: "e.g., the timeline slip we have not named directly",
    eliteContractHowPlaceholder: "e.g., “I’d like 15 minutes today to align on the handoff.”",
    eliteContractWhenPlaceholder: "e.g., today after standup, or Thursday 9am",
    eliteContractSubmit: "Submit contract",
    eliteContractSubmitting: "Submitting…",
    eliteContractLoadError: "Submit failed.",
    eliteContractEnsureFailed: "Could not initialize the contract. Retry shortly.",
    eliteContractCreating: "Preparing your action contract…",
    eliteContractInitFailedTitle: "Action contract unavailable",
    eliteContractPatternThresholdTitle: "Action contract not open yet",
    eliteContractPatternThresholdBody:
      "Qualifying exits for the same pattern family have not reached the threshold yet. This is expected—not an error. You can continue to the execution gate.",
    eliteContractPatternThresholdContinue: "Continue to execution gate",
    eliteContractRevisionRequired: "Revision required. Update the contract fields below and submit again.",
    actionContractFollowupTitle: "Execution check-in",
    actionContractFollowupLead: "A short check-in tied to your contract. This is not scoring or coaching.",
    actionContractFollowupQuestion: "Did you start the conversation?",
    actionContractFollowupYes: "Yes",
    actionContractFollowupNo: "No",
    actionContractFollowupFirst30Label: "What happened in the first 30 seconds?",
    actionContractFollowupFirst30Placeholder: "Say what happened, in your own words.",
    actionContractFollowupSubmit: "Submit",
    actionContractFollowupSubmitting: "Submitting…",
    actionContractFollowupError: "Could not submit. Please retry shortly.",
    eliteExecutionGateTitle: "Execution Gate",
    eliteExecutionGateIntro:
      "Verification is recorded according to procedure. Accepted evidence types and where to submit follow product policy. Your contract text below is shown exactly as authored.",
    eliteExecutionContractHeading: "Submitted contract text",
    eliteExecutionVerificationRecorded: "Verification is on file.",
    eliteExecutionAwaitingVerification: "Awaiting verification outcome.",
    eliteExecutionVerificationNotRecorded: "No verification on file.",
    eliteExecutionUnderReview: "Under review.",
    eliteExecutionAdditionalConfirmation: "Additional confirmation required.",
    eliteExecutionAbandonedLead: "Execution verification is still pending.",
    eliteExecutionResumeHint:
      "To close this run, continue evidence submission or the verification path. Your commitment text remains as written below.",
    eliteExecutionRawTextFallback: "(No contract text)",
    eliteExecutionNextBlocked: "A recorded verification is required before moving to the next scenario.",
    eliteExecutionLoadError: "Could not load execution state.",
    eliteExecutionPatternThresholdDeferLine:
      "A draft action contract opens when your pattern window meets the threshold. You can finish this run without submitted contract text for now.",
  },
  emotionalStats: {
    emptyMessage: "No records yet. Try Arena or chat to start.",
    sectionTitle: "Today's growth",
    errorLoad: "Could not load display.",
  },
  profile: {
    title: "Profile",
    displayNameLabel: "Display name",
    displayNamePlaceholder: "Nickname or name to show (optional)",
    save: "Save",
    saving: "Saving…",
    backToDashboard: "Back to dashboard",
    errorLoad: "Failed to load profile.",
    errorSave: "Failed to save.",
    errorTooLong: "Display name must be 64 characters or less.",
    avatarSettingsLink: "Avatar settings",
    saveButtonAriaLabel: "Save display name",
    profileMainRegionAria: "Profile, display name and avatar settings",
  },
  uxPhase1Stub: {
    bottomNavArena: "Arena",
    bottomNavGrowth: "Growth",
    bottomNavMyPage: "My Page",
    growthTitle: "Growth",
    growthSubtitle: "Interpretation · recovery",
    growthLead: "Rebuild your internal state.",
    growthReturnArena: "Arena hub",
    growthWeeklyRankingLink: "Weekly ranking",
    growthFoundryLink: "Foundry dashboard",
    growthOptionsRegionAria: "Growth area shortcuts",
    wireframeLandmarkAria: "Arena wireframe — Phase 1 stub screen",
    wireframeCtaRegionAria: "Scenario run entry — continue or start new",
    wireframeBottomNavAria: "BTY app tab bar — Arena, Growth, My Page",
    weeklySnapshotRegionAria: "Weekly rank snapshot sample",
    wireframeScreenTitle: "bty arena",
    wireframeScreenSubtitle: "Wireframe · Phase 1",
    wireframeSystemReady: "System ready.",
    wireframePlayGame: "Play Game",
    wireframeStartScenario: "Start new scenario",
    wireframeContinue: "Continue",
    wireframeResumeLast: "Resume last simulation",
    wireframeOr: "or",
    wireframeWeeklyRankLine: "Weekly Rank: #128",
    wireframeSeasonLine: "Season ends in 12 days",
    wireframeWireRuleNote:
      "Sample values · AIR, TII, LRI not shown on this screen (wireframe rules).",
    wireframeResultStubPrefix: "Result screen stub:",
    resultTitle: "Simulation Complete",
    resultSubtitle: "Arena · Result",
    resultCoreXpSample: "+25 Core XP",
    resultWeeklyXpSample: "+15 Weekly XP",
    resultSampleNote: "Sample rewards · live data after API wiring",
    resultSystemTitle: "System message",
    resultSystemBody: "System detected improved decision consistency.",
    resultContinueCta: "Continue",
    resultReturnArenaCta: "Return to Arena",
    resultWireHubLink: "Arena wireframe hub",
    growthArenaPlayLink: "Play Arena",
    growthArenaWireHubLink: "Arena hub (wireframe)",
    growthShortcutsNavAria: "Ranking, dashboard, Arena, and Healing shortcuts",
    growthElitePageLink: "Elite (top 5%)",
    wireframeFoundryEliteAria: "Foundry and Elite shortcuts",
    growthHealingLink: "Healing",
    growthAwakeningLink: "Awakening",
    resultRunCompleteHint:
      "After a run, tap “Next scenario” for weekly XP and leaderboard credit.",
    resultShareRegionAria: "Share completion summary",
    resultShareCta: "Copy one-line summary",
    resultShareCopied: "Copied.",
    resultShareFailed: "Could not copy.",
    resultShareClipboardLine: "Completed an Arena scenario run on BTY.",
    resultNextActionsRegionAria: "Next steps after your run",
    resultNextScenarioSectionLabel: "Recommended next step",
    resultNextScenarioCta: "Next scenario",
    resultNextScenarioSub: "Keep playing to earn weekly XP and leaderboard credit.",
    wireframeArenaPracticeNavAria: "Dojo and Integrity practice entry",
    wireframeDojoLink: "Dojo 50",
    wireframeIntegrityLink: "Integrity Mirror",
    wireframeRunsDashboardHint: "Recent runs are listed on the Foundry dashboard.",
    growthMyRankCardTitle: "Your rank this week",
    growthMyRankRegionAria: "Weekly leaderboard rank and weekly XP",
    growthMyRankLoading: "Loading rank…",
    growthMyRankYourRank: "Your rank: #{rank}",
    growthMyRankWeeklyXpLine: "Weekly XP: {xp}",
    growthMyRankNoRank: "Not on the weekly leaderboard yet.",
    growthMyRankWeekNotParticipated:
      "No weekly XP yet this week, so you’re not ranked. Play in Arena to appear.",
    growthMyRankAnonymous: "Sign in to see your rank.",
    growthMyRankSeeLeaderboard: "Full leaderboard",
    comebackTitle: "Welcome back",
    comebackBody:
      "It looks like you stepped away for a few days. Whenever you’re ready, you can continue your recovery path—no pressure.",
    comebackResumeJourneyCta: "Resume Training",
    comebackNotNowCta: "Not now",
    growthNavDojoTitle: "Dojo",
    growthNavDojoLine: "50-question snapshot of where you are",
    growthNavIntegrityTitle: "Integrity",
    growthNavIntegrityLine: "See the situation from the other side",
    growthNavGuidanceTitle: "Guidance",
    growthNavGuidanceLine: "Review your decision patterns",
    growthNavReflectionTitle: "Reflection",
    growthNavReflectionLine: "Stabilize your inner voice (Dear Me)",
    growthBackToGrowth: "Back to Growth",
    growthJourneyLandmarkAria: "28-day Journey board",
    growthRouteSuspenseMainRegionAria: "Growth — loading",
    arenaHubTitle: "Continue your Arena.",
    arenaHubSubtitle: "Decision, repetition, and pressure training live here.",
    arenaHubReadyTitle: "System ready",
    arenaHubReadyBody: "Resume your last simulation or begin a new scenario.",
    arenaHubCardTitleContinue: "Continue your Arena.",
    arenaHubCardBodyResume: "Resume your last simulation.",
    arenaHubCardBodyNew: "Start a new scenario.",
    arenaHubCtaWeeklyRank: "Weekly rank",
    arenaHubEntryLoading: "Loading…",
    arenaHubContinueCta: "Continue",
    arenaHubPlayCta: "Play Game",
    arenaHubWeeklyRankLabel: "Weekly rank",
    arenaHubSeasonEndsLabel: "Season ends in",
    arenaHubSummaryRegionAria: "Arena hub — weekly rank and season",
    arenaHubSummaryLoadError: "Could not load stats.",
    arenaHubMainRegionAria: "Arena hub — entry card, weekly rank, season",
    arenaMissionLobbyMainRegionAria: "Arena mission lobby — scenario entry and resume",
    arenaMissionLobbyLoadingMainRegionAria: "Arena mission lobby — loading",
    lobbyLandmarkAria: "Arena lobby — decision cycle and run entry",
    lobbyLoadingMainRegionAria: "Arena lobby — loading",
    lobbyCtaRegionAria: "Run entry — enter arena or resume scenario",
    lobbyTopBarAria: "Arena mission header — stage and progress",
    arenaBtyArenaRouteSegmentLoadingMainRegionAria: "BTY Arena — loading page",
    arenaMissionPlayLoadingMainRegionAria: "Mission play — loading",
    arenaMissionPlayMainRegionAria: "Mission play — run, choices, reflection, history",
    arenaMissionPlayTopBarAria: "Mission play header — stage, progress, codename",
    arenaMissionPlaySceneRegionAria: "Mission play — situation scene",
    arenaMissionPlayDecisionsRegionAria:
      "Mission play — scenario copy, primary choice, intent lock-in, resolve",
    arenaMissionResultLoadingMainRegionAria: "Mission result — loading",
    arenaMissionResultMainRegionAria: "Mission result — decision record, interpretation, next actions",
    delayedOutcomeBannerRegionAria: "7-day delayed outcome",
    delayedOutcomeIntro: "A choice from one week ago shaped this outcome.",
    delayedOutcomeDismiss: "Dismiss",
    delayedOutcomeFlagHeroTrap: "Hero Trap",
    delayedOutcomeFlagIntegritySlip: "Integrity Slip",
    delayedOutcomeFlagClean: "Clean",
    delayedOutcomeFlagRoleMirror: "Role mirror",
    arenaResolveSessionLoadingMainRegionAria: "Restore session — loading",
    arenaResolveSessionMainRegionAria: "Restore session — new scenario, choices, reflection",
    arenaLabMainRegionAria: "Leadership Lab — daily attempts and remaining uses",
    arenaLabUsageRegionAria: "Leadership Lab — today's attempts and remaining quota",
    arenaLabCtaRegionAria: "Leadership Lab — next actions — back to Arena run",
    arenaLabBackNavAria: "Leadership Lab — return to Arena run picker",
    arenaLabTitle: "Leadership Lab",
    arenaLabLead: "Complete up to 3 scenarios per day to earn Core XP.",
    arenaLabRemainingTodayLabel: "Remaining today",
    arenaLabRemainingSuffix: "remaining",
    arenaLabUsedPrefix: "Used: ",
    arenaLabBackToArena: "← Back to Arena",
    arenaLabUsageLoadError: "Could not load usage.",
    arenaLabScoreCardRegionAria: "Leadership Lab — score by difficulty and attempt",
    arenaLabScoreTitle: "Lab score",
    arenaLabScoreSlashMax: "{score} / {max} pts",
    arenaLabScoreDifficultyPrefix: "Difficulty",
    arenaLabScoreAttemptPrefix: "Attempt",
    arenaLabScoreDifficultyEasy: "Easy",
    arenaLabScoreDifficultyMid: "Mid",
    arenaLabScoreDifficultyHard: "Hard",
    arenaLabScoreDifficultyExtreme: "Extreme",
    arenaLabEmptyRegionAria: "Leadership Lab — no active scenario",
    arenaLabEmptyTitle: "No scenario to continue",
    arenaLabEmptyMessage: "Pick a scenario in Arena, then continue here in the Lab.",
    arenaLabEmptyHint: "Go back to the Arena run screen to start a scenario.",
    arenaBeginnerPathInitMainRegionAria: "Beginner path — preparing scenario",
    arenaBeginnerPathMainRegionAria: "Beginner path — situation, emotion, decision, reflection",
    arenaBeginnerPathCompleteMainRegionAria: "Beginner path — your result",
    arenaBeginnerPathTrackLabel: "Beginner",
    arenaBeginnerPathSubmittingAria: "Completing…",
    recordLandmarkAria: "Arena record screen — run summary, XP, and next actions",
    recordCtaRegionAria: "Next actions — continue play or return to hub",
    recordBottomNavAria: "Bottom app tabs — Arena, Growth, My Page",
    arenaResultEyebrow: "Arena Result",
    arenaResultRecordedTitle: "Decision recorded.",
    arenaResultRecordedSubtitle: "Outcome has been updated.",
    arenaResultSystemNoteTitle: "System note",
    arenaResultSystemNoteBody: "Improved decision consistency detected.",
    arenaResultContinuePlayCta: "Continue",
    arenaResultReturnHubCta: "Return to Arena",
    growthHubSectionLabel: "Growth",
    growthHubHeadline: "Rebuild your internal state.",
    growthHubLead: "Reflection, recovery, and alignment live here.",
    growthHubCardsNavAria: "Dojo, Integrity Mirror, Guidance, Journey, and Growth history",
    growthHubMainRegionAria: "Growth hub — inner alignment, Dojo, Integrity, Guidance, Journey",
    growthCardDojoTitle: "Dojo",
    growthCardDojoDesc: "Measure your current state",
    growthCardIntegrityTitle: "Integrity Mirror",
    growthCardIntegrityDesc: "See the situation from the other side",
    growthCardGuidanceTitle: "Guidance",
    growthCardGuidanceDesc: "Review your decision patterns",
    growthReflectionRegionAria: "Reflection entry from Arena",
    growthReflectionEyebrow: "Linked reflection",
    growthReflectionTitle: "A question from your last decision",
    growthReflectionSubtitle:
      "This is not generic journaling—it follows your sealed choice. Observational, not judgmental.",
    growthReflectionFocusLabel: "Focus",
    growthReflectionCueLabel: "Development cue",
    growthReflectionOpenCta: "Open in Journal",
    growthReflectionSuggestedTrack: "Suggested growth track",
    growthReflectionBackHub: "Back to Growth hub",
    growthReflectionNoSeed:
      "No Arena reflection seed yet. Complete an Arena cycle to populate this entry.",
    growthRecoveryCardTitle: "Recovery alignment signal",
    growthRecoveryCardBody:
      "Recent decisions show elevated pressure on emotional regulation. Consider pace, boundaries, and trust repair—not removal, re-entry.",
    growthReflectionAirlockEyebrow: "Growth Reflection",
    growthReflectionAirlockTitle: "Review the pattern before the next cycle.",
    growthReflectionAirlockLead:
      "Your last Arena decision has been translated into a guided reflection prompt. Use this to strengthen the next response—not to replay the past.",
    growthReflectionFocusChannel: "Focus channel",
    growthReflectionFocusClarity: "Clarity focus",
    growthReflectionFocusTrust: "Trust focus",
    growthReflectionFocusRegulation: "Regulation focus",
    growthReflectionFocusAlignment: "Alignment focus",
    growthReflectionFocusReady: "Reflection ready",
    growthReflectionSectionPrompt: "Reflection prompt",
    growthReflectionSectionCue: "Development cue",
    growthReflectionSectionRecovery: "Recovery prompt",
    growthReflectionOpenReflection: "Open reflection",
    growthReflectionOpenRecovery: "Open recovery prompt",
    growthReflectionReturnArena: "Return to Arena",
    growthReflectionCueEmpty: "No active cue detected yet.",
    growthRecoveryPlaceholderTitle: "Recovery space",
    growthRecoveryPlaceholderLead:
      "A path for post-pressure recovery and re-entry. Slow the pace before the next cycle.",
    growthWritingGuidedLabel: "Guided reflection",
    growthWritingHeadline: "Convert the last signal into your next direction.",
    growthWritingSubhead:
      "This is pattern clarification—not a mood essay. Keep it short, precise, and close with a commitment sentence.",
    growthWritingPromptSection: "Reflection prompt",
    growthWritingCueInline: "Development cue",
    growthWritingQ1Label: "Question 1",
    growthWritingQ1: "What reaction surfaced first under pressure?",
    growthWritingQ2Label: "Question 2",
    growthWritingQ2: "What did you try to protect, and what became vulnerable?",
    growthWritingQ3Label: "Question 3",
    growthWritingQ3: "What must become more precise in your next response?",
    growthWritingCommitmentLabel: "Next commitment",
    growthWritingCommitmentHelper: "End with one sentence you can carry into the next decision cycle.",
    growthWritingPlaceholder: "Write a precise response.",
    growthWritingCommitmentPlaceholder: "I will…",
    growthWritingSystemNote: "SYSTEM // Reflection converts signal into direction.",
    growthWritingSave: "Save reflection",
    growthWritingSaved: "Saved.",
    growthWritingReturnArena: "Return to Arena",
    growthWritingViewMyPage: "View leadership state",
    growthWritingNoSeed: "No reflection seed yet. Complete an Arena cycle first.",
    growthHistoryRegionAria: "Growth history — recent reflections",
    growthHistoryEyebrow: "Growth history",
    growthHistoryHeadline: "Track what your decisions are teaching you.",
    growthHistoryLead:
      "Reflection history is not a journal feed. It is a structured record of how Arena decisions are being translated into clearer future behavior.",
    growthHistoryEmpty: "No saved reflections yet. Complete a post-Arena reflection to populate this board.",
    growthHistoryRecoveryStrip:
      "Recovery support is available before the next escalation cycle—a quiet path for re-entry.",
    growthHistoryFocusLabel: "Focus",
    growthHistoryCommitmentLabel: "Commitment",
    growthHistoryRecoveryBadge: "Recovery-linked",
    growthHistoryBackGrowth: "Back to Growth hub",
    growthCardHistoryTitle: "Growth history",
    growthCardHistoryDesc: "Recent reflections and commitment lines at a glance",
    growthHistoryReflectionCountLabel: "Reflection count",
    growthHistoryCapsuleClarity: "Clarity",
    growthHistoryCapsuleTrust: "Trust",
    growthHistoryCapsuleRegulation: "Regulation",
    growthHistoryCapsuleAlignment: "Alignment",
    growthHistoryArenaReflectionBadge: "Arena reflection",
    growthHistoryRecoveryTitle: "Recovery signal",
    growthHistoryOpenRecoveryCta: "Open recovery",
    growthHistoryEmptyEyebrow: "No reflection history",
    growthHistoryEmptyTitle: "No structured reflection has been recorded yet.",
    growthHistoryEmptySubtext:
      "Complete one Arena decision cycle and save one reflection to begin building your growth record.",
    growthHistoryCtaOpenLatest: "Open latest reflection",
    growthHistoryCtaReturnArena: "Return to Arena",
    recoveryPromptLowRegulationTitle: "Pressure is accumulating faster than recovery.",
    recoveryPromptLowRegulationBody:
      "Recent signals suggest response pressure is rising before internal regulation fully resets.",
    recoveryPromptLowRegulationCue: "Rebuild regulation before the next escalation cycle.",
    recoveryPromptRepeatedFrictionTitle: "Regulation-focused threads are clustering.",
    recoveryPromptRepeatedFrictionBody:
      "Recent reflections emphasize emotional regulation—depth is building before your next response.",
    recoveryPromptRepeatedFrictionCue: "Name the pattern, then reset pace before the next escalation.",
    recoveryPromptPressureAccumulationTitle: "Escalation pressure is edging upward.",
    recoveryPromptPressureAccumulationBody:
      "Not an alarm state, but recovery margin is thinning. A structured pause protects the next move.",
    recoveryPromptPressureAccumulationCue: "Tighten clarity and pace before the next escalation cycle.",
    recoveryEntryEyebrow: "Recovery entry",
    recoveryEntryHeadline: "Reset structure before the next escalation cycle.",
    recoveryEntryLead:
      "Recovery is not retreat. It is a structured pause that protects the next decision from avoidable distortion.",
    recoveryEntryChannelLabel: "Recovery channel",
    recoveryChannelLowRegulation: "Regulation recovery",
    recoveryChannelRepeatedFriction: "Friction recovery",
    recoveryChannelPressureAccumulation: "Pressure recovery",
    recoveryChannelReady: "Recovery ready",
    recoveryEntrySignalTitle: "Recovery signal",
    recoveryEntryCueLabel: "Recovery cue",
    recoveryEntryNoPrompt:
      "No recovery prompt is active right now. You can return to Growth or Arena.",
    recoveryEntryQ1Label: "Recovery question 1",
    recoveryEntryQ1: "What pressure pattern is repeating before your response stabilizes?",
    recoveryEntryQ2Label: "Recovery question 2",
    recoveryEntryQ2: "What must be reset before the next escalation?",
    recoveryEntryQ3Label: "Re-entry commitment",
    recoveryEntryQ3: "Write one sentence that will guide your next response.",
    recoveryEntryPlaceholder: "Write a precise response.",
    recoveryEntrySystemStrip: "SYSTEM // Recovery protects the next decision cycle.",
    recoveryEntrySave: "Save recovery",
    recoveryEntryReturnGrowth: "Return to Growth",
    recoveryEntryReturnArena: "Return to Arena",
    recoveryEntryRegionAria: "Recovery entry — re-alignment",
  },
  journal: {
    title: "Journal",
    subtitle: "Let’s practice letting your mind rest today.",
    prompt: "What’s on your mind that you couldn’t tell anyone today?",
    placeholder: "Write here.",
    save: "Save",
    saveAria: "Save journal entry",
    footerDrChi: "Tap the Dr. Chi avatar button (bottom-right) to chat.",
    chatToggle: "Chat",
    chatToggleAria: "Open chat",
    chatTitle: "Coach chat",
    chatClose: "Close",
    chatCloseAria: "Close chat",
    chatStub: "(Journal chat UI — placeholder)",
    journalMainRegionAria: "Journal — daily reflection and coach chat",
    journalLoadingMainRegionAria: "Journal — loading",
    journalRedirectingMainRegionAria: "Journal — redirecting to sign-in",
    journalRedirectingMessage: "Redirecting to sign-in…",
  },
  myPageStub: {
    subMyPage: "My Page",
    progressTitle: "Progress",
    coreXp: "Core XP",
    weeklyXp: "Weekly XP",
    streak: "Streak",
    systemMsg: "System message",
    progressStage: "Stage 3 → Stage 4",
    progressRank: "Current rank: #128",
    progressStreakVal: "Consistency: 5 days",
    progressSystemLine: "Stable execution pattern detected.",
    progressFootnote: "Sample values · no deep analytics or raw metrics",
    teamTitle: "Team",
    teamTiiCard: "Team Integrity Score (TII)",
    teamTiiPlaceholder: "Awaiting team snapshot",
    teamStatusCard: "Team status",
    teamInnerStatus: "Status",
    teamStable: "Stable",
    teamInnerTrend: "Trend",
    teamTrendVal: "Improving",
    teamRankCard: "Team rank",
    teamRankCaption: "Rank",
    teamRankLine: "#8",
    teamFooter: "Team score reflects collective execution quality.",
    leaderTitle: "Leader Track",
    leaderCardStatus: "Status",
    leaderBuilding: "Building",
    leaderCardReadiness: "Leader readiness",
    leaderReadinessVal: "Near threshold",
    leaderCardCert: "Certification",
    leaderCertVal: "Not yet qualified",
    leaderSystem: "System message",
    leaderSystemLine: "Improving responsibility pattern detected.",
    leaderFootnote: "Tone-first · minimal numbers",
    overviewHealingCardTitle: "Healing",
    overviewHealingCardLead: "Recovery and Second Awakening paths.",
    overviewHealingNavAria: "Healing and Awakening shortcuts",
    recentRunsCardTitle: "Recent runs",
    recentRunsRegionAria: "Recent Arena runs",
    recentRunsLoading: "Loading runs…",
    recentRunsEmpty: "No recent runs.",
    recentRunsAnonymous: "Sign in to see your recent runs.",
    recentRunsFootnote: "Order and fields match the API. Next page uses cursor.",
    recentRunsLoadMore: "Load more runs",
    recentRunsLoadingMore: "Loading…",
    recentRunsScenarioPrefix: "Scenario",
    recentRunsStartedPrefix: "Started",
    recentRunsStatusPrefix: "Status",
    recentRunsOpenDetail: "Details",
    runDetailPageTitle: "Run details",
    runDetailPageSubtitle: "Arena · single run",
    runDetailRegionAria: "Arena run details",
    runDetailBackMyPage: "My page",
    runDetailLoading: "Loading run…",
    runDetailSkeletonLoading: "Loading run…",
    runDetailSkeletonEmpty: "Run not found or not allowed.",
    runDetailError: "Could not load this run.",
    runDetailNotFound: "Run not found or not allowed.",
    runDetailSignIn: "Sign in to view your runs.",
    runDetailScenarioId: "Scenario",
    runDetailStatus: "Status",
    runDetailStartedAt: "Started",
    runDetailCompletedAt: "Completed",
    runDetailLocaleLabel: "Locale",
    runDetailDifficulty: "Difficulty",
    runStatusLabelDone: "Completed",
    runStatusLabelInProgress: "In progress",
    runStatusLabelStarted: "Started",
    myPageShellOverviewTitle: "Your current status.",
    myPageShellOverviewSubtitle: "Accumulated Arena decisions shape your leadership identity.",
    myPageOverviewRegionAria: "My Page overview — identity, progress, team, account link",
    myPageShellProgressTitle: "Your current movement.",
    myPageShellProgressSubtitle: "Growth remains cumulative. Competition resets by window.",
    myPageShellTeamTitle: "Collective integrity status.",
    myPageShellTeamSubtitle: "Team score reflects collective execution quality.",
    myPageShellLeaderTitle: "Readiness and qualification.",
    myPageShellLeaderSubtitle: "Leadership status is shown as state, not raw analytics.",
    myPageCardIdentity: "Identity",
    myPageCardProgress: "Progress",
    myPageCardTeam: "Team",
    myPageLinkView: "View →",
    myPageLinkLeader: "Leader →",
    myPageLabelCodeName: "Code Name",
    myPageLabelStage: "Stage",
    myPageLabelCoreProgress: "Core Progress",
    myPageLabelWeeklyProgress: "Weekly Progress",
    myPageOverviewTeamStatus: "Status",
    myPageProgressMovement: "Stage movement",
    myPageTeamNoteHeading: "Note",
    myPageAccountLink: "Account",
    myPageTabsAria: "My Page sections",
    myPageTabOverview: "Overview",
    myPageTabProgress: "Progress",
    myPageTabTeam: "Team",
    myPageTabLeader: "Leader",
    myPageTabAccount: "Account",
    myPageRouteSuspenseMainRegionAria: "My Page — loading",
    leadershipRegionAria: "Leadership state console",
    leadershipCardInsight: "Insight pattern",
    leadershipCardInfluence: "Team influence",
    leadershipCardRhythm: "Rhythm",
    leadershipSectionSignalPattern: "Signal pattern",
    leadershipSectionInfluence: "Team & influence",
    leadershipSectionNext: "Recommended next focus",
    leadershipLabelRelationalBias: "Relational bias",
    leadershipLabelOperationalBias: "Operational bias",
    leadershipLabelEmotionalRegulation: "Emotional regulation",
    leadershipLabelCommunicationWeight: "Communication weight",
    leadershipLabelTeamSignal: "Team signal",
    leadershipLabelInfluencePattern: "Influence pattern",
    leadershipLabelAlignmentTrend: "Alignment trend",
    leadershipLinkArena: "Open Arena",
    leadershipLinkGrowth: "Growth reflection",
    leadershipCoreTrace: "Core trace",
    leadershipIdentityEyebrow: "Leadership Identity",
    leadershipSystemNoteTitle: "System Note",
    leadershipSystemNoteBodyDormant: "Arena signals will accumulate into a leadership pattern.",
    leadershipSystemNoteBodyActive: "Arena signals are accumulating into a stable leadership pattern.",
    leadershipAbbrevAir: "AIR (execution integrity)",
    leadershipAirExecutionFootnote:
      "This row summarizes Arena decision traces. Formal AIR uses Leadership Engine activation windows. Separate from Pattern Shift.",
    leadershipAbbrevTii: "TII",
    leadershipAbbrevRhythm: "Rhythm",
    leadershipNextFocusHeading: "Next focus",
    leadershipDevelopmentCueHeading: "Development cue",
    leadershipSuggestedModuleLine: "Suggested: Arena scenario · Growth reflection",
    leadershipReflectionLayerEyebrow: "Reflection accumulation",
    leadershipReflectionDepthColumn: "Reflection depth",
    leadershipReflectionIntegrationColumn: "Pattern integration",
    leadershipRecoveryAwarenessColumn: "Recovery awareness",
    leadershipReflectionDepthEarly: "Early",
    leadershipReflectionDepthActive: "Active",
    leadershipReflectionDepthDeepening: "Deepening",
    leadershipReflectionIntegrationForming: "Forming",
    leadershipReflectionIntegrationImproving: "Improving",
    leadershipRecoveryAwarenessStable: "Stable",
    leadershipRecoveryAwarenessDetected: "Detected",
    leadershipNextFocusWithReflectionRegulation:
      "Operational clarity remains the next refinement edge while relational signal stays strong.",
    leadershipFieldPatternTitle: "Decision pattern shape",
    leadershipPatternShiftSectionTitle: "Pattern Shift (behavior validation)",
    leadershipPatternShiftPlaceholder:
      "pattern_shift_results is not available yet. Do not read this as an AIR metric — it will fill in when behavior-validation data is wired.",
    leadershipFieldInfluenceTitle: "Influence",
    leadershipReflectionPanelTitle: "Reflection depth",
    leadershipRowRecentFocus: "Recent focus",
    leadershipRowIntegration: "Integration signal",
    leadershipReflectionTraceEmpty: "This layer opens as structured reflections accumulate.",
    leadershipRecoveryTraceEmpty: "Recovery awareness appears quietly when pressure patterns cluster.",
    leadershipIdentityCrestAria: "Leadership identity mark",
    leadershipRecoverySupportNote:
      "When patterns cluster, recovery-oriented framing can protect the next decision.",
    leadershipRecoveryStableNote: "No immediate recovery framing is required at this window.",
    leadershipSupportNoteLabel: "Support note",
    leadershipRecoveryStatusLabel: "Recovery status",
    leadershipSuggestedRouteHeading: "Suggested route",
    leadershipSuggestedRouteDefault: "Arena · Growth · Reflection",
    leadershipSuggestedRouteRecovery: "Recovery · Reflection",
  },
  actionContract: {
    pendingTitle: "Pending action",
    pendingBody: "Your next action is ready.",
    completedTitle: "Completed Promise",
    completedBody: "This promise has been confirmed.",
    missedTitle: "Action window closed.",
    missedBody: "You can continue to your next scenario.",
    btnQr: "Show QR for verification",
    expiresIn: "Expires in",
    expiredShort: "expired",
    qrWitnessNotice: "This QR is not for you to complete yourself.\nShow it to a colleague, manager, or assistant who can verify the action.\nOnce they scan the QR code, your action will be verified and completed.",
    dismiss: "Close",
    awaitingListTitle: "Actions awaiting QR verification",
    pendingListTitle: "My actions to complete",
    pendingEmptyMessage: "No pending actions right now.",
    pendingErrorMessage: "Couldn't load your actions.",
    badgeQrRequired: "QR needed",
    badgeAwaitingVerification: "Awaiting verification",
    badgeInProgress: "In progress",
    tierPersonal: "Personal",
    tierManager: "Manager",
    scanVerified: "Confirmed. This action is now part of today's growth.",
    scanAlready: "This action is already verified.",
    scanFailed: "Verification failed. Please try again.",
    scanValidationRequired: "This action is not ready for witness verification yet. Ask the actor to complete validation first.",
    qrValidationRequired: "Action validation is not complete yet. Submit validation first, then show the QR.",
    goToValidationCta: "Go to validation",
    witnessPromiseTitle: "Today's Promise",
    witnessConfirmQuestion: "Did this really happen today?",
    witnessConfirmCta: "Confirm",
    witnessConfirming: "Confirming…",
    witnessConfirmSuccess: "Confirmed. This action is now part of today's growth.",
    witnessLoadFailed: "Couldn't load this action. Please check the link.",
    witnessSelfBlocked:
      "This action needs someone else to confirm it. Please show this QR to a teammate, manager, or someone who witnessed the action.",
    actorCompletedTitle: "You completed one real action today.",
    actorReflectionPrompt: "How did it feel to actually do it?",
    actorContinueCta: "Continue Tomorrow",
  },
  legal: {
    accept: {
      title: "Terms of Service & Privacy Policy",
      placeholder_notice:
        "[PLACEHOLDER] The final legal text is being prepared by attorneys and will replace this notice before public launch. Internal cohort acceptance proceeds with this placeholder.",
      section_heading: "Pending Legal Review",
      checkbox_label:
        "I acknowledge that I am reviewing a placeholder consent form. I agree to provide consent in good faith for internal testing, understanding that the final Terms of Service and Privacy Policy will be presented before public launch.",
      submit_button: "Accept & Continue",
      submitting: "Saving...",
      error_required: "Please check the box to continue",
      error_network: "Connection error. Please try again.",
      error_server: "Something went wrong. Please try again.",
      error_rate_limit: "Too many attempts. Please wait a few minutes.",
      success_message: "Thank you. Redirecting...",
    },
  },
};

export function getMessages(locale: Locale): Messages {
  return locale === "en" ? en : ko;
}

/** 도메인 `weeklyTierDisplayLabelKey` / `arenaRunStateDisplayLabelKey` 반환 키 → 로케일 문구 */
export type ArenaStableDisplayKey =
  | "arena.leaderboard.weeklyTierBronze"
  | "arena.leaderboard.weeklyTierSilver"
  | "arena.leaderboard.weeklyTierGold"
  | "arena.leaderboard.weeklyTierPlatinum"
  | "arena.leaderboard.tieRankSuffix"
  | "arena.run.stateInProgress"
  | "arena.run.stateCompleted"
  | "arena.run.stateAborted";

export function arenaStableLabel(locale: Locale, key: ArenaStableDisplayKey): string {
  const a = getMessages(locale).arena;
  switch (key) {
    case "arena.leaderboard.weeklyTierBronze":
      return a.weeklyTierBronze;
    case "arena.leaderboard.weeklyTierSilver":
      return a.weeklyTierSilver;
    case "arena.leaderboard.weeklyTierGold":
      return a.weeklyTierGold;
    case "arena.leaderboard.weeklyTierPlatinum":
      return a.weeklyTierPlatinum;
    case "arena.leaderboard.tieRankSuffix":
      return a.tieRankSuffix;
    case "arena.run.stateInProgress":
      return a.runStateInProgress;
    case "arena.run.stateCompleted":
      return a.runStateCompleted;
    case "arena.run.stateAborted":
      return a.runStateAborted;
    default: {
      const _x: never = key;
      return _x;
    }
  }
}

/** `arenaRunDetailSkeletonDisplayKey` → 로케일 문구 (250). */
export function arenaRunDetailDisplayLabel(
  locale: Locale,
  key:
    | typeof ARENA_RUN_DETAIL_LOADING_DISPLAY_KEY
    | typeof ARENA_RUN_DETAIL_EMPTY_DISPLAY_KEY
): string {
  const m = getMessages(locale).myPageStub;
  return key === ARENA_RUN_DETAIL_LOADING_DISPLAY_KEY
    ? m.runDetailSkeletonLoading
    : m.runDetailSkeletonEmpty;
}

/** `DASHBOARD_RECOMMENDATION_EMPTY_PLACEHOLDER_KEY` → 로케일 문구. */
export function dashboardRecommendationEmptyCopy(locale: Locale): string {
  void DASHBOARD_RECOMMENDATION_EMPTY_PLACEHOLDER_KEY;
  return getMessages(locale).bty.recommendationEmptyPlaceholder;
}

/** `resilienceLevelDisplayLabelKey` → 로케일 문구. */
export function resilienceLevelDisplayCopy(
  locale: Locale,
  key: ResilienceLevelDisplayLabelKey
): string {
  const r = getMessages(locale).resilience;
  switch (key) {
    case "center.resilience.level_low":
      return r.levelLow;
    case "center.resilience.level_mid":
      return r.levelMid;
    case "center.resilience.level_high":
      return r.levelHigh;
    default: {
      const _e: never = key;
      return _e;
    }
  }
}

export type ReflectTextLengthHintKey =
  | "reflect_hint_empty"
  | "reflect_hint_short"
  | "reflect_hint_developing"
  | "reflect_hint_substantial"
  | "reflect_hint_near_limit"
  | "reflect_hint_at_limit";

export function reflectHintLabel(locale: Locale, key: ReflectTextLengthHintKey): string {
  const h = getMessages(locale).reflectHints;
  switch (key) {
    case "reflect_hint_empty":
      return h.reflect_hint_empty;
    case "reflect_hint_short":
      return h.reflect_hint_short;
    case "reflect_hint_developing":
      return h.reflect_hint_developing;
    case "reflect_hint_substantial":
      return h.reflect_hint_substantial;
    case "reflect_hint_near_limit":
      return h.reflect_hint_near_limit;
    case "reflect_hint_at_limit":
      return h.reflect_hint_at_limit;
    default: {
      const _e: never = key;
      return _e;
    }
  }
}

export function eliteMentorDomainCopy(
  locale: Locale,
  key:
    | "elite_mentor_terminal_approved"
    | "elite_mentor_terminal_rejected"
    | "elite_mentor_pending_stale"
    | "elite_mentor_status_pending"
    | "elite_mentor_status_approved"
    | "elite_mentor_status_rejected"
): string {
  const m = getMessages(locale).mentorRequest;
  switch (key) {
    case "elite_mentor_terminal_approved":
      return m.eliteDomainTerminalApproved;
    case "elite_mentor_terminal_rejected":
      return m.eliteDomainTerminalRejected;
    case "elite_mentor_pending_stale":
      return m.eliteDomainPendingStale;
    case "elite_mentor_status_pending":
      return m.eliteStatusBadgePending;
    case "elite_mentor_status_approved":
      return m.eliteStatusBadgeApproved;
    case "elite_mentor_status_rejected":
      return m.eliteStatusBadgeRejected;
    default: {
      const _e: never = key;
      return _e;
    }
  }
}

export function healingAwakeningBlockedCopy(
  locale: Locale,
  key: "healing_awakening_act_already_complete" | "healing_awakening_act_order_required"
): string {
  const h = getMessages(locale).healing;
  return key === "healing_awakening_act_already_complete"
    ? h.healingAwakeningBlockedAlreadyComplete
    : h.healingAwakeningBlockedOrderRequired;
}

export function healingAwakeningLockReasonCopy(
  locale: Locale,
  key: "healing_act_lock_prerequisite" | "healing_act_lock_already_complete"
): string {
  const h = getMessages(locale).healing;
  return key === "healing_act_lock_prerequisite"
    ? h.healingActLockPrerequisite
    : h.healingActLockAlreadyComplete;
}

/** `weeklyCompetitionStageTierBandDisplayLabelKey` → 로케일 문구 (251). */
export function weeklyCompetitionStageBandCopy(
  locale: Locale,
  key: WeeklyCompetitionStageTierBandDisplayLabelKey
): string {
  const b = getMessages(locale).bty;
  switch (key) {
    case "arena.weekly_competition.stage_band_bronze":
      return b.weeklyCompetitionStageBandBronze;
    case "arena.weekly_competition.stage_band_silver":
      return b.weeklyCompetitionStageBandSilver;
    case "arena.weekly_competition.stage_band_gold":
      return b.weeklyCompetitionStageBandGold;
    case "arena.weekly_competition.stage_band_platinum":
      return b.weeklyCompetitionStageBandPlatinum;
    default: {
      const _e: never = key;
      return _e;
    }
  }
}

/** `healingPathProgressBlockedUserDisplayKey` 결과·동일 상수 → 로케일 (251). */
export function healingPathProgressBlockedCopy(
  locale: Locale,
  key:
    | typeof HEALING_PROGRESS_BLOCKED_COOLDOWN_DISPLAY_KEY
    | typeof HEALING_PROGRESS_BLOCKED_PHASE_DISPLAY_KEY
): string {
  const h = getMessages(locale).healing;
  return key === HEALING_PROGRESS_BLOCKED_COOLDOWN_DISPLAY_KEY
    ? h.pathProgressBlockedCooldown
    : h.pathProgressBlockedPhase;
}
